import React, { useEffect, useLayoutEffect, useRef, useCallback, useState, useMemo } from 'react';
import { GridController, type CellFormat, type CellMetadataProjection, type FillOptionsContext, type PasteOptionsContext } from '../compositor/GridController';
import { SpreadsheetCanvas } from './SpreadsheetCanvas';
import { CellEditor } from './CellEditor';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';
// import { SheetBar } from './SheetBar';
import { FindDialog, type FindDialogMode, type FindDialogScope } from './FindDialog';
import { DataConnectionDialog } from './DataConnectionDialog';
import { colToLetter } from '../utils/columnUtils';
import { ExcelRibbon, type ExcelCommandHandler, type ExcelRibbonVariant, type ExcelVisualizeCommandHandlers } from './excel/ExcelRibbon';
import { ExcelFormulaBarRow } from './excel/ExcelFormulaBarRow';
import { ExcelStatusBar } from './excel/ExcelStatusBar';
import { ExcelFormatCellsDialog } from './excel/ExcelFormatCellsDialog';
import { ExcelConditionalFormattingDialog } from './excel/ExcelConditionalFormattingDialog';
import { ExcelSparklineDialog } from './excel/ExcelSparklineDialog';
import { ExcelChartInsertDialog } from './excel/ExcelChartInsertDialog';
import { ExcelDefinedNameManagerDialog } from './excel/ExcelDefinedNameManagerDialog';
import { ExcelInsertFunctionDialog } from './excel/ExcelInsertFunctionDialog';
import { ExcelCreateNamesFromSelectionDialog, type ExcelCreateNamesFromSelectionOptions } from './excel/ExcelCreateNamesFromSelectionDialog';
import { ExcelCellMetadataDialog, type ExcelCellMetadataMode, type ExcelValidationRuleDraft } from './excel/ExcelCellMetadataDialog';
import { ExcelSheetProtectionDialog } from './excel/ExcelSheetProtectionDialog';
import { ExcelValidationListPicker } from './excel/ExcelValidationListPicker';
import { perf } from '../perf/devPerf';
import {
    canCommitFormulaSessionOnSheet,
    buildFormulaFunctionCall,
    buildFormulaFunctionInsertionDraft,
    clearSharedFormulaEditSession,
    createFormulaEditSession,
    analyzeFormulaAuthoringText,
    findFormulaFunctionCallAtCaret,
    formatFormulaRangeRef,
    getFormulaReferenceColor,
    getSharedFormulaEditSession,
    insertOrReplaceFormulaRange,
    isActiveFormulaReferenceEditSession,
    isFormulaReferenceVisibleOnSheet,
    moveFormulaReferenceRangeById,
    publishSharedFormulaEditSession,
    qualifyFormulaRangeForActiveSheet,
    replaceFormulaReferenceRangeById,
    resumeFormulaEditSessionForSheet,
    updateFormulaDraft,
    type FormulaCommitNavigation,
    type FormulaEditSession,
    type FormulaFunctionEditTarget,
    type FormulaRangeCoord,
} from '../spreadsheet/formulaAuthoring';
import { ensureFormulaAuthoringRustParser } from '../spreadsheet/formulaAuthoringRustParser';
import { buildStructuredReferenceSuggestions } from '../spreadsheet/structuredReferenceUi';
import {
    buildApplyNamesResultNotice,
    buildCreateNamesFromSelectionDrafts,
    buildDefinedNameCommandPresentation,
    buildDefinedNamedRanges,
    buildDefinedNameSheetNameMaps,
    buildDefinedNamesPasteTsv,
    buildFormulaDefinedNameSuggestions,
    buildUseInFormulaMenuDescriptors,
    getVisibleDefinedNames,
} from '../spreadsheet/definedNamesPresentation';
import {
    buildSheetProtectionApplyPlan,
    buildSheetProtectionResizePermission,
    sheetProtectionIsEnabled,
} from '../spreadsheet/sheetProtectionPresentation';
import {
    buildConditionalFormattingMenuDescriptors,
    buildFormattingVisualCommandPresentation,
    derivedArtifactKindForCommand,
    sparklineTypeForCommand,
} from '../spreadsheet/formattingVisualPresentation';
import {
    buildSparklineDialogApplyPlan,
    defaultSparklineLocationForSelection,
    findSparklineGroupOverlappingRange,
    formatSparklineRangeFormula,
} from '../spreadsheet/sparklinePresentation';
import {
    buildDerivedArtifactContextMenuPresentation,
    buildActiveSheetDerivedArtifactMap,
    buildDerivedArtifactOverlayDragPreviewUpdate,
    buildDerivedArtifactOverlayProjections,
    buildDerivedArtifactPreviewFetchPlan,
    buildDerivedArtifactOverlayRenderPresentation,
    buildDerivedArtifactOverlaySelectionUpdate,
    applyDerivedArtifactPreviewFetchError,
    applyDerivedArtifactPreviewFetchSuccess,
    reconcileDerivedArtifactPreviewCacheStates,
    type DerivedArtifactChartQuickToolbarItemPresentation,
    type DerivedArtifactOverlayDragState,
    type DerivedArtifactOverlayProjection,
    type DerivedArtifactOverlayPreviewCacheState,
} from '../spreadsheet/derivedArtifactPreview';
import { normalizeSelectionRange } from '../spreadsheet/addressing';
import { getCommandState, getSpreadsheetCommand, type SpreadsheetCommandId, type SpreadsheetCommandState } from '../spreadsheet/commandRegistry';
import {
    buildPasteOptionsMenuEntries,
    PASTE_SPECIAL_COMMANDS,
    pasteSpecialCommandForAction,
} from '../spreadsheet/clipboardPresentation';
import {
    buildFillOptionsMenuEntries,
    fillOptionCommandForAction,
} from '../spreadsheet/fillOptionsPresentation';
import {
    buildInvalidEntryActionWorkflowPlan,
    buildInvalidEntryNoticePresentation,
    buildInvalidEntryNoticeFromMutationError,
    buildProtectedSheetActionNotice,
    buildValidationDropdownButtonPresentation,
    buildValidationDropdownFetchPlan,
    buildValidationDropdownProjection,
    buildValidationIssueMarkerProjections,
    buildValidationPromptProjection,
    buildValidationCommandPresentation,
    buildValidationScanResultNotice,
    buildValidationScanWorkflowPlan,
    buildValidationStatusNotice,
    getValidationInputPrompt,
    normalizeValidationAlertStyle,
    selectValidationRuleForMetadata,
    shouldClearValidationScanNotice,
    validationAlertTitle,
    type PositionedInvalidEntryNotice,
} from '../spreadsheet/validationPresentation';
import {
    buildCellMetadataActionPlan,
    buildCellMetadataCommandPresentation,
    buildCellMetadataOpenHyperlinkPlan,
    buildCommentsPanePresentation,
    buildSelectedCellMetadataCardPresentation,
    buildSelectedCommentEntry,
    buildSelectedMetadataCardOpenHyperlinkPlan,
    buildVisibleCommentEntries,
    describeEffectiveCellProtection,
    type CellMetadataActionId,
    type CellMetadataActionPlan,
    type CellMetadataProjectionLike,
    type VisibleCommentEntry,
} from '../spreadsheet/cellMetadataPresentation';
import {
    AUTO_FILTER_OPERATOR_OPTIONS,
    type AutoFilterMenuState,
    type AutoFilterMenuAction,
    type AutoFilterValueOption,
    applyAutoFilterEmptyValueLoad,
    applyAutoFilterValueLoadError,
    applyAutoFilterValueLoadSuccess,
    autoFilterRangeToUiRange,
    buildAutoFilterApplyDraftPlan,
    buildAutoFilterHeaderButtonPresentation,
    buildAutoFilterMenuActionPlan,
    buildAutoFilterMenuPresentation,
    buildAutoFilterMenuTogglePlan,
    buildAutoFilterValueReadPlan,
    buildAutoFilterHeaderButtons,
    buildTableContextMenuPresentation,
    buildTableFilterHeaderButtons,
    buildTableOverlayProjections,
    clearAutoFilterMenuConditions,
    isAutoFilterMenuTargetValid,
    markAutoFilterValueLoadStarted,
    resolveActiveAutoFilterForMenu,
    setAutoFilterMenuConditionJoin,
    setAutoFilterMenuConditionOperator,
    setAutoFilterMenuConditionValue,
    setAutoFilterMenuDraftAllOptions,
    toggleAutoFilterMenuDraftOption,
    updateAutoFilterMenuValueQuery,
} from '../spreadsheet/autoFilterUi';
import {
    buildSpreadsheetCellContextMenuDescriptors,
    buildSpreadsheetClipboardContextMenuDescriptors,
    buildSpreadsheetContextMenuActionPlan,
    buildSpreadsheetRowColumnContextMenuDescriptors,
    type SpreadsheetContextMenuDescriptor,
} from '../spreadsheet/contextMenuPresentation';
import type { SheetViewportStore } from './parity-dev/SheetViewportStore';
import type { SpreadsheetFindMatch, SpreadsheetMutationError, SpreadsheetWorkerSheetListEntry } from '../data/ViewportStreamer';
import {
    buildSpreadsheetEngineSheetId,
    type SpreadsheetDefinedNameEntry,
    type SpreadsheetDefinedNameScope,
    type SpreadsheetDefinedNamesResponsePayload,
    type SpreadsheetAutoFilter,
    type SpreadsheetAutoFilterConditionJoin,
    type SpreadsheetAutoFilterOperator,
    type SpreadsheetChartArtifactType,
    type SpreadsheetConditionalFormatRule,
    type SpreadsheetConditionalFormatRulesResponsePayload,
    type SpreadsheetDerivedArtifact,
    type SpreadsheetDerivedArtifactsResponsePayload,
    type SpreadsheetNamedRangeEntry,
    type SpreadsheetNamedRangesResponsePayload,
    type SpreadsheetRangeAddress,
    type SpreadsheetSheetListEntry,
    type SpreadsheetSheetProtectionOptions,
    type SpreadsheetSparklineGroup,
    type SpreadsheetSparklineGroupsResponsePayload,
    type SpreadsheetSparklineType,
    type SpreadsheetValidationIssue,
    type SpreadsheetValidationDropdownOptionsResponsePayload,
    type SpreadsheetValidationRule,
    type SpreadsheetValidationRulesResponsePayload,
} from '../protocol/spreadsheetEngineProtocol';
import '../styles/quadraticCreatorShell.css';

const chartTextOverflowStyle = (
    style?: { textHorizontalOverflow?: 'clip' | 'overflow' | null; textVerticalOverflow?: 'clip' | 'ellipsis' | 'overflow' | null } | null,
): Pick<React.CSSProperties, 'overflow' | 'textOverflow'> => ({
    overflow: style?.textHorizontalOverflow === 'overflow' || style?.textVerticalOverflow === 'overflow' ? 'visible' : 'hidden',
    textOverflow: style?.textVerticalOverflow === 'clip' ? 'clip' : 'ellipsis',
});

interface AppHTMLHeadersProps {
    controller: GridController;
    colCount: number;
    rowCount: number;
    onAddRows: (count: number) => void;
    onColumnAction?: (action: 'insert_left' | 'insert_right' | 'clear' | 'delete', selection: { startCol: number; endCol: number }) => void | Promise<void>;
    onRowAction?: (action: 'insert_above' | 'insert_below' | 'clear' | 'delete', selection: { startRow: number; endRow: number }) => void | Promise<void>;
    onToggleQuery?: () => void;
    onGeometryChange?: () => void;
    onAutoSizeColumn?: (index: number) => boolean;
    onAutoSizeRow?: (index: number) => boolean;
    onColumnResizeCommit?: (index: number, width: number) => void;
    onRowResizeCommit?: (index: number, height: number) => void;
    sheetName?: string;
    workbookName?: string;
    onWorkbookNameChange?: (name: string) => void | Promise<void>;
    visualizeActions?: ExcelVisualizeCommandHandlers;
    commandHandlers?: Partial<Record<SpreadsheetCommandId, ExcelCommandHandler>>;

    bareMode?: boolean; // When true, render minimal UI for perf isolation
    embedded?: boolean; // When true, size to parent container
    renderOverlay?: (ctx: { totalWidth: number; totalHeight: number }) => React.ReactNode;
    renderFixedOverlay?: React.ReactNode;
    externalScrollRef?: React.RefObject<HTMLDivElement | null>;
    viewportStore?: SheetViewportStore | null;
    showToolbar?: boolean;
    showFormulaBar?: boolean;
    formulaReferenceSuggestions?: Array<{
        name: string;
        scopeLabel?: string;
        refersTo?: string;
        hidden?: boolean;
    }>;
}

const HEADER_WIDTH = 50;
const HEADER_HEIGHT = 25;
const LIVE_EXCEL_RIBBON_VARIANT: ExcelRibbonVariant = 'compact';

const LIVE_COMPACT_RIBBON_HEIGHT = 108;
const LIVE_COMPACT_FORMULA_HEIGHT = 40;
const LIVE_EXPANDED_FORMULA_HEIGHT = 96;
type FormulaEditSurface = 'cell' | 'formula-bar'
type SpreadsheetContextMenuState =
    | { kind: 'cell', x: number, y: number, col: number, row: number }
    | { kind: 'column', x: number, y: number, startCol: number, endCol: number }
    | { kind: 'row', x: number, y: number, startRow: number, endRow: number }

type SpreadsheetPasteOptionsState = PasteOptionsContext & {
    x: number;
    y: number;
};

type SpreadsheetFillOptionsState = FillOptionsContext & {
    x: number;
    y: number;
};

type DerivedArtifactProjectionRefreshHandle = {
    kind: 'raf' | 'timeout';
    id: number;
};

function rangesEqual(left?: SpreadsheetRangeAddress | null, right?: SpreadsheetRangeAddress | null): boolean {
    if (!left || !right) return false;
    return String(left.sheet_id).toLowerCase() === String(right.sheet_id).toLowerCase()
        && left.start_col === right.start_col
        && left.start_row === right.start_row
        && left.end_col === right.end_col
        && left.end_row === right.end_row;
}

function formulaSpanRangeToSpreadsheetRange(
    spanRange: { sheetName?: string; start: { col: number; row: number }; end: { col: number; row: number } } | undefined,
    activeSheetId: string,
    sheetIdByName: Map<string, string>,
): SpreadsheetRangeAddress | null {
    if (!spanRange) return null;
    const sheetId = spanRange.sheetName
        ? sheetIdByName.get(spanRange.sheetName.toLowerCase())
        : activeSheetId;
    if (!sheetId) return null;
    return {
        sheet_id: sheetId,
        start_col: Math.min(spanRange.start.col, spanRange.end.col),
        start_row: Math.min(spanRange.start.row, spanRange.end.row),
        end_col: Math.max(spanRange.start.col, spanRange.end.col),
        end_row: Math.max(spanRange.start.row, spanRange.end.row),
    };
}

export const AppHTMLHeaders: React.FC<AppHTMLHeadersProps> = ({
    controller,
    colCount,
    rowCount,
    onAddRows,
    onColumnAction,
    onRowAction,
    onToggleQuery,
    onGeometryChange,
    onAutoSizeColumn,
    onAutoSizeRow,
    onColumnResizeCommit,
    onRowResizeCommit,
    sheetName,
    workbookName,
    onWorkbookNameChange,
    visualizeActions,
    commandHandlers,
    bareMode = false,
    embedded = false,
    renderOverlay,
    renderFixedOverlay,
    externalScrollRef,
    viewportStore = null,
    showToolbar = true,
    showFormulaBar = true,
    formulaReferenceSuggestions: externalFormulaReferenceSuggestions = [],
}) => {

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const colHeaderRef = useRef<HTMLDivElement>(null);
    const rowHeaderRef = useRef<HTMLDivElement>(null);
    const colHeaderContentRef = useRef<HTMLDivElement>(null);
    const rowHeaderContentRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const prevFullscreenRef = useRef(false);
    const [resizing, setResizing] = useState<{ type: 'col' | 'row', index: number, startPos: number, startSize: number } | null>(null);
    const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
    const scrollElRef = useRef<HTMLElement | null>(null);
    const [addRowCount, setAddRowCount] = useState(1000);
    const [editingCell, setEditingCell] = useState<{ col: number, row: number, rect: { x: number, y: number, w: number, h: number }, initialValue: string, format?: CellFormat } | null>(null);
    const [selectedCell, setSelectedCell] = useState<{ col: number, row: number } | null>(null);
    const [selectedRange, setSelectedRange] = useState<{ col: number, row: number, endCol: number, endRow: number } | null>(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [currEditValue, setCurrEditValue] = useState<string | null>(null);
    const [formulaEditSession, setFormulaEditSession] = useState<FormulaEditSession | null>(null);
    const [formulaEditSurface, setFormulaEditSurface] = useState<FormulaEditSurface | null>(null);
    const [formulaBarExpanded, setFormulaBarExpanded] = useState(false);
    const [showFormatCells, setShowFormatCells] = useState(false);
    const [showConditionalFormatting, setShowConditionalFormatting] = useState(false);
    const [sparklineDialogType, setSparklineDialogType] = useState<SpreadsheetSparklineType | null>(null);
    const [sparklineDialogAutoEdit, setSparklineDialogAutoEdit] = useState(false);
    const [chartDialogType, setChartDialogType] = useState<SpreadsheetChartArtifactType | null>(null);
    const [showNameManager, setShowNameManager] = useState(false);
    const [nameManagerInitialMode, setNameManagerInitialMode] = useState<'new' | null>(null);
    const [showInsertFunctionDialog, setShowInsertFunctionDialog] = useState(false);
    const [insertFunctionEditTarget, setInsertFunctionEditTarget] = useState<FormulaFunctionEditTarget | null>(null);
    const [showCreateNamesDialog, setShowCreateNamesDialog] = useState(false);
    const [showSheetProtectionDialog, setShowSheetProtectionDialog] = useState(false);
    const [cellMetadataMode, setCellMetadataMode] = useState<ExcelCellMetadataMode | null>(null);
    const [showCommentsPane, setShowCommentsPane] = useState(false);
    const [definedNames, setDefinedNames] = useState<SpreadsheetDefinedNameEntry[]>([]);
    const [definedNameSheets, setDefinedNameSheets] = useState<SpreadsheetSheetListEntry[]>([]);
    const [sheetMetadataSheets, setSheetMetadataSheets] = useState<SpreadsheetWorkerSheetListEntry[]>([]);
    const [conditionalFormatRules, setConditionalFormatRules] = useState<SpreadsheetConditionalFormatRule[]>([]);
    const [conditionalFormatRulesLoading, setConditionalFormatRulesLoading] = useState(false);
    const [conditionalFormatRulesError, setConditionalFormatRulesError] = useState<string | null>(null);
    const [derivedArtifacts, setDerivedArtifacts] = useState<SpreadsheetDerivedArtifact[]>([]);
    const [sparklineGroups, setSparklineGroups] = useState<SpreadsheetSparklineGroup[]>([]);
    const [derivedArtifactPreviews, setDerivedArtifactPreviews] = useState<Record<string, DerivedArtifactOverlayPreviewCacheState>>({});
    const [derivedArtifactProjectionVersion, setDerivedArtifactProjectionVersion] = useState(0);
    const derivedArtifactProjectionRefreshHandleRef = useRef<DerivedArtifactProjectionRefreshHandle | null>(null);
    const [validationRules, setValidationRules] = useState<SpreadsheetValidationRule[]>([]);
    const [validationIssues, setValidationIssues] = useState<SpreadsheetValidationIssue[]>([]);
    const [invalidEntryNotice, setInvalidEntryNotice] = useState<PositionedInvalidEntryNotice | null>(null);
    const [nameManagerLoading, setNameManagerLoading] = useState(false);
    const [nameManagerError, setNameManagerError] = useState<string | null>(null);
    const cancelScheduledDerivedArtifactProjectionRefresh = useCallback(() => {
        const handle = derivedArtifactProjectionRefreshHandleRef.current;
        if (!handle || typeof window === 'undefined') {
            derivedArtifactProjectionRefreshHandleRef.current = null;
            return;
        }
        if (handle.kind === 'raf') {
            window.cancelAnimationFrame(handle.id);
        } else {
            window.clearTimeout(handle.id);
        }
        derivedArtifactProjectionRefreshHandleRef.current = null;
    }, []);
    const scheduleDerivedArtifactProjectionRefresh = useCallback(() => {
        if (derivedArtifactProjectionRefreshHandleRef.current) return;
        if (typeof window === 'undefined') {
            setDerivedArtifactProjectionVersion((version) => version + 1);
            return;
        }
        const flush = () => {
            derivedArtifactProjectionRefreshHandleRef.current = null;
            setDerivedArtifactProjectionVersion((version) => version + 1);
        };
        if (typeof window.requestAnimationFrame === 'function') {
            derivedArtifactProjectionRefreshHandleRef.current = {
                kind: 'raf',
                id: window.requestAnimationFrame(flush),
            };
            return;
        }
        derivedArtifactProjectionRefreshHandleRef.current = {
            kind: 'timeout',
            id: window.setTimeout(flush, 16),
        };
    }, []);
    const formulaEditSessionRef = useRef<FormulaEditSession | null>(null);
    const formulaReferenceResizeRef = useRef<{
        referenceId: string;
        anchor: FormulaRangeCoord['start'];
        sheetName?: string;
    } | null>(null);
    const formulaReferenceMoveRef = useRef<{
        referenceId: string;
    } | null>(null);
    const [, setTick] = useState(0); // Force render
    const [viewportProjectionVersion, setViewportProjectionVersion] = useState(0);

    const [visibleBounds, setVisibleBounds] = useState<{ startCol: number, endCol: number, startRow: number, endRow: number }>({
        startCol: 0,
        endCol: 20,
        startRow: 0,
        endRow: 40
    });
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [canFullscreen, setCanFullscreen] = useState(false);
    const [showFullscreenButton, setShowFullscreenButton] = useState(false);
    // Browser scrollTop clamps at ~18m px; use a windowed scroll model above this.
    const SCROLL_WINDOW_PX = 10_000_000;
    const SCROLL_RECENTER_MARGIN_PX = 1_000_000;
    const scrollBaseYRef = useRef(0); // world-space y offset at dom scrollTop=0
    const isUserScrollingRef = useRef(false);
    const clearUserScrollTimeoutRef = useRef<number | null>(null);
    const pendingDomScrollRef = useRef<{ x: number; y: number } | null>(null);
    const applyDomScrollRafRef = useRef<number | null>(null);
    const lastHeaderLogRef = useRef(0);
    const [isImmersive, setIsImmersive] = useState(false);
    const [viewportHeight, setViewportHeight] = useState<number>(() =>
        Math.round(window.visualViewport?.height ?? window.innerHeight)
    );
    const rootRef = useRef<HTMLDivElement>(null);
    const [embeddedSize, setEmbeddedSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
    const activeColumnHeaderDragRef = useRef<null | { anchorCol: number }>(null);
    const activeRowHeaderDragRef = useRef<null | { anchorRow: number }>(null);
    const activeDerivedArtifactDragRef = useRef<DerivedArtifactOverlayDragState | null>(null);

    // Reset loading state when row count changes
    useEffect(() => {
        setIsLoadingMore(false);
    }, [rowCount, controller.getRowCount()]);

    // Prefer to show the fullscreen control on smaller/touch devices
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 1024px)');
        const updateVisibility = () => setShowFullscreenButton(mediaQuery.matches || 'ontouchstart' in window);
        updateVisibility();
        mediaQuery.addEventListener('change', updateVisibility);
        return () => mediaQuery.removeEventListener('change', updateVisibility);
    }, []);

    // Track fullscreen availability/state so we can enable/disable the button
    useEffect(() => {
        const checkSupport = () => {
            const docEl: any = document.documentElement;
            const request =
                docEl.requestFullscreen ||
                docEl.webkitRequestFullscreen ||
                docEl.webkitRequestFullScreen ||
                docEl.msRequestFullscreen ||
                docEl.mozRequestFullScreen ||
                docEl.mozRequestFullscreen;
            setCanFullscreen(Boolean(request));
        };

        const handleChange = () => {
            const fullscreenEl =
                document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement;
            setIsFullscreen(Boolean(fullscreenEl));
        };

        checkSupport();
        handleChange();

        document.addEventListener('fullscreenchange', handleChange);
        document.addEventListener('webkitfullscreenchange', handleChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleChange);
            document.removeEventListener('webkitfullscreenchange', handleChange);
        };
    }, []);

    // Keep our app height synced with the visible viewport (iOS address bar / keyboard).
    useEffect(() => {
        let raf = 0;
        const update = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const next = Math.round(window.visualViewport?.height ?? window.innerHeight);
                setViewportHeight(next);
            });
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);
        window.visualViewport?.addEventListener('resize', update);

        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
            window.visualViewport?.removeEventListener('resize', update);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    useEffect(() => {
        if (!embedded) return;
        const root = rootRef.current;
        if (!root) return;
        const target = root.parentElement || root;
        const update = () => {
            const nextWidth = Math.max(0, Math.round(target.clientWidth || 0));
            const nextHeight = Math.max(0, Math.round(target.clientHeight || 0));
            setEmbeddedSize((prev) => (
                prev.width === nextWidth && prev.height === nextHeight
                    ? prev
                    : { width: nextWidth, height: nextHeight }
            ));
        };
        update();
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                const nextWidth = Math.max(0, Math.round(width || target.clientWidth || 0));
                const nextHeight = Math.max(0, Math.round(height || target.clientHeight || 0));
                setEmbeddedSize((prev) => (
                    prev.width === nextWidth && prev.height === nextHeight
                        ? prev
                        : { width: nextWidth, height: nextHeight }
                ));
            }
        });
        observer.observe(target);
        return () => observer.disconnect();
    }, [embedded]);

    // If fullscreen exits via system UI, also exit immersive UI.
    useEffect(() => {
        if (prevFullscreenRef.current && !isFullscreen) {
            setIsImmersive(false);
        }
        prevFullscreenRef.current = isFullscreen;
    }, [isFullscreen]);

    useEffect(() => {
        if (bareMode) return;
        const handleFormulaBarToggle = (event: KeyboardEvent) => {
            const key = String(event.key || '').toLowerCase();
            if (!(event.ctrlKey || event.metaKey) || !event.shiftKey || key !== 'u') return;
            event.preventDefault();
            event.stopPropagation();
            setFormulaBarExpanded((expanded) => !expanded);
        };
        window.addEventListener('keydown', handleFormulaBarToggle, true);
        return () => window.removeEventListener('keydown', handleFormulaBarToggle, true);
    }, [bareMode]);

    useEffect(() => {
        if (bareMode || isImmersive) return;
        const handleFormatCellsShortcut = (event: KeyboardEvent) => {
            const key = String(event.key || '').toLowerCase();
            if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey || key !== '1') return;
            const target = event.target as HTMLElement | null;
            if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
            if (!controller.selection) return;
            event.preventDefault();
            event.stopPropagation();
            setShowFormatCells(true);
        };
        window.addEventListener('keydown', handleFormatCellsShortcut, true);
        return () => window.removeEventListener('keydown', handleFormatCellsShortcut, true);
    }, [bareMode, controller, isImmersive]);

    const rafRef = useRef<number | null>(null);

    const refreshViewportProjections = useCallback(() => {
        setViewportProjectionVersion((version) => version + 1);
    }, []);

    const updateVisibleBounds = useCallback(() => {
        if (rafRef.current) return;

        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            const viewport = controller.getViewport();
            // Skip until the canvas has reported a real viewport; prevents rendering the whole grid.
            if (viewport.width <= 0 || viewport.height <= 0) {
                return;
            }

            const bounds = controller.getVisibleBounds();

            // Add buffer
            const buffer = 8; // Small buffer to keep DOM light
            const currentColCount = controller.getColCount() || colCount;
            const currentRowCount = controller.getRowCount() || rowCount;

            const startCol = Math.max(0, bounds.startCol - buffer);
            const endCol = Math.min(currentColCount - 1, bounds.endCol + buffer);
            const startRow = Math.max(0, bounds.startRow - buffer);
            const endRow = Math.min(currentRowCount - 1, bounds.endRow + buffer);

            perf('headers.updateVisibleBounds', () => {
                setVisibleBounds(prev => {
                    if (prev.startCol !== startCol || prev.endCol !== endCol || prev.startRow !== startRow || prev.endRow !== endRow) {
                        return { startCol, endCol, startRow, endRow };
                    }
                    return prev;
                });
            });
        });
    }, [controller, colCount, rowCount]);

    const handleWorkbookChromeViewStateChange = useCallback(() => {
        controller.requestRender();
        setTick(t => t + 1);
        onGeometryChange?.();
        updateVisibleBounds();
    }, [controller, onGeometryChange, updateVisibleBounds]);

    const activeEngineSheetId = buildSpreadsheetEngineSheetId(controller.streamer?.getActiveSheetId?.() ?? 1);
    const workbookSheetEntries = sheetMetadataSheets.length > 0 ? sheetMetadataSheets : definedNameSheets;
    const definedNameSheetMaps = useMemo(() => (
        buildDefinedNameSheetNameMaps(workbookSheetEntries)
    ), [workbookSheetEntries]);
    const sheetNameByEngineId = definedNameSheetMaps.sheetNameByEngineId;
    const sheetIdByName = definedNameSheetMaps.sheetIdByName;
    const activeSheetEntry = useMemo(() => (
        workbookSheetEntries.find((sheet) => buildSpreadsheetEngineSheetId(sheet.id) === activeEngineSheetId) ?? null
    ), [activeEngineSheetId, workbookSheetEntries]);
    const activeSheetProtection = activeSheetEntry?.sheet_protection ?? null;
    const activeSheetAutoFilter: SpreadsheetAutoFilter | null = activeSheetEntry?.auto_filter ?? null;
    const activeSheetTables = useMemo(() => activeSheetEntry?.tables ?? {}, [activeSheetEntry?.tables]);
    const activeSheetTableNameEntries = useMemo(() => (
        Object.values(activeSheetTables)
            .map((table) => {
                const name = String(table?.name || '').trim();
                const range = table?.range ?? null;
                if (!name || !range) return null;
                return {
                    name,
                    range: {
                        col: Math.min(range.start_col, range.end_col),
                        row: Math.min(range.start_row, range.end_row),
                        endCol: Math.max(range.start_col, range.end_col),
                        endRow: Math.max(range.start_row, range.end_row),
                    },
                };
            })
            .filter((entry): entry is { name: string; range: { col: number; row: number; endCol: number; endRow: number } } => Boolean(entry))
            .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))
    ), [activeSheetTables]);
    const activeSheetDerivedArtifacts = useMemo(() => (
        buildActiveSheetDerivedArtifactMap(derivedArtifacts, activeEngineSheetId)
    ), [activeEngineSheetId, derivedArtifacts]);
    const activeSheetProtected = sheetProtectionIsEnabled(activeSheetProtection);
    const columnResizeProtection = buildSheetProtectionResizePermission({
        axis: 'column',
        protection: activeSheetProtection,
    });
    const rowResizeProtection = buildSheetProtectionResizePermission({
        axis: 'row',
        protection: activeSheetProtection,
    });
    const columnResizeBlockedByProtection = columnResizeProtection.blocked;
    const rowResizeBlockedByProtection = rowResizeProtection.blocked;
    const activeDefinedNameSheetName = sheetNameByEngineId.get(activeEngineSheetId) ?? sheetName ?? 'Sheet';
    const selectedEngineRange = useMemo<SpreadsheetRangeAddress>(() => {
        const selection = selectedRange ?? { col: 0, row: 0, endCol: 0, endRow: 0 };
        return {
            sheet_id: activeEngineSheetId,
            start_row: Math.min(selection.row, selection.endRow),
            start_col: Math.min(selection.col, selection.endCol),
            end_row: Math.max(selection.row, selection.endRow),
            end_col: Math.max(selection.col, selection.endCol),
        };
    }, [activeEngineSheetId, selectedRange]);
    const selectedRangeLabel = useMemo(() => {
        const start = `${colToLetter(selectedEngineRange.start_col)}${selectedEngineRange.start_row + 1}`;
        const end = `${colToLetter(selectedEngineRange.end_col)}${selectedEngineRange.end_row + 1}`;
        return start === end ? start : `${start}:${end}`;
    }, [selectedEngineRange]);
    const getSelectedCellMetadata = useCallback((): CellMetadataProjection | undefined => {
        const selection = selectedRange ?? controller.selection;
        if (!selection) return undefined;
        const startCol = Math.min(selection.col, selection.endCol);
        const startRow = Math.min(selection.row, selection.endRow);
        return controller.getCellMetadata(startCol, startRow);
    }, [controller, selectedRange]);
    const selectedCellMetadata = getSelectedCellMetadata();
    const applyDerivedArtifactSelectionUpdate = useCallback((
        update: ReturnType<typeof buildDerivedArtifactOverlaySelectionUpdate>,
    ) => {
        controller.setSelection(update.selection, { force: true });
        setSelectedRange(update.selection);
        setSelectedCell(update.selectedCell);
    }, [controller]);
    const selectedCellAuthoringFormat = useMemo(() => {
        if (!selectedCell) return undefined;
        const format = controller.getAuthoringCellFormat(selectedCell.col, selectedCell.row) ?? {};
        return selectedCellMetadata?.protection
            ? { ...format, protection: selectedCellMetadata.protection }
            : format;
    }, [controller, selectedCell, selectedCellMetadata?.protection]);
    const selectedCellProtectionStatus = useMemo(() => (
        describeEffectiveCellProtection(
            selectedCellMetadata?.protection ?? null,
            activeSheetProtection,
            activeDefinedNameSheetName,
        )
    ), [activeDefinedNameSheetName, activeSheetProtection, selectedCellMetadata?.protection]);

    useEffect(() => {
        controller.setSheetProtectionProjection(activeSheetProtected ? activeSheetProtection : null);
    }, [activeSheetProtected, activeSheetProtection, controller]);
    useEffect(() => {
        controller.setAutoFilterProjection(activeSheetAutoFilter);
    }, [activeSheetAutoFilter, controller]);
    useEffect(() => {
        controller.setTableProjection(activeSheetTables);
    }, [activeSheetTables, controller]);
    useEffect(() => {
        controller.setDerivedArtifactProjection(activeSheetDerivedArtifacts);
    }, [activeSheetDerivedArtifacts, controller]);
    const selectedValidationRule = useMemo(() => (
        selectValidationRuleForMetadata({
            metadata: selectedCellMetadata,
            validationRules,
        })
    ), [selectedCellMetadata, validationRules]);
    const selectedValidationInputPrompt = useMemo(
        () => getValidationInputPrompt(selectedValidationRule),
        [selectedValidationRule],
    );

    const definedNamedRanges = useMemo(() => buildDefinedNamedRanges(definedNames), [definedNames]);
    const formulaDefinedNameSuggestions = useMemo(() => (
        buildFormulaDefinedNameSuggestions(definedNames, sheetNameByEngineId)
    ), [definedNames, sheetNameByEngineId]);
    const tableStructuredReferenceSuggestions = useMemo(() => (
        buildStructuredReferenceSuggestions(activeSheetTables)
    ), [activeSheetTables]);
    const formulaReferenceSuggestions = useMemo(() => (
        [
            ...formulaDefinedNameSuggestions,
            ...tableStructuredReferenceSuggestions,
            ...externalFormulaReferenceSuggestions,
        ]
    ), [externalFormulaReferenceSuggestions, formulaDefinedNameSuggestions, tableStructuredReferenceSuggestions]);

    const applyDefinedNamesPayload = useCallback((payload: SpreadsheetDefinedNamesResponsePayload) => {
        setDefinedNames(payload.defined_names);
        setDefinedNameSheets(payload.sheets);
        setNameManagerError(null);
    }, []);

    const applyNamedRangesPayload = useCallback((payload: SpreadsheetNamedRangesResponsePayload) => {
        const nextDefinedNames: SpreadsheetDefinedNameEntry[] = payload.named_ranges.map((entry): SpreadsheetDefinedNameEntry => ({
            name: entry.name,
            scope: { kind: 'workbook' },
            refers_to: '',
            range: entry.range,
        }));
        setDefinedNames(nextDefinedNames);
        setDefinedNameSheets(payload.sheets);
        setNameManagerError(null);
    }, []);

    const applyValidationRulesPayload = useCallback((payload: SpreadsheetValidationRulesResponsePayload) => {
        setValidationRules(payload.validation_rules);
    }, []);

    const applyConditionalFormatRulesPayload = useCallback((payload: SpreadsheetConditionalFormatRulesResponsePayload) => {
        setConditionalFormatRules(payload.conditional_format_rules);
        setConditionalFormatRulesError(null);
    }, []);

    const applySparklineGroupsPayload = useCallback((payload: SpreadsheetSparklineGroupsResponsePayload) => {
        setSparklineGroups(Array.isArray(payload.sparkline_groups) ? payload.sparkline_groups : []);
    }, []);

    const applyDerivedArtifactsPayload = useCallback((payload: SpreadsheetDerivedArtifactsResponsePayload) => {
        setDerivedArtifacts(payload.derived_artifacts);
    }, []);

    const refreshDefinedNames = useCallback(async () => {
        const streamer = controller.streamer;
        if (!streamer) {
            setDefinedNames([]);
            setDefinedNameSheets([]);
            return;
        }
        setNameManagerLoading(true);
        try {
            if (typeof streamer.getDefinedNames === 'function') {
                const payload = await streamer.getDefinedNames();
                applyDefinedNamesPayload(payload);
            } else {
                const payload = await streamer.getNamedRanges();
                applyNamedRangesPayload(payload);
            }
        } catch (error) {
            setNameManagerError(error instanceof Error ? error.message : String(error));
        } finally {
            setNameManagerLoading(false);
        }
    }, [applyDefinedNamesPayload, applyNamedRangesPayload, controller.streamer]);

    const refreshValidationRules = useCallback(async () => {
        const streamer = controller.streamer;
        if (!streamer) {
            setValidationRules([]);
            return;
        }
        try {
            const payload = await streamer.getValidationRules();
            applyValidationRulesPayload(payload);
        } catch {
            // Validation rules are editor assistance; the engine still validates on apply.
        }
    }, [applyValidationRulesPayload, controller.streamer]);

    const refreshConditionalFormatRules = useCallback(async () => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.getConditionalFormatRules !== 'function') {
            setConditionalFormatRules([]);
            return;
        }
        setConditionalFormatRulesLoading(true);
        try {
            const payload = await streamer.getConditionalFormatRules();
            applyConditionalFormatRulesPayload(payload);
        } catch (error) {
            setConditionalFormatRulesError(error instanceof Error ? error.message : String(error));
        } finally {
            setConditionalFormatRulesLoading(false);
        }
    }, [applyConditionalFormatRulesPayload, controller.streamer]);

    const refreshSparklineGroups = useCallback(async () => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.getSparklineGroups !== 'function') {
            setSparklineGroups([]);
            return;
        }
        try {
            const payload = await streamer.getSparklineGroups();
            applySparklineGroupsPayload(payload);
        } catch {
            // Sparkline groups remain engine metadata; the edit menu can wait for the next projection update.
        }
    }, [applySparklineGroupsPayload, controller.streamer]);

    const refreshDerivedArtifacts = useCallback(async () => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.getDerivedArtifacts !== 'function') {
            setDerivedArtifacts([]);
            return;
        }
        try {
            const payload = await streamer.getDerivedArtifacts();
            applyDerivedArtifactsPayload(payload);
        } catch {
            // Derived artifacts remain engine metadata; projection fetch failures do not create UI-local truth.
        }
    }, [applyDerivedArtifactsPayload, controller.streamer]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer) {
            setDefinedNames([]);
            setDefinedNameSheets([]);
            return undefined;
        }
        if (typeof streamer.setDefinedNamesCallback === 'function') {
            streamer.setDefinedNamesCallback(applyDefinedNamesPayload);
        }
        streamer.setNamedRangesCallback(applyNamedRangesPayload);
        void refreshDefinedNames();
        return () => {
            if (controller.streamer === streamer && typeof streamer.setDefinedNamesCallback === 'function') streamer.setDefinedNamesCallback(null);
            if (controller.streamer === streamer) streamer.setNamedRangesCallback(null);
        };
    }, [applyDefinedNamesPayload, applyNamedRangesPayload, controller.streamer, refreshDefinedNames]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.setSheetsCallback !== 'function') {
            setSheetMetadataSheets([]);
            return undefined;
        }
        streamer.setSheetsCallback((list) => {
            setSheetMetadataSheets(list);
        });
        streamer.getSheets();
        return () => {
            if (controller.streamer === streamer) streamer.setSheetsCallback(() => {});
        };
    }, [controller.streamer]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer) {
            setValidationRules([]);
            return undefined;
        }
        streamer.setValidationRulesCallback(applyValidationRulesPayload);
        void refreshValidationRules();
        return () => {
            if (controller.streamer === streamer) streamer.setValidationRulesCallback(null);
        };
    }, [applyValidationRulesPayload, controller.streamer, refreshValidationRules]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.setConditionalFormatRulesCallback !== 'function') {
            setConditionalFormatRules([]);
            return undefined;
        }
        streamer.setConditionalFormatRulesCallback(applyConditionalFormatRulesPayload);
        void refreshConditionalFormatRules();
        return () => {
            if (controller.streamer === streamer) streamer.setConditionalFormatRulesCallback(null);
        };
    }, [applyConditionalFormatRulesPayload, controller.streamer, refreshConditionalFormatRules]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.setSparklineGroupsCallback !== 'function') {
            setSparklineGroups([]);
            return undefined;
        }
        streamer.setSparklineGroupsCallback(applySparklineGroupsPayload);
        void refreshSparklineGroups();
        return () => {
            if (controller.streamer === streamer) streamer.setSparklineGroupsCallback(null);
        };
    }, [applySparklineGroupsPayload, controller.streamer, refreshSparklineGroups]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.setDerivedArtifactsCallback !== 'function') {
            setDerivedArtifacts([]);
            setDerivedArtifactPreviews({});
            return undefined;
        }
        streamer.setDerivedArtifactsCallback(applyDerivedArtifactsPayload);
        void refreshDerivedArtifacts();
        return () => {
            if (controller.streamer === streamer) streamer.setDerivedArtifactsCallback(null);
        };
    }, [applyDerivedArtifactsPayload, controller.streamer, refreshDerivedArtifacts]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.setProjectionRefreshCallback !== 'function') return undefined;
        streamer.setProjectionRefreshCallback(scheduleDerivedArtifactProjectionRefresh);
        return () => {
            if (controller.streamer === streamer) streamer.setProjectionRefreshCallback(null);
            cancelScheduledDerivedArtifactProjectionRefresh();
        };
    }, [cancelScheduledDerivedArtifactProjectionRefresh, controller.streamer, scheduleDerivedArtifactProjectionRefresh]);

    useEffect(() => {
        const streamer = controller.streamer;
        const fetchPlan = buildDerivedArtifactPreviewFetchPlan(Object.values(activeSheetDerivedArtifacts));
        if (!streamer || fetchPlan.shouldClearCache) {
            setDerivedArtifactPreviews({});
            return undefined;
        }

        setDerivedArtifactPreviews((current) => reconcileDerivedArtifactPreviewCacheStates(current, fetchPlan.requests));

        let cancelled = false;
        for (const rangeRead of fetchPlan.rangeReads) {
            const { artifactId, sourceKey } = rangeRead;
            void streamer.getRangeCells(
                rangeRead.startCol,
                rangeRead.startRow,
                rangeRead.endCol,
                rangeRead.endRow,
                rangeRead.mode,
                { sheetId: rangeRead.sheetId },
            ).then((cells) => {
                if (cancelled) return;
                setDerivedArtifactPreviews((current) => applyDerivedArtifactPreviewFetchSuccess(current, artifactId, sourceKey, cells));
            }).catch((error) => {
                if (cancelled) return;
                setDerivedArtifactPreviews((current) => applyDerivedArtifactPreviewFetchError(current, artifactId, sourceKey, error));
            });
        }

        return () => {
            cancelled = true;
        };
    }, [activeEngineSheetId, activeSheetDerivedArtifacts, controller.streamer, derivedArtifactProjectionVersion]);

    useEffect(() => {
        const streamer = controller.streamer;
        const selection = selectedRange ?? controller.selection;
        const fetchPlan = buildValidationDropdownFetchPlan({
            disabled: bareMode,
            hasStreamer: Boolean(streamer),
            rule: selectedValidationRule,
            activeSheetId: activeEngineSheetId,
            selection,
        });
        setValidationDropdownOptions(null);
        if (!streamer || !fetchPlan.request) return undefined;
        let cancelled = false;
        void streamer.getValidationDropdownOptions(fetchPlan.request).then((payload) => {
            if (cancelled) return;
            setValidationDropdownOptions(payload);
        }).catch(() => {
            if (!cancelled) setValidationDropdownOptions(null);
        });
        return () => {
            cancelled = true;
        };
    }, [
        activeEngineSheetId,
        bareMode,
        controller,
        controller.streamer,
        selectedRange,
        selectedRange?.col,
        selectedRange?.row,
        selectedRange?.endCol,
        selectedRange?.endRow,
        selectedValidationRule?.id,
        selectedValidationRule?.kind,
    ]);

    const openNameManager = useCallback((mode?: 'new') => {
        setNameManagerInitialMode(mode === 'new' ? 'new' : null);
        setShowNameManager(true);
        void refreshDefinedNames();
    }, [refreshDefinedNames]);

    const openInsertFunctionDialog = useCallback(() => {
        const session = formulaEditSessionRef.current;
        setInsertFunctionEditTarget(
            session && session.status === 'active'
                ? findFormulaFunctionCallAtCaret(session.draft, session.caretEnd)
                : null,
        );
        setShowInsertFunctionDialog(true);
    }, []);

    useEffect(() => {
        if (bareMode || isImmersive) return;
        const handleInsertFunctionShortcut = (event: KeyboardEvent) => {
            if (!event.shiftKey || event.ctrlKey || event.metaKey || event.altKey || event.key !== 'F3') return;
            event.preventDefault();
            event.stopPropagation();
            openInsertFunctionDialog();
        };
        window.addEventListener('keydown', handleInsertFunctionShortcut, true);
        return () => window.removeEventListener('keydown', handleInsertFunctionShortcut, true);
    }, [bareMode, isImmersive, openInsertFunctionDialog]);

    const handleUpsertDefinedName = useCallback((draft: {
        name: string;
        scope: SpreadsheetDefinedNameScope;
        refersTo: string;
        range?: SpreadsheetRangeAddress | null;
        comment?: string | null;
        hidden?: boolean;
    }) => {
        const streamer = controller.streamer;
        if (!streamer) {
            const error = new Error('Spreadsheet engine is not ready.');
            setNameManagerError(error.message);
            throw error;
        }
        try {
            if (typeof streamer.upsertDefinedName === 'function') {
                streamer.upsertDefinedName(draft);
            } else if (draft.range) {
                streamer.defineName(draft.name, draft.range);
            } else {
                throw new Error('The spreadsheet engine only supports range names in this build.');
            }
            setNameManagerError(null);
        } catch (error) {
            setNameManagerError(error instanceof Error ? error.message : String(error));
            throw error;
        }
    }, [controller.streamer]);

    const handleDeleteName = useCallback((name: string, scope?: SpreadsheetDefinedNameScope) => {
        const streamer = controller.streamer;
        if (!streamer) {
            setNameManagerError('Spreadsheet engine is not ready.');
            return;
        }
        try {
            streamer.deleteName(name, { scope });
            setNameManagerError(null);
        } catch (error) {
            setNameManagerError(error instanceof Error ? error.message : String(error));
        }
    }, [controller.streamer]);

    const navigateToNamedRange = useCallback((entry: SpreadsheetNamedRangeEntry | SpreadsheetDefinedNameEntry) => {
        if (!entry.range) return;
        const range = entry.range;
        const match = /^sheet_([1-9]\d*)$/.exec(String(range.sheet_id || '').trim());
        const uiSheetId = match ? Number(match[1]) : null;
        if (uiSheetId && controller.streamer?.getWorkerActiveSheetId?.() !== uiSheetId) {
            controller.streamer?.switchSheet(uiSheetId);
        }
        controller.setSelection({
            col: range.start_col,
            row: range.start_row,
            endCol: range.end_col,
            endRow: range.end_row,
        });
        controller.scrollToCell(range.start_col, range.start_row);
        controller.requestRender();
        setTick(t => t + 1);
        updateVisibleBounds();
    }, [controller, updateVisibleBounds]);

    const openCellMetadataDialog = useCallback((mode: ExcelCellMetadataMode) => {
        if (!controller.selection || !controller.streamer) return;
        if (mode === 'validation') void refreshValidationRules();
        setCellMetadataMode(mode);
    }, [controller, refreshValidationRules]);

    const openCommentsPane = useCallback(() => {
        if (!controller.selection || !controller.streamer) return;
        setShowCommentsPane(true);
    }, [controller]);

    const handleApplyCellComment = useCallback((comment: string | null) => {
        controller.setCommentOnSelection(comment);
        handleWorkbookChromeViewStateChange();
    }, [controller, handleWorkbookChromeViewStateChange]);

    const handleApplyCellHyperlink = useCallback((hyperlink: string | null) => {
        controller.setHyperlinkOnSelection(hyperlink);
        handleWorkbookChromeViewStateChange();
    }, [controller, handleWorkbookChromeViewStateChange]);

    const handleApplyDataValidation = useCallback((validationId: string | null) => {
        controller.setValidationOnSelection(validationId);
        setValidationIssues([]);
        handleWorkbookChromeViewStateChange();
    }, [controller, handleWorkbookChromeViewStateChange]);

    const handleApplyDataValidationRule = useCallback((rule: ExcelValidationRuleDraft) => {
        controller.setValidationRuleOnSelection(rule);
        setValidationIssues([]);
        handleWorkbookChromeViewStateChange();
    }, [controller, handleWorkbookChromeViewStateChange]);

    const handleApplyCellProtection = useCallback((protection: Record<string, unknown> | null) => {
        controller.setProtectionOnSelection(protection);
        handleWorkbookChromeViewStateChange();
    }, [controller, handleWorkbookChromeViewStateChange]);

    const executeCellMetadataActionPlan = useCallback((
        plan: CellMetadataActionPlan,
        metadata?: CellMetadataProjectionLike | null,
    ) => {
        switch (plan.action) {
            case 'open-dialog':
                openCellMetadataDialog(plan.mode);
                break;
            case 'clear-comment':
                handleApplyCellComment(null);
                break;
            case 'clear-hyperlink':
                handleApplyCellHyperlink(null);
                break;
            case 'clear-protection':
                handleApplyCellProtection(null);
                break;
            case 'open-comments-pane':
                openCommentsPane();
                break;
            case 'open-hyperlink': {
                const url = plan.url ?? buildCellMetadataOpenHyperlinkPlan(metadata?.hyperlink)?.url;
                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
                break;
            }
        }
    }, [
        handleApplyCellComment,
        handleApplyCellHyperlink,
        handleApplyCellProtection,
        openCellMetadataDialog,
        openCommentsPane,
    ]);

    useEffect(() => {
        controller.setOpenHyperlinkCallback(({ hyperlink }) => {
            const plan = buildCellMetadataOpenHyperlinkPlan(hyperlink);
            if (plan) executeCellMetadataActionPlan(plan);
        });
        return () => {
            controller.setOpenHyperlinkCallback(null);
        };
    }, [controller, executeCellMetadataActionPlan]);

    const handleOpenSheetProtectionDialog = useCallback(() => {
        if (!controller.streamer) return;
        setShowSheetProtectionDialog(true);
    }, [controller.streamer]);

    const handleApplySheetProtection = useCallback((protection: SpreadsheetSheetProtectionOptions | null) => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.setSheetProtection !== 'function') return;
        const plan = buildSheetProtectionApplyPlan({
            protection,
            activeSheetId: streamer.getActiveSheetId?.() ?? null,
        });
        streamer.setSheetProtection(plan.protection, plan.options);
        void refreshDefinedNames();
        handleWorkbookChromeViewStateChange();
    }, [controller.streamer, handleWorkbookChromeViewStateChange, refreshDefinedNames]);

    const handleCircleInvalidData = useCallback(async () => {
        const streamer = controller.streamer;
        if (!streamer) {
            setInvalidEntryNotice(buildValidationStatusNotice({
                source: 'validation-scan',
                message: 'Spreadsheet engine is not ready to scan validation rules.',
            }));
            return;
        }
        try {
            const payload = await streamer.getValidationIssues();
            const scanPlan = buildValidationScanWorkflowPlan({
                issues: payload.issues,
                activeSheetId: activeEngineSheetId,
            });
            setValidationIssues(scanPlan.issues);
            if (scanPlan.action === 'show-notice') {
                setInvalidEntryNotice(scanPlan.notice);
                return;
            }
            const { selection, selectedIssue } = scanPlan;
            controller.setSelection(selection);
            controller.notifySelectionChange();
            controller.scrollToCell(selection.col, selection.row);
            controller.requestRender();
            window.setTimeout(() => {
                const col = selection.col;
                const row = selection.row;
                const rect = controller.getRangeScreenRect({
                    start: { col, row },
                    end: { col, row },
                });
                setInvalidEntryNotice(buildValidationScanResultNotice({
                    issues: scanPlan.issues,
                    activeSheetId: activeEngineSheetId,
                    selectedIssue,
                    cellRect: rect,
                    viewport: controller.getViewport(),
                }));
            }, 0);
        } catch (error) {
            setValidationIssues([]);
            setInvalidEntryNotice(buildValidationStatusNotice({
                source: 'validation-scan',
                message: error instanceof Error ? error.message : String(error),
            }));
        }
    }, [activeEngineSheetId, controller]);

    const handleClearValidationCircles = useCallback(() => {
        setValidationIssues([]);
        setInvalidEntryNotice((current) => shouldClearValidationScanNotice(current) ? null : current);
        controller.requestRender();
    }, [controller]);

    const insertFormulaTextAtCaret = useCallback((text: string) => {
        const token = String(text || '').trim();
        if (!token) return;
        const session = formulaEditSessionRef.current;
        if (session && session.status === 'active') {
            let draft = session.draft || '=';
            let caretStart = Math.max(0, Math.min(session.caretStart, draft.length));
            let caretEnd = Math.max(caretStart, Math.min(session.caretEnd, draft.length));
            if (!draft.trimStart().startsWith('=')) {
                draft = `=${draft}`;
                caretStart += 1;
                caretEnd += 1;
            }
            caretStart = Math.max(1, caretStart);
            caretEnd = Math.max(caretStart, caretEnd);
            const nextDraft = `${draft.slice(0, caretStart)}${token}${draft.slice(caretEnd)}`;
            const nextCaret = caretStart + token.length;
            const nextSession = updateFormulaDraft(session, nextDraft, nextCaret, nextCaret);
            formulaEditSessionRef.current = nextSession;
            setFormulaEditSession(nextSession);
            setCurrEditValue(nextSession.draft);
            setFormulaEditSurface('formula-bar');
            publishSharedFormulaEditSession(nextSession);
            controller.setIsEditing(true);
            return;
        }
        const selection = controller.selection;
        if (!selection) return;
        const draft = `=${token}`;
        const caret = draft.length;
        const nextSession = createFormulaEditSession({
            anchorCell: { col: selection.col, row: selection.row },
            sheetName,
            text: draft,
            caretStart: caret,
            caretEnd: caret,
        });
        formulaEditSessionRef.current = nextSession;
        setFormulaEditSession(nextSession);
        setCurrEditValue(nextSession.draft);
        setFormulaEditSurface('formula-bar');
        publishSharedFormulaEditSession(nextSession);
        controller.setIsEditing(true);
    }, [controller, sheetName]);

    const visibleDefinedNames = useMemo(() => getVisibleDefinedNames(definedNames), [definedNames]);

    const useInFormulaMenuItems = useMemo(() => {
        return buildUseInFormulaMenuDescriptors({
            visibleDefinedNames,
            structuredReferenceSuggestions: tableStructuredReferenceSuggestions,
            sheetNameByEngineId,
        }).map((entry) => ({
            id: entry.id,
            label: entry.label,
            title: entry.title,
            onClick: () => insertFormulaTextAtCaret(entry.insertText),
        }));
    }, [insertFormulaTextAtCaret, sheetNameByEngineId, tableStructuredReferenceSuggestions, visibleDefinedNames]);

    const pasteDefinedNames = useCallback(() => {
        const selection = controller.selection;
        const streamer = controller.streamer;
        if (!selection || !streamer || definedNames.length === 0) return;
        const tsv = buildDefinedNamesPasteTsv({ definedNames, sheetNameByEngineId });
        streamer.pasteData(Math.min(selection.col, selection.endCol), Math.min(selection.row, selection.endRow), tsv);
    }, [controller.selection, controller.streamer, definedNames, sheetNameByEngineId]);

    const handleCreateNamesFromSelection = useCallback(async (options: ExcelCreateNamesFromSelectionOptions) => {
        const selection = controller.selection;
        if (!selection) throw new Error('Select the range that contains labels and values.');
        const drafts = buildCreateNamesFromSelectionDrafts({
            selection,
            activeSheetId: activeEngineSheetId,
            activeSheetName: activeDefinedNameSheetName,
            existingNames: definedNames,
            getCellValue: (col, row) => controller.getCellValue(col, row),
            options,
        });
        for (const draft of drafts) {
            await Promise.resolve(handleUpsertDefinedName(draft));
        }
        void refreshDefinedNames();
    }, [activeDefinedNameSheetName, activeEngineSheetId, controller, definedNames, handleUpsertDefinedName, refreshDefinedNames]);

    const applyNamesToSelectedFormulas = useCallback(async () => {
        const selection = controller.selection;
        const streamer = controller.streamer;
        if (!selection || !streamer) return;
        const candidates = visibleDefinedNames.filter((entry) => entry.range);
        if (candidates.length === 0) return;
        await ensureFormulaAuthoringRustParser();
        const normalized = normalizeSelectionRange(selection);
        let replacementCount = 0;
        for (let row = normalized.row; row <= normalized.endRow; row += 1) {
            for (let col = normalized.col; col <= normalized.endCol; col += 1) {
                const rawFormula = streamer.getCellFormula?.(col, row) ?? controller.getCellValue(col, row) ?? '';
                if (!String(rawFormula).trimStart().startsWith('=')) continue;
                const analysis = analyzeFormulaAuthoringText(rawFormula);
                if (analysis.referenceSpans.length === 0) continue;
                let nextFormula = rawFormula;
                const spans = [...analysis.referenceSpans].sort((left, right) => right.startOffset - left.startOffset);
                for (const span of spans) {
                    const spanRange = formulaSpanRangeToSpreadsheetRange(span.range, activeEngineSheetId, sheetIdByName);
                    if (!spanRange) continue;
                    const match = candidates.find((entry) => rangesEqual(entry.range, spanRange));
                    if (!match) continue;
                    nextFormula = `${nextFormula.slice(0, span.startOffset)}${match.name}${nextFormula.slice(span.endOffset)}`;
                    replacementCount += 1;
                }
                if (nextFormula !== rawFormula) {
                    controller.setCellValue(col, row, nextFormula);
                }
            }
        }
        const resultNotice = buildApplyNamesResultNotice({ replacementCount });
        if (resultNotice) setInvalidEntryNotice(resultNotice);
    }, [activeEngineSheetId, controller, sheetIdByName, visibleDefinedNames]);

    const handleConditionalFormatRulesChanged = useCallback(() => {
        handleWorkbookChromeViewStateChange();
        window.setTimeout(() => {
            void refreshConditionalFormatRules();
        }, 0);
    }, [handleWorkbookChromeViewStateChange, refreshConditionalFormatRules]);

    const handleSparklineGroupsChanged = useCallback(() => {
        handleWorkbookChromeViewStateChange();
        void refreshSparklineGroups();
    }, [handleWorkbookChromeViewStateChange, refreshSparklineGroups]);

    const insertSparklineFromSelection = useCallback((nextType: SpreadsheetSparklineType) => {
        const defaultLocation = defaultSparklineLocationForSelection(selectedEngineRange);
        const plan = buildSparklineDialogApplyPlan({
            selectedRange: selectedEngineRange,
            editingGroupId: null,
            draft: {
                type: nextType,
                plotBy: 'rows',
                dataRangeDraft: formatSparklineRangeFormula(selectedEngineRange, selectedEngineRange.sheet_id),
                locationRangeDraft: formatSparklineRangeFormula(defaultLocation, selectedEngineRange.sheet_id),
                dateAxis: false,
                dateRangeDraft: '',
                emptyPolicy: 'gap',
                displayHidden: true,
                displayXAxis: false,
                rightToLeft: false,
                minAxis: 'individual',
                maxAxis: 'individual',
                manualMin: '',
                manualMax: '',
                lineWeight: '1.25',
                seriesColor: '#107c41',
                negativeColor: '#c2410c',
                axisColor: '#6b7280',
                markerColor: '#107c41',
                markers: false,
                highPoint: false,
                lowPoint: false,
                firstPoint: false,
                lastPoint: false,
                negativePoints: true,
            },
        });
        if (plan.action === 'blocked') {
            setInvalidEntryNotice(buildValidationStatusNotice({
                title: 'Cannot insert sparklines',
                message: plan.error,
                operationLabel: 'Insert sparklines',
            }));
            return;
        }
        controller.upsertSparklineGroup(plan.group);
        handleSparklineGroupsChanged();
    }, [controller, handleSparklineGroupsChanged, selectedEngineRange]);

    const handleClearSparklines = useCallback(() => {
        controller.clearSparklinesFromSelection();
        handleSparklineGroupsChanged();
    }, [controller, handleSparklineGroupsChanged]);

    const handleDeleteConditionalFormatRule = useCallback((ruleId: string) => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.deleteConditionalFormatRule !== 'function') return;
        streamer.deleteConditionalFormatRule(ruleId);
        handleConditionalFormatRulesChanged();
    }, [controller.streamer, handleConditionalFormatRulesChanged]);

    const handleReorderConditionalFormatRules = useCallback((ruleIds: string[]) => {
        const streamer = controller.streamer;
        if (!streamer || typeof streamer.reorderConditionalFormatRules !== 'function') return;
        streamer.reorderConditionalFormatRules(ruleIds);
        handleConditionalFormatRulesChanged();
    }, [controller.streamer, handleConditionalFormatRulesChanged]);

    const workbookCommandHandlers = useMemo<Partial<Record<SpreadsheetCommandId, ExcelCommandHandler>>>(() => {
        const validationCommands = buildValidationCommandPresentation({
            hasSelection: Boolean(controller.selection),
            hasStreamer: Boolean(controller.streamer),
            validationIssueCount: validationIssues.length,
        });
        const metadataCommands = buildCellMetadataCommandPresentation({
            hasSelection: Boolean(controller.selection),
            hasStreamer: Boolean(controller.streamer),
            activeSheetProtected,
        });
        const definedNameCommands = buildDefinedNameCommandPresentation({
            hasSelection: Boolean(controller.selection),
            hasStreamer: Boolean(controller.streamer),
            definedNameCount: definedNames.length,
            useInFormulaItemCount: useInFormulaMenuItems.length,
            hasVisibleRangeName: visibleDefinedNames.some((entry) => entry.range),
        });
        const formattingVisualCommands = buildFormattingVisualCommandPresentation({
            hasSelection: Boolean(controller.selection),
            hasStreamer: Boolean(controller.streamer),
        });
        const conditionalFormattingMenuItems = buildConditionalFormattingMenuDescriptors({
            canEditSelection: Boolean(controller.selection && controller.streamer),
        }).map((entry) => ({
            id: entry.id,
            label: entry.label,
            disabled: entry.disabled,
            onClick: () => {
                if (entry.id === 'new-rule') {
                    setShowConditionalFormatting(true);
                    return;
                }
                controller.clearConditionalFormatsFromSelection();
                handleConditionalFormatRulesChanged();
            },
        }));
        return {
        'insert-function': {
            enabled: Boolean(controller.selection),
            disabledReason: 'Select a cell before inserting a function',
            onClick: openInsertFunctionDialog,
        },
        'name-manager': {
            ...definedNameCommands['name-manager'],
            onClick: () => openNameManager(),
        },
        'define-name': {
            ...definedNameCommands['define-name'],
            onClick: () => openNameManager('new'),
        },
        'use-in-formula': {
            ...definedNameCommands['use-in-formula'],
            menuItems: useInFormulaMenuItems,
        },
        'create-from-selection': {
            ...definedNameCommands['create-from-selection'],
            onClick: () => setShowCreateNamesDialog(true),
        },
        'paste-names': {
            ...definedNameCommands['paste-names'],
            onClick: pasteDefinedNames,
        },
        'apply-names': {
            ...definedNameCommands['apply-names'],
            onClick: applyNamesToSelectedFormulas,
        },
        'conditional-formatting': {
            ...formattingVisualCommands['conditional-formatting'],
            onClick: () => setShowConditionalFormatting(true),
            menuItems: conditionalFormattingMenuItems,
        },
        'insert-sparkline-line': {
            ...formattingVisualCommands['insert-sparkline-line'],
            onClick: () => {
                const sparklineType = sparklineTypeForCommand('insert-sparkline-line');
                if (sparklineType) insertSparklineFromSelection(sparklineType);
            },
        },
        'insert-chart': {
            ...formattingVisualCommands['insert-chart'],
            onClick: () => {
                if (derivedArtifactKindForCommand('insert-chart') === 'chart') {
                    setChartDialogType('column');
                }
            },
        },
        'insert-pivot': {
            ...formattingVisualCommands['insert-pivot'],
            onClick: () => {
                if (derivedArtifactKindForCommand('insert-pivot') === 'pivot') {
                    controller.insertPivotArtifactFromSelection();
                }
            },
        },
        'insert-sparkline-column': {
            ...formattingVisualCommands['insert-sparkline-column'],
            onClick: () => {
                const sparklineType = sparklineTypeForCommand('insert-sparkline-column');
                if (sparklineType) insertSparklineFromSelection(sparklineType);
            },
        },
        'insert-sparkline-win-loss': {
            ...formattingVisualCommands['insert-sparkline-win-loss'],
            onClick: () => {
                const sparklineType = sparklineTypeForCommand('insert-sparkline-win-loss');
                if (sparklineType) insertSparklineFromSelection(sparklineType);
            },
        },
        'clear-sparklines': {
            ...formattingVisualCommands['clear-sparklines'],
            onClick: handleClearSparklines,
        },
        hyperlink: {
            ...metadataCommands.hyperlink,
            onClick: () => openCellMetadataDialog('hyperlink'),
        },
        'data-validation': {
            ...validationCommands['data-validation'],
            onClick: () => openCellMetadataDialog('validation'),
        },
        'circle-invalid-data': {
            ...validationCommands['circle-invalid-data'],
            onClick: handleCircleInvalidData,
        },
        'clear-validation-circles': {
            ...validationCommands['clear-validation-circles'],
            onClick: handleClearValidationCircles,
        },
        comments: {
            ...metadataCommands.comments,
            onClick: openCommentsPane,
        },
        'protect-cells': {
            ...metadataCommands['protect-cells'],
            onClick: () => openCellMetadataDialog('protection'),
        },
        'protect-sheet': {
            ...metadataCommands['protect-sheet'],
            onClick: handleOpenSheetProtectionDialog,
        },
    };
    }, [
        activeSheetProtected,
        applyNamesToSelectedFormulas,
        controller.selection,
        controller.streamer,
        definedNames.length,
        handleCircleInvalidData,
        handleClearValidationCircles,
        handleConditionalFormatRulesChanged,
        handleClearSparklines,
        handleOpenSheetProtectionDialog,
        insertSparklineFromSelection,
        openCellMetadataDialog,
        openCommentsPane,
        openInsertFunctionDialog,
        openNameManager,
        pasteDefinedNames,
        useInFormulaMenuItems,
        validationIssues.length,
        visibleDefinedNames,
    ]);

    const ribbonCommandHandlers = useMemo<Partial<Record<SpreadsheetCommandId, ExcelCommandHandler>>>(() => {
        const merged: Partial<Record<SpreadsheetCommandId, ExcelCommandHandler>> = {
            ...workbookCommandHandlers,
            ...(commandHandlers || {}),
        };
        if (!commandHandlers?.['connect-data']) {
            merged['connect-data'] = { enabled: false, visible: false };
        }
        return merged;
    }, [commandHandlers, workbookCommandHandlers]);

    const setScrollContainerEl = useCallback((el: HTMLDivElement | null) => {
        scrollContainerRef.current = el;
        if (externalScrollRef) externalScrollRef.current = el;
        if (!el || scrollElRef.current === el) return;
        scrollElRef.current = el;
        setScrollEl(el);
    }, [externalScrollRef]);

    // Ensure bounds update when dimensions change (Fixed ordering)
    useEffect(() => {
        updateVisibleBounds();
    }, [rowCount, colCount, updateVisibleBounds]);

    // We need to know column widths and row heights to render headers correctly.
    const getColWidth = (i: number) => controller.getColumnWidth(i);
    const getRowHeight = (i: number) => controller.getRowHeight(i);

    // Use precise total dimensions from controller (world space)
    const totalWidth = controller.getTotalWidth();
    const totalHeightWorld = controller.getTotalHeight();
    const windowedScroll = totalHeightWorld > SCROLL_WINDOW_PX;
    const maxScrollBaseY = windowedScroll ? Math.max(0, totalHeightWorld - SCROLL_WINDOW_PX) : 0;
    const totalHeightDom = windowedScroll ? SCROLL_WINDOW_PX : totalHeightWorld;

    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const applyHeaderRailTransforms = useCallback((scrollLeft: number, domScrollTop: number) => {
        const x = Math.max(0, Number(scrollLeft) || 0);
        const y = Math.max(0, Number(domScrollTop) || 0);
        if (colHeaderRef.current && colHeaderRef.current.scrollLeft !== 0) {
            colHeaderRef.current.scrollLeft = 0;
        }
        if (rowHeaderRef.current && rowHeaderRef.current.scrollTop !== 0) {
            rowHeaderRef.current.scrollTop = 0;
        }
        if (colHeaderContentRef.current) {
            colHeaderContentRef.current.style.transform = `translate3d(${-x}px, 0, 0)`;
        }
        if (rowHeaderContentRef.current) {
            rowHeaderContentRef.current.style.transform = `translate3d(0, ${-y}px, 0)`;
        }
    }, []);

    const publishViewportSnapshot = useCallback((
        el?: HTMLDivElement | null,
        worldX?: number,
        worldY?: number,
    ) => {
        if (!viewportStore) return;
        const target = el || scrollContainerRef.current;
        if (!target) return;
        const domScrollLeft = Math.max(0, Number(target.scrollLeft || 0));
        const domScrollTop = Math.max(0, Number(target.scrollTop || 0));
        const left = Math.max(0, Number(worldX ?? domScrollLeft));
        const top = Math.max(0, Number(worldY ?? (windowedScroll ? (scrollBaseYRef.current + domScrollTop) : domScrollTop)));
        const width = Math.max(1, Number(target.clientWidth || controller.getViewport().width || 1));
        const height = Math.max(1, Number(target.clientHeight || controller.getViewport().height || 1));
        viewportStore.setSnapshot({
            left,
            top,
            width,
            height,
            domScrollLeft,
            domScrollTop,
            scrollBaseY: Math.max(0, Number(scrollBaseYRef.current || 0)),
            totalWidth: Math.max(0, Number(totalWidth || 0)),
            totalHeight: Math.max(0, Number(totalHeightWorld || 0)),
            isWindowed: windowedScroll,
        });
    }, [controller, totalHeightWorld, totalWidth, viewportStore, windowedScroll]);

    // Sync Scroll
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        perf('headers.handleScroll', () => {
            // Mark that this scroll came from the user, so controller->DOM sync doesn't fight momentum.
            isUserScrollingRef.current = true;
            if (clearUserScrollTimeoutRef.current !== null) {
                window.clearTimeout(clearUserScrollTimeoutRef.current);
            }
            clearUserScrollTimeoutRef.current = window.setTimeout(() => {
                isUserScrollingRef.current = false;
                clearUserScrollTimeoutRef.current = null;
            }, 120);

            const { scrollLeft, scrollTop } = e.currentTarget;
            // Sync Footer (Horizontal Pin)
            if (footerRef.current) {
                footerRef.current.style.transform = `translateX(${scrollLeft}px)`;
            }

            // Map DOM scrollTop into world scrollY using windowed scroll base
            let baseY = scrollBaseYRef.current;
            if (windowedScroll) {
                if (scrollTop < SCROLL_RECENTER_MARGIN_PX && baseY > 0) {
                    const shift = Math.min(baseY, SCROLL_WINDOW_PX / 2);
                    baseY -= shift;
                    scrollBaseYRef.current = baseY;
                    e.currentTarget.scrollTop = scrollTop + shift;
                } else if (scrollTop > (SCROLL_WINDOW_PX - SCROLL_RECENTER_MARGIN_PX) && baseY < maxScrollBaseY) {
                    const shift = Math.min(maxScrollBaseY - baseY, SCROLL_WINDOW_PX / 2);
                    baseY += shift;
                    scrollBaseYRef.current = baseY;
                    e.currentTarget.scrollTop = scrollTop - shift;
                }
            } else if (baseY !== 0) {
                scrollBaseYRef.current = 0;
                baseY = 0;
            }

            // Keep headers visually in sync with the scroll container during user scroll.
            applyHeaderRailTransforms(scrollLeft, e.currentTarget.scrollTop);

            // On iOS, doing heavy work directly in the scroll event kills momentum.
            // For "normal" scroll ranges, coalesce to one RAF and render in that same frame
            // so overlay content and the grid canvas do not drift by an extra frame.
            if (!windowedScroll) {
                pendingDomScrollRef.current = { x: scrollLeft, y: scrollTop };
                if (applyDomScrollRafRef.current === null) {
                    applyDomScrollRafRef.current = requestAnimationFrame(() => {
                        applyDomScrollRafRef.current = null;
                        const p = pendingDomScrollRef.current;
                        if (!p) return;
                        controller.setScroll(p.x, p.y, { renderMode: 'direct' });
                        refreshViewportProjections();
                        updateVisibleBounds();
                        publishViewportSnapshot(e.currentTarget, p.x, p.y);
                    });
                }
                return;
            }

            const worldY = windowedScroll ? (scrollBaseYRef.current + e.currentTarget.scrollTop) : scrollTop;

            // Sync Controller (world space)
            controller.setScroll(scrollLeft, worldY);
            refreshViewportProjections();

            // Infinite Scroll removed to mimic Google Sheets manual control
            // if (!bareMode && !isLoadingMore) { ... }

            // Update virtualization
            updateVisibleBounds();
            publishViewportSnapshot(e.currentTarget, scrollLeft, worldY);
        });
    }, [applyHeaderRailTransforms, controller, updateVisibleBounds, windowedScroll, maxScrollBaseY, publishViewportSnapshot, refreshViewportProjections]);


    // Resize Logic
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizing) return;

            if (resizing.type === 'col') {
                const delta = e.clientX - resizing.startPos;
                const newWidth = Math.max(20, resizing.startSize + delta);
                controller.setColumnWidth(resizing.index, newWidth, { displaySize: true });
            } else {
                const delta = e.clientY - resizing.startPos;
                const newHeight = Math.max(20, resizing.startSize + delta);
                controller.setRowHeight(resizing.index, newHeight, { displaySize: true });
            }
            setTick(t => t + 1);
            onGeometryChange?.();
            updateVisibleBounds(); // Re-check bounds on resize
        };

        const handleMouseUp = () => {
            const currentResize = resizing;
            if (!currentResize) return;
            if (currentResize.type === 'col') {
                const displayWidth = Math.max(20, controller.getColumnWidth(currentResize.index));
                const logicalWidth = Math.max(20, controller.getColumnLogicalWidth(currentResize.index));
                if (displayWidth !== currentResize.startSize) onColumnResizeCommit?.(currentResize.index, logicalWidth);
            } else {
                const displayHeight = Math.max(20, controller.getRowHeight(currentResize.index));
                const logicalHeight = Math.max(20, controller.getRowLogicalHeight(currentResize.index));
                if (displayHeight !== currentResize.startSize) onRowResizeCommit?.(currentResize.index, logicalHeight);
            }
            setResizing(null);
            document.body.style.cursor = 'default';
        };

        if (resizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizing, controller, onColumnResizeCommit, onGeometryChange, onRowResizeCommit, updateVisibleBounds]);

    const beginFormulaEditSession = useCallback((col: number, row: number, value: string, caretStart = value.length, caretEnd = caretStart, surface: FormulaEditSurface = 'cell') => {
        const session = createFormulaEditSession({
            anchorCell: { col, row },
            sheetName,
            text: value,
            caretStart,
            caretEnd,
        });
        setFormulaEditSurface(surface);
        formulaEditSessionRef.current = session;
        setFormulaEditSession(session);
        setCurrEditValue(session.draft);
        publishSharedFormulaEditSession(session);
    }, [sheetName]);

    const updateFormulaEditDraft = useCallback((value: string, caretStart = value.length, caretEnd = caretStart) => {
        setCurrEditValue(value);
        const current = formulaEditSessionRef.current;
        if (!current) {
            const selection = controller.selection ?? selectedRange ?? selectedCell ?? { col: 0, row: 0 };
            const session = createFormulaEditSession({
                anchorCell: { col: selection.col, row: selection.row },
                sheetName,
                text: value,
                caretStart,
                caretEnd,
            });
            formulaEditSessionRef.current = session;
            publishSharedFormulaEditSession(session);
            setFormulaEditSession(session);
            setFormulaEditSurface((surface) => surface ?? 'formula-bar');
            controller.setIsEditing(true);
            return;
        }
        const next = updateFormulaDraft(current, value, caretStart, caretEnd);
        formulaEditSessionRef.current = next;
        publishSharedFormulaEditSession(next);
        setFormulaEditSession(next);
    }, [controller, selectedCell, selectedRange, sheetName]);

    const acceptFormulaEditSession = useCallback((session: FormulaEditSession) => {
        formulaEditSessionRef.current = session;
        setFormulaEditSession(session);
        setCurrEditValue(session.status === 'active' ? session.draft : null);
        publishSharedFormulaEditSession(session);
    }, []);

    const finishFormulaEditSession = useCallback(() => {
        const current = formulaEditSessionRef.current;
        if (isActiveFormulaReferenceEditSession(current)) {
            setFormulaEditSession(current);
            setCurrEditValue(current.draft);
            publishSharedFormulaEditSession(current);
            controller.setIsEditing(true);
            return;
        }
        setFormulaEditSession(null);
        formulaEditSessionRef.current = null;
        setCurrEditValue(null);
        setFormulaEditSurface(null);
        clearSharedFormulaEditSession();
    }, [controller]);

    const beginFormulaBarEdit = useCallback((value: string, caretStart: number, caretEnd: number) => {
        const current = formulaEditSessionRef.current;
        if (isActiveFormulaReferenceEditSession(current)) {
            const next = updateFormulaDraft(current, current.draft, caretStart, caretEnd);
            formulaEditSessionRef.current = next;
            setFormulaEditSession(next);
            setCurrEditValue(next.draft);
            setFormulaEditSurface('formula-bar');
            publishSharedFormulaEditSession(next);
            controller.setIsEditing(true);
            return;
        }
        const selection = controller.selection;
        if (!selection) return;
        beginFormulaEditSession(selection.col, selection.row, value, caretStart, caretEnd, 'formula-bar');
    }, [beginFormulaEditSession, controller]);

    const insertFunctionAtFormulaCaret = useCallback((functionName: string, args: string[], editTarget?: FormulaFunctionEditTarget | null) => {
        const session = formulaEditSessionRef.current;
        const replacement = buildFormulaFunctionInsertionDraft({
            session,
            functionName,
            functionArgs: args,
            editTarget,
        });
        if (replacement) {
            updateFormulaEditDraft(replacement.draft, replacement.caretStart, replacement.caretEnd);
            setFormulaEditSurface('formula-bar');
            controller.setIsEditing(true);
            return;
        }
        const call = buildFormulaFunctionCall(functionName, args);
        if (!call) return;
        insertFormulaTextAtCaret(call);
    }, [controller, insertFormulaTextAtCaret, updateFormulaEditDraft]);

    useEffect(() => {
        let cancelled = false;
        void ensureFormulaAuthoringRustParser().then(() => {
            if (cancelled) return;
            const current = formulaEditSessionRef.current;
            if (!current || current.status !== 'active') return;
            const next = updateFormulaDraft(current, current.draft, current.caretStart, current.caretEnd);
            formulaEditSessionRef.current = next;
            setFormulaEditSession(next);
            publishSharedFormulaEditSession(next);
        }).catch((error) => {
            console.warn('[spreadsheet] failed to initialize Rust formula authoring parser', error);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const shared = getSharedFormulaEditSession();
        if (!isActiveFormulaReferenceEditSession(shared)) return;
        const next = resumeFormulaEditSessionForSheet(shared, sheetName);
        formulaEditSessionRef.current = next;
        setFormulaEditSession(next);
        setCurrEditValue(next.draft);
        setFormulaEditSurface('cell');
        publishSharedFormulaEditSession(next);
        controller.setIsEditing(true);
        if (sheetName && next.anchorSheetName && sheetName.toLowerCase() === next.anchorSheetName.toLowerCase()) {
            const selected = controller.setSelection({
                col: next.anchorCell.col,
                row: next.anchorCell.row,
                endCol: next.anchorCell.col,
                endRow: next.anchorCell.row,
            }, { force: true });
            if (selected) controller.notifySelectionChange();
        }
    }, [controller, sheetName]);

    useEffect(() => {
        formulaEditSessionRef.current = formulaEditSession;
        if (!formulaEditSession && isActiveFormulaReferenceEditSession(getSharedFormulaEditSession())) return;
        publishSharedFormulaEditSession(formulaEditSession);
    }, [formulaEditSession]);

    useEffect(() => {
        controller.setFormulaRangeSelectionCallback((request) => {
            const session = formulaEditSessionRef.current;
            if (!session || session.status !== 'active' || session.mode === 'value') return false;
            const activeFormulaSheetName = request.range.sheetName ?? sheetName;
            const requestRange = { ...request.range, sheetName: request.range.sheetName ?? undefined };
            const activeSession = resumeFormulaEditSessionForSheet(session, activeFormulaSheetName);
            const next = insertOrReplaceFormulaRange(
                activeSession,
                qualifyFormulaRangeForActiveSheet(activeSession, requestRange, activeFormulaSheetName),
            );
            formulaEditSessionRef.current = next;
            setFormulaEditSession(next);
            setCurrEditValue(next.draft);
            publishSharedFormulaEditSession(next);
            return true;
        });
        return () => {
            controller.setFormulaRangeSelectionCallback(null);
        };
    }, [controller, sheetName]);

    const updateFormulaReferenceResizeAtClientPoint = useCallback((clientX: number, clientY: number) => {
        const drag = formulaReferenceResizeRef.current;
        const session = formulaEditSessionRef.current;
        if (!drag || !session || session.status !== 'active') return;
        const cell = controller.getCellAtClientPoint(clientX, clientY);
        if (!cell) return;
        const next = replaceFormulaReferenceRangeById(session, drag.referenceId, {
            sheetName: drag.sheetName,
            start: drag.anchor,
            end: cell,
        });
        formulaEditSessionRef.current = next;
        setFormulaEditSession(next);
        setCurrEditValue(next.draft);
        publishSharedFormulaEditSession(next);
    }, [controller]);

    const updateFormulaReferenceMoveAtClientPoint = useCallback((clientX: number, clientY: number) => {
        const drag = formulaReferenceMoveRef.current;
        const session = formulaEditSessionRef.current;
        if (!drag || !session || session.status !== 'active') return;
        const cell = controller.getCellAtClientPoint(clientX, clientY);
        if (!cell) return;
        const next = moveFormulaReferenceRangeById(session, drag.referenceId, cell);
        formulaEditSessionRef.current = next;
        setFormulaEditSession(next);
        setCurrEditValue(next.draft);
        publishSharedFormulaEditSession(next);
    }, [controller]);

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (!formulaReferenceResizeRef.current && !formulaReferenceMoveRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            if (formulaReferenceResizeRef.current) {
                updateFormulaReferenceResizeAtClientPoint(event.clientX, event.clientY);
            } else {
                updateFormulaReferenceMoveAtClientPoint(event.clientX, event.clientY);
            }
        };
        const handleMouseUp = (event: MouseEvent) => {
            if (!formulaReferenceResizeRef.current && !formulaReferenceMoveRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            if (formulaReferenceResizeRef.current) {
                updateFormulaReferenceResizeAtClientPoint(event.clientX, event.clientY);
            } else {
                updateFormulaReferenceMoveAtClientPoint(event.clientX, event.clientY);
            }
            formulaReferenceResizeRef.current = null;
            formulaReferenceMoveRef.current = null;
        };
        const handleWindowBlur = () => {
            formulaReferenceResizeRef.current = null;
            formulaReferenceMoveRef.current = null;
        };

        window.addEventListener('mousemove', handleMouseMove, true);
        window.addEventListener('mouseup', handleMouseUp, true);
        window.addEventListener('blur', handleWindowBlur, true);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove, true);
            window.removeEventListener('mouseup', handleMouseUp, true);
            window.removeEventListener('blur', handleWindowBlur, true);
        };
    }, [updateFormulaReferenceMoveAtClientPoint, updateFormulaReferenceResizeAtClientPoint]);

    // Sync from Controller to DOM (e.g. Zoom or programmatic scroll)
    useEffect(() => {
        if (!bareMode) {
            controller.setSelectionChangeCallback((selection) => {
                setSelectedRange(selection);
                if (selection) {
                    setSelectedCell(prev => {
                        if (prev && prev.col === selection.col && prev.row === selection.row) {
                            return prev;
                        }
                        return { col: selection.col, row: selection.row };
                    });
                } else {
                    setSelectedCell(null);
                }
            });

            controller.setStartEditCallback((col, row, rect, initialInput, format) => {
                let value = initialInput;
                if (value === undefined) {
                    const formula = controller.getCellFormula(col, row);
                    value = formula !== undefined ? formula : (controller.getCellValue(col, row) || '');
                }
                setEditingCell({ col, row, rect, initialValue: value, format });
                beginFormulaEditSession(col, row, value);
                controller.setIsEditing(true);
            });

            controller.setContextMenuCallback((x, y, col, row) => {
                setContextMenu({ kind: 'cell', x, y, col, row });
            });
        } else {
            controller.setSelectionChangeCallback(() => { });
            controller.setStartEditCallback(() => { });
            controller.setContextMenuCallback(() => { });
        }

        controller.setScrollChangeCallback((x, y) => {
            // During user scrolling we already updated DOM/header scroll positions in `handleScroll`.
            // Avoid extra DOM writes here (especially iOS) as it kills momentum.
            if (isUserScrollingRef.current) {
                return;
            }

            const el = scrollContainerRef.current;
            if (el) {
                const currentEl = el;
                const currentX = currentEl.scrollLeft;
                const currentY = currentEl.scrollTop;
                const domY = (() => {
                    if (!windowedScroll) {
                        scrollBaseYRef.current = 0;
                        return y;
                    }
                    // Keep current base if y is inside window; recenter only on big jumps.
                    let base = scrollBaseYRef.current;
                    if (y < base || y > base + SCROLL_WINDOW_PX) {
                        base = Math.min(Math.max(y - SCROLL_WINDOW_PX / 2, 0), maxScrollBaseY);
                        scrollBaseYRef.current = base;
                    }
                    base = Math.min(Math.max(scrollBaseYRef.current, 0), maxScrollBaseY);
                    scrollBaseYRef.current = base;
                    return Math.max(0, Math.min(SCROLL_WINDOW_PX, y - base));
                })();

                if (Math.abs(currentX - x) > 0.1) {
                    currentEl.scrollLeft = x;
                }
                if (Math.abs(currentY - domY) > 0.1) {
                    currentEl.scrollTop = domY;
                }

                applyHeaderRailTransforms(x, domY);
            }
            if (footerRef.current) {
                footerRef.current.style.transform = `translateX(${x}px)`;
            }
            // Keep virtualization bounds aligned with programmatic scroll jumps (e.g., Cmd+Arrow)
            refreshViewportProjections();
            updateVisibleBounds();
            publishViewportSnapshot(scrollContainerRef.current, x, y);
        });

        // Initial bounds check
        updateVisibleBounds();

        // Resize Observer to keep bounds in sync
        const handleResize = () => {
            requestAnimationFrame(() => {
                updateVisibleBounds();
                publishViewportSnapshot();
            });
        };
        const resizeObserver = new ResizeObserver(handleResize);
        if (scrollContainerRef.current) {
            resizeObserver.observe(scrollContainerRef.current);
        }

        // Listen to Controller Resize (from SpreadsheetCanvas)
        controller.setResizeCallback(() => {
            updateVisibleBounds();
        });

        const safetyTimer = setTimeout(() => {
            updateVisibleBounds();
            publishViewportSnapshot();
        }, 100);

        return () => {
            controller.setScrollChangeCallback(() => { });
            controller.setSelectionChangeCallback(() => { });
            controller.setStartEditCallback(() => { });
            controller.setContextMenuCallback(() => { });
            controller.setResizeCallback(() => { });
            resizeObserver.disconnect();
            clearTimeout(safetyTimer);
        };
    }, [applyHeaderRailTransforms, bareMode, beginFormulaEditSession, controller, updateVisibleBounds, windowedScroll, maxScrollBaseY, publishViewportSnapshot, refreshViewportProjections]);

    useEffect(() => {
        publishViewportSnapshot();
    }, [publishViewportSnapshot]);

    const [contextMenu, setContextMenu] = useState<
        SpreadsheetContextMenuState
        | null
    >(null);
    const [pasteOptions, setPasteOptions] = useState<SpreadsheetPasteOptionsState | null>(null);
    const [pasteOptionsMenu, setPasteOptionsMenu] = useState<{ x: number, y: number } | null>(null);
    const [fillOptions, setFillOptions] = useState<SpreadsheetFillOptionsState | null>(null);
    const [fillOptionsMenu, setFillOptionsMenu] = useState<{ x: number, y: number } | null>(null);
    const [validationDropdownMenu, setValidationDropdownMenu] = useState<{ x: number, y: number } | null>(null);
    const [autoFilterMenu, setAutoFilterMenu] = useState<AutoFilterMenuState | null>(null);
    const [validationDropdownOptions, setValidationDropdownOptions] = useState<SpreadsheetValidationDropdownOptionsResponsePayload | null>(null);
    const autoFilterPopoverRef = useRef<HTMLDivElement | null>(null);
    const activeAutoFilterForMenu = useMemo<SpreadsheetAutoFilter | null>(() => (
        resolveActiveAutoFilterForMenu(autoFilterMenu, activeSheetAutoFilter)
    ), [activeSheetAutoFilter, autoFilterMenu?.range, autoFilterMenu?.col, autoFilterMenu?.row]);

    useEffect(() => {
        if (!autoFilterMenu) return;
        if (!isAutoFilterMenuTargetValid(autoFilterMenu)) {
            setAutoFilterMenu(null);
        }
    }, [autoFilterMenu]);

    useEffect(() => {
        if (!autoFilterMenu) return undefined;
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target instanceof Node ? event.target : null;
            if (target && autoFilterPopoverRef.current?.contains(target)) return;
            setAutoFilterMenu(null);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [autoFilterMenu]);

    useEffect(() => {
        if (!autoFilterMenu || !activeAutoFilterForMenu || !controller.streamer) return;
        const readPlan = buildAutoFilterValueReadPlan(autoFilterMenu, activeAutoFilterForMenu);
        let cancelled = false;
        setAutoFilterMenu((current) => markAutoFilterValueLoadStarted(current, readPlan));
        if (!readPlan.read) {
            setAutoFilterMenu((current) => applyAutoFilterEmptyValueLoad(current, activeAutoFilterForMenu, readPlan));
            return;
        }
        void controller.streamer.getRangeCells(
            readPlan.read.startCol,
            readPlan.read.startRow,
            readPlan.read.endCol,
            readPlan.read.endRow,
            readPlan.read.mode,
        )
            .then((cells) => {
                if (cancelled) return;
                setAutoFilterMenu((current) => applyAutoFilterValueLoadSuccess(current, activeAutoFilterForMenu, readPlan, cells));
            })
            .catch((error) => {
                if (cancelled) return;
                setAutoFilterMenu((current) => applyAutoFilterValueLoadError(current, readPlan, error));
            });
        return () => {
            cancelled = true;
        };
    }, [activeAutoFilterForMenu, autoFilterMenu?.col, autoFilterMenu?.row, controller]);
    /*
    const [sheets, setSheets] = useState<{ id: number, name: string }[]>([{ id: 1, name: 'Sheet1' }]);
    const [activeSheetId, setActiveSheetId] = useState(1);

    useEffect(() => {
        if (controller.streamer) {
            controller.streamer.setSheetsCallback((list) => {
                setSheets(list);
            });
            controller.streamer.getSheets();
        }
    }, [controller.streamer]);

    const handleSwitchSheet = (id: number) => {
        setActiveSheetId(id);
        if (controller.streamer) {
            controller.streamer.switchSheet(id);
        }
    };

    const handleAddSheet = () => {
        if (controller.streamer) {
            const name = `Sheet${sheets.length + 1}`;
            controller.streamer.addSheet(name);
        }
    };
    */

    // Find/Replace State
    const [showFind, setShowFind] = useState(false);
    const [findMode, setFindMode] = useState<FindDialogMode>('find');
    const [findQuery, setFindQuery] = useState('');
    const [replaceValue, setReplaceValue] = useState('');
    const [findScope, setFindScope] = useState<FindDialogScope>('sheet');
    const [matches, setMatches] = useState<SpreadsheetFindMatch[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

    const positionPasteOptions = useCallback((context: PasteOptionsContext) => {
        const targetCol = Math.max(0, Math.floor(Number(context.targetCol) || 0));
        const targetRow = Math.max(0, Math.floor(Number(context.targetRow) || 0));
        const colCountForRect = Math.max(1, Math.floor(Number(context.colCount) || 1));
        const rowCountForRect = Math.max(1, Math.floor(Number(context.rowCount) || 1));
        const rect = controller.getRangeScreenRect({
            start: { col: targetCol, row: targetRow },
            end: { col: targetCol + colCountForRect - 1, row: targetRow + rowCountForRect - 1 },
        });
        if (!rect) {
            setPasteOptions(null);
            setPasteOptionsMenu(null);
            return;
        }
        const viewport = controller.getViewport();
        const maxX = Math.max(8, viewport.width - 156);
        const maxY = Math.max(8, viewport.height - 44);
        setPasteOptions({
            ...context,
            targetCol,
            targetRow,
            rowCount: rowCountForRect,
            colCount: colCountForRect,
            x: Math.max(8, Math.min(maxX, rect.x + rect.w + 8)),
            y: Math.max(8, Math.min(maxY, rect.y + rect.h + 8)),
        });
        setPasteOptionsMenu(null);
    }, [controller]);

    const positionFillOptions = useCallback((context: FillOptionsContext) => {
        const targetCol = Math.max(0, Math.floor(Number(context.targetCol) || 0));
        const targetRow = Math.max(0, Math.floor(Number(context.targetRow) || 0));
        const targetWidth = Math.max(1, Math.floor(Number(context.targetWidth) || 1));
        const targetHeight = Math.max(1, Math.floor(Number(context.targetHeight) || 1));
        const rect = controller.getRangeScreenRect({
            start: { col: targetCol, row: targetRow },
            end: { col: targetCol + targetWidth - 1, row: targetRow + targetHeight - 1 },
        });
        if (!rect) {
            setFillOptions(null);
            setFillOptionsMenu(null);
            return;
        }
        const viewport = controller.getViewport();
        const maxX = Math.max(8, viewport.width - 168);
        const maxY = Math.max(8, viewport.height - 44);
        setFillOptions({
            ...context,
            targetCol,
            targetRow,
            targetWidth,
            targetHeight,
            sourceCol: Math.max(0, Math.floor(Number(context.sourceCol) || 0)),
            sourceRow: Math.max(0, Math.floor(Number(context.sourceRow) || 0)),
            sourceWidth: Math.max(1, Math.floor(Number(context.sourceWidth) || 1)),
            sourceHeight: Math.max(1, Math.floor(Number(context.sourceHeight) || 1)),
            x: Math.max(8, Math.min(maxX, rect.x + rect.w + 8)),
            y: Math.max(8, Math.min(maxY, rect.y + rect.h + 8)),
        });
        setFillOptionsMenu(null);
    }, [controller]);

    const positionInvalidEntryNotice = useCallback((error: SpreadsheetMutationError) => {
        setInvalidEntryNotice(buildInvalidEntryNoticeFromMutationError({
            error,
            validationRules,
            activeSheetName: activeDefinedNameSheetName,
            activeSheetProtected,
            getCellMetadata: (col, row) => controller.getCellMetadata(col, row),
            getCellRect: (col, row) => controller.getRangeScreenRect({
                start: { col, row },
                end: { col, row },
            }),
            viewport: controller.getViewport(),
        }));
    }, [activeDefinedNameSheetName, activeSheetProtected, controller, validationRules]);

    const handleInvalidEntryAction = useCallback((actionId: string) => {
        const workflow = buildInvalidEntryActionWorkflowPlan({
            notice: invalidEntryNotice,
            actionId,
        });
        for (const effect of workflow.effects) {
            switch (effect.kind) {
                case 'apply-validation-override':
                    if (effect.retryWithValidationOverride) {
                        effect.retryWithValidationOverride();
                    } else if (effect.attemptedValue != null) {
                        controller.setCellValue(effect.col, effect.row, effect.attemptedValue, { validationOverride: true });
                    }
                    break;
                case 'set-selection':
                    controller.setSelection(effect.selection);
                    break;
                case 'notify-selection-change':
                    controller.notifySelectionChange();
                    break;
                case 'scroll-to-cell':
                    controller.scrollToCell(effect.col, effect.row);
                    break;
                case 'dismiss-notice':
                    setInvalidEntryNotice(null);
                    break;
                case 'close-validation-dropdown':
                    setValidationDropdownMenu(null);
                    break;
                case 'refresh-validation-rules':
                    void refreshValidationRules();
                    break;
                case 'open-cell-metadata-mode':
                    setCellMetadataMode(effect.mode);
                    break;
                case 'open-sheet-protection':
                    handleOpenSheetProtectionDialog();
                    break;
                case 'start-editing-deferred':
                    window.setTimeout(() => {
                        controller.startEditing(effect.col, effect.row, effect.value);
                    }, 0);
                    break;
                case 'request-render':
                    controller.requestRender();
                    break;
                case 'workbook-chrome-view-state-change':
                    handleWorkbookChromeViewStateChange();
                    break;
            }
        }
    }, [
        controller,
        handleOpenSheetProtectionDialog,
        handleWorkbookChromeViewStateChange,
        invalidEntryNotice,
        refreshValidationRules,
    ]);

    const validationIssueMarkers = useMemo(() => buildValidationIssueMarkerProjections({
        disabled: bareMode,
        issues: validationIssues,
        activeSheetId: activeEngineSheetId,
        visibleBounds,
        getCellRect: (col, row) => controller.getRangeScreenRect({
            start: { col, row },
            end: { col, row },
        }),
    }), [
        activeEngineSheetId,
        bareMode,
        controller,
        validationIssues,
        viewportProjectionVersion,
        visibleBounds.startCol,
        visibleBounds.endCol,
        visibleBounds.startRow,
        visibleBounds.endRow,
    ]);

    useEffect(() => {
        if (bareMode) {
            controller.setPasteOptionsCallback(null);
            return;
        }
        controller.setPasteOptionsCallback(positionPasteOptions);
        return () => controller.setPasteOptionsCallback(null);
    }, [bareMode, controller, positionPasteOptions]);

    useEffect(() => {
        if (bareMode) {
            controller.setFillOptionsCallback(null);
            return;
        }
        controller.setFillOptionsCallback(positionFillOptions);
        return () => controller.setFillOptionsCallback(null);
    }, [bareMode, controller, positionFillOptions]);

    useEffect(() => {
        const streamer = controller.streamer;
        if (bareMode || !streamer) {
            streamer?.setMutationErrorCallback(null);
            return undefined;
        }
        streamer.setMutationErrorCallback(positionInvalidEntryNotice);
        return () => {
            if (controller.streamer === streamer) streamer.setMutationErrorCallback(null);
        };
    }, [bareMode, controller, controller.streamer, positionInvalidEntryNotice]);

    useEffect(() => {
        setPasteOptions(null);
        setPasteOptionsMenu(null);
    }, [
        selectedRange?.col,
        selectedRange?.row,
        selectedRange?.endCol,
        selectedRange?.endRow,
        visibleBounds.startCol,
        visibleBounds.startRow,
    ]);

    useEffect(() => {
        setFillOptions(null);
        setFillOptionsMenu(null);
    }, [
        visibleBounds.startCol,
        visibleBounds.startRow,
    ]);

    useEffect(() => {
        setInvalidEntryNotice(null);
    }, [
        selectedRange?.col,
        selectedRange?.row,
        selectedRange?.endCol,
        selectedRange?.endRow,
        visibleBounds.startCol,
        visibleBounds.startRow,
    ]);

    useEffect(() => {
        setValidationDropdownMenu(null);
    }, [
        selectedRange?.col,
        selectedRange?.row,
        selectedRange?.endCol,
        selectedRange?.endRow,
        selectedValidationRule?.id,
        visibleBounds.startCol,
        visibleBounds.startRow,
    ]);

    const selectMatch = useCallback((match: SpreadsheetFindMatch) => {
        controller.setSelection({
            col: match.col,
            row: match.row,
            endCol: match.col,
            endRow: match.row
        });
        controller.notifySelectionChange();
        controller.scrollToCell(match.col, match.row);
        controller.requestRender();
    }, [controller]);

    const handleFind = useCallback((query: string) => {
        const nextQuery = String(query ?? '');
        if (nextQuery === '') {
            setMatches([]);
            setCurrentMatchIndex(0);
            return;
        }
        if (controller.streamer) {
            controller.streamer.findText(nextQuery);
            return;
        }
        const localMatches = controller.findTextInLocalPreview(nextQuery);
        setMatches(localMatches);
        setCurrentMatchIndex(0);
        if (localMatches.length > 0) {
            selectMatch(localMatches[0]);
        }
    }, [controller, selectMatch]);

    const openFindDialog = useCallback((mode: FindDialogMode = 'find') => {
        setFindMode(mode);
        setShowFind(true);
        if (findQuery) handleFind(findQuery);
    }, [findQuery, handleFind]);

    useEffect(() => {
        if (controller.streamer) {
            controller.streamer.setFindCallback((results) => {
                setMatches(results);
                setCurrentMatchIndex(0);
                if (results.length > 0) {
                    selectMatch(results[0]);
                }
            });
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            const key = String(e.key || '').toLowerCase();
            if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || (key !== 'f' && key !== 'h')) return;
            const target = e.target as HTMLElement | null;
            if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
            e.preventDefault();
            e.stopPropagation();
            openFindDialog(key === 'h' ? 'replace' : 'find');
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [controller.streamer, openFindDialog, selectMatch]);

    const handleNextMatch = () => {
        if (matches.length === 0) return;
        const nextIndex = (currentMatchIndex + 1) % matches.length;
        setCurrentMatchIndex(nextIndex);
        selectMatch(matches[nextIndex]);
    };

    const handlePrevMatch = () => {
        if (matches.length === 0) return;
        const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
        setCurrentMatchIndex(prevIndex);
        selectMatch(matches[prevIndex]);
    };

    const handleFindQueryChange = (query: string) => {
        setFindQuery(query);
        handleFind(query);
    };

    const handleReplaceCurrent = () => {
        if (!controller.streamer || !findQuery || matches.length <= 0) return;
        const match = matches[currentMatchIndex] ?? matches[0];
        if (!match) return;
        controller.streamer.replaceText(findQuery, replaceValue, {
            range: {
                startCol: match.col,
                startRow: match.row,
                endCol: match.col,
                endRow: match.row,
            },
        });
        window.setTimeout(() => handleFind(findQuery), 0);
    };

    const handleReplaceAll = () => {
        if (!controller.streamer || !findQuery || matches.length <= 0) return;
        controller.streamer.replaceText(findQuery, replaceValue);
        window.setTimeout(() => handleFind(findQuery), 0);
    };

    const handleContextMenuAction = (action: string) => {
        if (bareMode || !contextMenu) return;
        const cellMenu = contextMenu.kind === 'cell' ? contextMenu : null;
        const contextTableMenu = cellMenu
            ? (() => {
                const table = controller.getTableAtCell(cellMenu.col, cellMenu.row);
                return table ? buildTableContextMenuPresentation(table) : null;
            })()
            : null;
        const contextArtifactMenu = cellMenu
            ? (() => {
                const artifact = controller.getDerivedArtifactAtCell(cellMenu.col, cellMenu.row);
                return artifact ? buildDerivedArtifactContextMenuPresentation(artifact) : null;
            })()
            : null;
        const actionPlan = buildSpreadsheetContextMenuActionPlan({
            action,
            contextKind: contextMenu.kind,
            tableMenu: contextTableMenu,
            derivedArtifactMenu: contextArtifactMenu,
        });

        switch (actionPlan.kind) {
            case 'none':
                break;
            case 'paste-special':
                controller.performPasteSpecial(actionPlan.mode);
                break;
            case 'set-derived-chart-type':
                if (cellMenu) controller.setChartArtifactTypeAtCell(cellMenu.col, cellMenu.row, actionPlan.chartType);
                break;
            case 'set-derived-chart-metadata':
                if (cellMenu) controller.setChartArtifactMetadataAtCell(cellMenu.col, cellMenu.row, actionPlan.update);
                break;
            case 'set-table-style':
                if (cellMenu) controller.setTableStyleAtCell(cellMenu.col, cellMenu.row, actionPlan.styleId);
                break;
            case 'cell-metadata': {
                const metadata = actionPlan.needsContextMetadata
                    ? cellMenu
                        ? controller.getCellMetadata(cellMenu.col, cellMenu.row)
                        : getSelectedCellMetadata()
                    : null;
                executeCellMetadataActionPlan(actionPlan.plan, metadata);
                break;
            }
            case 'clipboard':
                if (actionPlan.action === 'cut') {
                    controller.performCut();
                } else if (actionPlan.action === 'copy') {
                    controller.performCopy();
                } else {
                    controller.performPaste();
                }
                break;
            case 'clear-contents':
                controller.clearSelectedCells();
                break;
            case 'open-format-cells':
                setShowFormatCells(true);
                break;
            case 'open-validation-settings':
                openCellMetadataDialog('validation');
                break;
            case 'open-sheet-protection':
                handleOpenSheetProtectionDialog();
                break;
            case 'open-find-dialog':
                openFindDialog(actionPlan.mode);
                break;
            case 'table-action':
                if (!cellMenu) break;
                switch (actionPlan.action) {
                    case 'resize-table':
                        controller.resizeTableAtCellToSelection(cellMenu.col, cellMenu.row);
                        break;
                    case 'toggle-table-header-row':
                        controller.toggleTableHeaderRowAtCell(cellMenu.col, cellMenu.row);
                        break;
                    case 'toggle-table-filter-buttons':
                        controller.toggleTableFilterButtonsAtCell(cellMenu.col, cellMenu.row);
                        break;
                    case 'toggle-table-total-row':
                        controller.toggleTableTotalRowAtCell(cellMenu.col, cellMenu.row);
                        break;
                    case 'delete-table':
                        controller.deleteTableAtCell(cellMenu.col, cellMenu.row);
                        break;
                }
                break;
            case 'rename-table':
                if (cellMenu) {
                    const nextName = window.prompt(actionPlan.promptTitle, actionPlan.promptDefaultName);
                    if (nextName !== null) {
                        controller.renameTableAtCell(cellMenu.col, cellMenu.row, nextName);
                    }
                }
                break;
            case 'derived-artifact-action':
                if (!cellMenu) break;
                switch (actionPlan.action) {
                    case 'delete-derived-artifact':
                        controller.deleteDerivedArtifactAtCell(cellMenu.col, cellMenu.row);
                        break;
                    case 'resize-derived-artifact-to-selection':
                        controller.resizeDerivedArtifactAtCellToSelection(cellMenu.col, cellMenu.row);
                        break;
                    case 'set-derived-artifact-source-to-selection':
                        controller.setDerivedArtifactSourceRangeAtCellToSelection(cellMenu.col, cellMenu.row);
                        break;
                    case 'select-derived-artifact-source-range': {
                        const artifact = controller.getDerivedArtifactAtCell(cellMenu.col, cellMenu.row);
                        if (artifact && controller.selectDerivedArtifactSourceRangeById(artifact.id)) {
                            applyDerivedArtifactSelectionUpdate(buildDerivedArtifactOverlaySelectionUpdate(artifact, 'source'));
                        }
                        break;
                    }
                }
                break;
            case 'rename-derived-artifact':
                if (cellMenu) {
                    const nextTitle = window.prompt(actionPlan.promptTitle, actionPlan.promptDefaultTitle);
                    if (nextTitle !== null) {
                        controller.renameDerivedArtifactAtCell(cellMenu.col, cellMenu.row, nextTitle);
                    }
                }
                break;
        }
        setContextMenu(null);
    };

    const handleDerivedArtifactOverlayAction = (
        artifact: DerivedArtifactOverlayProjection,
        action: string,
        toolbarItem?: DerivedArtifactChartQuickToolbarItemPresentation,
    ) => {
        if (bareMode) return;
        const placementCell = artifact.placementSelectionUpdate.placementCell;
        const sourceArtifact = controller.getDerivedArtifactAtCell(placementCell.col, placementCell.row);
        const actionPlan = buildSpreadsheetContextMenuActionPlan({
            action,
            contextKind: 'cell',
            derivedArtifactMenu: sourceArtifact ? buildDerivedArtifactContextMenuPresentation(sourceArtifact) : null,
        });
        switch (actionPlan.kind) {
            case 'set-derived-chart-type':
                controller.setChartArtifactTypeAtCell(placementCell.col, placementCell.row, actionPlan.chartType);
                break;
            case 'set-derived-chart-metadata':
                controller.setChartArtifactMetadataAtCell(placementCell.col, placementCell.row, actionPlan.update);
                break;
            case 'derived-artifact-action':
                if (actionPlan.action === 'select-derived-artifact-source-range') {
                    const currentArtifact = controller.getDerivedArtifactAtCell(placementCell.col, placementCell.row);
                    if (currentArtifact && controller.selectDerivedArtifactSourceRangeById(currentArtifact.id)) {
                        applyDerivedArtifactSelectionUpdate(buildDerivedArtifactOverlaySelectionUpdate(currentArtifact, 'source'));
                    }
                }
                break;
            default:
                if (action === 'derived-chart-edit-title') {
                    const nextTitle = window.prompt(
                        toolbarItem?.promptTitle ?? 'Chart title',
                        toolbarItem?.promptDefault ?? String(sourceArtifact?.title ?? 'Chart'),
                    );
                    if (nextTitle !== null) controller.renameDerivedArtifactAtCell(placementCell.col, placementCell.row, nextTitle);
                } else if (action === 'derived-chart-edit-source-range') {
                    const nextSource = window.prompt(
                        toolbarItem?.promptTitle ?? 'Chart source range',
                        toolbarItem?.promptDefault ?? (artifact.sourceAddress || 'A1:B2'),
                    );
                    if (nextSource !== null) controller.setDerivedArtifactSourceRangeAtCellFromDraft(placementCell.col, placementCell.row, nextSource);
                } else if (action === 'derived-chart-edit-category-axis-title') {
                    const nextTitle = window.prompt(
                        toolbarItem?.promptTitle ?? 'Category axis title',
                        toolbarItem?.promptDefault ?? String((sourceArtifact?.metadata as any)?.excel_range_chart?.categoryAxisTitle ?? ''),
                    );
                    if (nextTitle !== null) {
                        controller.setChartArtifactMetadataAtCell(placementCell.col, placementCell.row, {
                            key: 'categoryAxisTitle',
                            value: nextTitle,
                        });
                    }
                } else if (action === 'derived-chart-edit-value-axis-title') {
                    const nextTitle = window.prompt(
                        toolbarItem?.promptTitle ?? 'Value axis title',
                        toolbarItem?.promptDefault ?? String((sourceArtifact?.metadata as any)?.excel_range_chart?.valueAxisTitle ?? ''),
                    );
                    if (nextTitle !== null) {
                        controller.setChartArtifactMetadataAtCell(placementCell.col, placementCell.row, {
                            key: 'valueAxisTitle',
                            value: nextTitle,
                        });
                    }
                }
                break;
        }
    };

    const handlePasteOptionAction = (action: string) => {
        if (bareMode || !pasteOptions) return;
        const pasteSpecialCommand = pasteSpecialCommandForAction(action);
        if (!pasteSpecialCommand) return;
        controller.performPasteSpecialAt(pasteOptions.targetCol, pasteOptions.targetRow, pasteSpecialCommand.mode);
        setPasteOptions(null);
        setPasteOptionsMenu(null);
    };

    const handleFillOptionAction = (action: string) => {
        if (bareMode || !fillOptions) return;
        const fillCommand = fillOptionCommandForAction(action);
        if (!fillCommand) return;
        controller.performFillOptionAt(fillOptions, fillCommand.mode);
        setFillOptions(null);
        setFillOptionsMenu(null);
    };

    const applyAutoFilterRangeColumns = useCallback((columns: NonNullable<SpreadsheetAutoFilter['columns']>) => {
        if (!controller.streamer || !activeAutoFilterForMenu) return;
        controller.streamer.setAutoFilter(autoFilterRangeToUiRange(activeAutoFilterForMenu.range), columns);
        controller.requestRender();
    }, [activeAutoFilterForMenu, controller]);

    const handleAutoFilterValueQueryChange = useCallback((query: string) => {
        setAutoFilterMenu((current) => updateAutoFilterMenuValueQuery(current, query));
    }, []);

    const handleAutoFilterToggleValue = useCallback((option: AutoFilterValueOption) => {
        setAutoFilterMenu((current) => toggleAutoFilterMenuDraftOption(current, option));
    }, []);

    const handleAutoFilterSelectAll = useCallback((selected: boolean) => {
        setAutoFilterMenu((current) => setAutoFilterMenuDraftAllOptions(current, selected));
    }, []);

    const handleAutoFilterConditionOperatorChange = useCallback((index: number, operator: SpreadsheetAutoFilterOperator) => {
        setAutoFilterMenu((current) => setAutoFilterMenuConditionOperator(current, index, operator));
    }, []);

    const handleAutoFilterConditionValueChange = useCallback((index: number, value: string) => {
        setAutoFilterMenu((current) => setAutoFilterMenuConditionValue(current, index, value));
    }, []);

    const handleAutoFilterConditionJoinChange = useCallback((conditionJoin: SpreadsheetAutoFilterConditionJoin) => {
        setAutoFilterMenu((current) => setAutoFilterMenuConditionJoin(current, conditionJoin));
    }, []);

    const handleAutoFilterClearConditions = useCallback(() => {
        setAutoFilterMenu((current) => clearAutoFilterMenuConditions(current));
    }, []);

    const executeAutoFilterMenuActionPlan = useCallback((actionPlan: NonNullable<ReturnType<typeof buildAutoFilterMenuActionPlan>>) => {
        switch (actionPlan.kind) {
            case 'sort':
                controller.streamer?.sortRange(actionPlan.range, actionPlan.sortOptions);
                controller.requestRender();
                break;
            case 'set-columns':
                applyAutoFilterRangeColumns(actionPlan.columns);
                break;
            case 'remove':
                controller.streamer?.setAutoFilter(null);
                controller.requestRender();
                break;
        }
    }, [applyAutoFilterRangeColumns, controller]);

    const handleAutoFilterApplyDraft = useCallback(() => {
        const actionPlan = buildAutoFilterApplyDraftPlan(autoFilterMenu, activeAutoFilterForMenu);
        if (!actionPlan) return;
        executeAutoFilterMenuActionPlan(actionPlan);
        setAutoFilterMenu(null);
    }, [activeAutoFilterForMenu, autoFilterMenu, executeAutoFilterMenuActionPlan]);

    const handleAutoFilterMenuAction = useCallback((action: AutoFilterMenuAction) => {
        if (bareMode || !autoFilterMenu || !controller.streamer || !activeAutoFilterForMenu) return;
        const actionPlan = buildAutoFilterMenuActionPlan(action, autoFilterMenu, activeAutoFilterForMenu);
        if (!actionPlan) return;
        executeAutoFilterMenuActionPlan(actionPlan);
        setAutoFilterMenu(null);
    }, [activeAutoFilterForMenu, autoFilterMenu, bareMode, controller.streamer, executeAutoFilterMenuActionPlan]);

    const getSelectedColumnRange = useCallback((selection: { col: number, row: number, endCol: number, endRow: number } | null) => {
        if (!selection) return null;
        const minRow = Math.min(selection.row, selection.endRow);
        const maxRow = Math.max(selection.row, selection.endRow);
        if (minRow !== 0 || maxRow !== Math.max(0, rowCount - 1)) return null;
        return {
            startCol: Math.max(0, Math.min(selection.col, selection.endCol)),
            endCol: Math.max(0, Math.max(selection.col, selection.endCol)),
            anchorCol: selection.col,
        };
    }, [rowCount]);

    const selectColumnRange = useCallback((anchorCol: number, leadCol: number) => (
        controller.selectColumnRange(anchorCol, leadCol)
    ), [controller]);

    const getSelectedRowRange = useCallback((selection: { col: number, row: number, endCol: number, endRow: number } | null) => {
        if (!selection) return null;
        const minCol = Math.min(selection.col, selection.endCol);
        const maxCol = Math.max(selection.col, selection.endCol);
        if (minCol !== 0 || maxCol !== Math.max(0, colCount - 1)) return null;
        return {
            startRow: Math.max(0, Math.min(selection.row, selection.endRow)),
            endRow: Math.max(0, Math.max(selection.row, selection.endRow)),
            anchorRow: selection.row,
        };
    }, [colCount]);

    const selectRowRange = useCallback((anchorRow: number, leadRow: number) => (
        controller.selectRowRange(anchorRow, leadRow)
    ), [controller]);

    const resolveColumnFromHeaderClientX = useCallback((clientX: number) => {
        const headerEl = colHeaderRef.current;
        if (!headerEl) return null;
        const rect = headerEl.getBoundingClientRect();
        const localX = Math.max(0, Math.min(rect.width - 1, clientX - rect.left));
        const hit = controller.hitTest(localX, 1);
        if (!hit) return null;
        return Math.max(0, Math.min(colCount - 1, hit.col));
    }, [colCount, controller]);

    const resolveRowFromHeaderClientY = useCallback((clientY: number) => {
        const headerEl = rowHeaderRef.current;
        if (!headerEl) return null;
        const rect = headerEl.getBoundingClientRect();
        const localY = Math.max(0, Math.min(rect.height - 1, clientY - rect.top));
        const hit = controller.hitTest(1, localY);
        if (!hit) return null;
        return Math.max(0, Math.min(rowCount - 1, hit.row));
    }, [controller, rowCount]);

    const handleColumnHeaderMouseDown = useCallback((e: React.MouseEvent, colIndex: number) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const selectedColumns = getSelectedColumnRange(selectedRange);
        const anchorCol = (e.shiftKey && selectedColumns) ? selectedColumns.anchorCol : colIndex;
        const selected = selectColumnRange(anchorCol, colIndex);
        if (selected) activeColumnHeaderDragRef.current = { anchorCol };
        setContextMenu(null);
    }, [getSelectedColumnRange, selectColumnRange, selectedRange]);

    const handleColumnHeaderContextMenu = useCallback((e: React.MouseEvent, colIndex: number) => {
        e.preventDefault();
        e.stopPropagation();
        const selectedColumns = getSelectedColumnRange(selectedRange);
        const withinSelection = Boolean(
            selectedColumns &&
            colIndex >= selectedColumns.startCol &&
            colIndex <= selectedColumns.endCol,
        );
        const nextStart = withinSelection && selectedColumns ? selectedColumns.startCol : colIndex;
        const nextEnd = withinSelection && selectedColumns ? selectedColumns.endCol : colIndex;
        if (!withinSelection) {
            selectColumnRange(colIndex, colIndex);
        }
        setContextMenu({
            kind: 'column',
            x: e.clientX,
            y: e.clientY,
            startCol: nextStart,
            endCol: nextEnd,
        });
    }, [getSelectedColumnRange, selectColumnRange, selectedRange]);

    const handleRowHeaderMouseDown = useCallback((e: React.MouseEvent, rowIndex: number) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const selectedRows = getSelectedRowRange(selectedRange);
        const anchorRow = (e.shiftKey && selectedRows) ? selectedRows.anchorRow : rowIndex;
        const selected = selectRowRange(anchorRow, rowIndex);
        if (selected) activeRowHeaderDragRef.current = { anchorRow };
        setContextMenu(null);
    }, [getSelectedRowRange, selectRowRange, selectedRange]);

    const handleRowHeaderContextMenu = useCallback((e: React.MouseEvent, rowIndex: number) => {
        e.preventDefault();
        e.stopPropagation();
        const selectedRows = getSelectedRowRange(selectedRange);
        const withinSelection = Boolean(
            selectedRows &&
            rowIndex >= selectedRows.startRow &&
            rowIndex <= selectedRows.endRow,
        );
        const nextStart = withinSelection && selectedRows ? selectedRows.startRow : rowIndex;
        const nextEnd = withinSelection && selectedRows ? selectedRows.endRow : rowIndex;
        if (!withinSelection) {
            selectRowRange(rowIndex, rowIndex);
        }
        setContextMenu({
            kind: 'row',
            x: e.clientX,
            y: e.clientY,
            startRow: nextStart,
            endRow: nextEnd,
        });
    }, [getSelectedRowRange, selectRowRange, selectedRange]);

    useEffect(() => {
        const handleWindowMouseMove = (event: MouseEvent) => {
            const drag = activeColumnHeaderDragRef.current;
            if (!drag) return;
            const colIndex = resolveColumnFromHeaderClientX(event.clientX);
            if (colIndex == null) return;
            selectColumnRange(drag.anchorCol, colIndex);
        };

        const stopDrag = () => {
            activeColumnHeaderDragRef.current = null;
        };

        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', stopDrag);
        window.addEventListener('blur', stopDrag);
        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('blur', stopDrag);
        };
    }, [resolveColumnFromHeaderClientX, selectColumnRange]);

    useEffect(() => {
        const handleWindowMouseMove = (event: MouseEvent) => {
            const drag = activeRowHeaderDragRef.current;
            if (!drag) return;
            const rowIndex = resolveRowFromHeaderClientY(event.clientY);
            if (rowIndex == null) return;
            selectRowRange(drag.anchorRow, rowIndex);
        };

        const stopDrag = () => {
            activeRowHeaderDragRef.current = null;
        };

        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', stopDrag);
        window.addEventListener('blur', stopDrag);
        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('blur', stopDrag);
        };
    }, [resolveRowFromHeaderClientY, selectRowRange]);

    useEffect(() => {
        const previewDerivedArtifactDrag = (event: MouseEvent) => {
            const dragState = activeDerivedArtifactDragRef.current;
            if (!dragState) return null;
            event.preventDefault();
            const target = controller.getCellAtClientPoint(event.clientX, event.clientY);
            if (!target) return null;
            const update = buildDerivedArtifactOverlayDragPreviewUpdate(dragState, target);
            const selected = controller.setSelection(update.selection, { force: true });
            if (selected) {
                setSelectedRange(update.selection);
                setSelectedCell(update.selectedCell);
            }
            return update;
        };

        const handleWindowMouseMove = (event: MouseEvent) => {
            previewDerivedArtifactDrag(event);
        };

        const handleWindowMouseUp = (event: MouseEvent) => {
            const dragState = activeDerivedArtifactDragRef.current;
            if (!dragState) return;
            const update = previewDerivedArtifactDrag(event);
            activeDerivedArtifactDragRef.current = null;
            if (!update) return;
            switch (update.commit.kind) {
                case 'placement-resize':
                    controller.resizeDerivedArtifactAtCellToCell(
                        update.commit.anchorCell.col,
                        update.commit.anchorCell.row,
                        update.commit.targetCell.col,
                        update.commit.targetCell.row,
                    );
                    break;
                case 'placement-move':
                    controller.moveDerivedArtifactAtCellToCell(
                        update.commit.originCell.col,
                        update.commit.originCell.row,
                        update.commit.targetCell.col,
                        update.commit.targetCell.row,
                    );
                    break;
                case 'source-resize':
                    controller.resizeDerivedArtifactSourceRangeByIdToCell(
                        update.commit.artifactId,
                        update.commit.targetCell.col,
                        update.commit.targetCell.row,
                    );
                    break;
                case 'source-move':
                    controller.moveDerivedArtifactSourceRangeByIdToCell(
                        update.commit.artifactId,
                        update.commit.targetCell.col,
                        update.commit.targetCell.row,
                    );
                    break;
            }
        };

        const stopDrag = () => {
            activeDerivedArtifactDragRef.current = null;
        };

        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', handleWindowMouseUp);
        window.addEventListener('blur', stopDrag);
        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', handleWindowMouseUp);
            window.removeEventListener('blur', stopDrag);
        };
    }, [controller]);

    const handleCommitEdit = (value: string, navigation: FormulaCommitNavigation | null = 'down') => {
        if (bareMode) return;
        if (formulaEditSession && !canCommitFormulaSessionOnSheet(formulaEditSession, formulaEditSession.activeSheetName ?? sheetName)) return;
        if (editingCell) {
            controller.setCellValue(editingCell.col, editingCell.row, formulaEditSession?.draft ?? value);
            setEditingCell(null);
            setCurrEditValue(null);
            setFormulaEditSession(null);
            formulaEditSessionRef.current = null;
            setFormulaEditSurface(null);
            clearSharedFormulaEditSession();
            controller.setIsEditing(false);

            if (navigation && navigation !== 'none') {
                const nextCol = Math.min(
                    Math.max(0, navigation === 'right' ? editingCell.col + 1 : navigation === 'left' ? editingCell.col - 1 : editingCell.col),
                    Math.max(0, colCount - 1),
                );
                const nextRow = Math.min(
                    Math.max(0, navigation === 'down' ? editingCell.row + 1 : navigation === 'up' ? editingCell.row - 1 : editingCell.row),
                    Math.max(0, rowCount - 1),
                );
                const selected = controller.setSelection({
                    col: nextCol,
                    row: nextRow,
                    endCol: nextCol,
                    endRow: nextRow
                });
                if (selected) {
                    controller.notifySelectionChange();
                    controller.scrollToCell(nextCol, nextRow);
                    controller.requestRender();
                }
            }
        }
    };

    const handleCancelEdit = () => {
        if (bareMode) return;
        setEditingCell(null);
        setCurrEditValue(null);
        setFormulaEditSession(null);
        formulaEditSessionRef.current = null;
        setFormulaEditSurface(null);
        clearSharedFormulaEditSession();
        controller.setIsEditing(false);
    };

    const handleFormulaBarAccept = (navigation: FormulaCommitNavigation | null = 'none') => {
        if (bareMode) return;
        const activeSession = formulaEditSessionRef.current;
        if (activeSession && !canCommitFormulaSessionOnSheet(activeSession, activeSession.activeSheetName ?? sheetName)) return;
        const target = editingCell
            ? { col: editingCell.col, row: editingCell.row }
            : controller.selection
                ? { col: controller.selection.col, row: controller.selection.row }
                : null;
        if (!target) return;
        const value = activeSession?.draft ?? currEditValue ?? '';
        controller.setCellValue(target.col, target.row, value);
        setEditingCell(null);
        setCurrEditValue(null);
        setFormulaEditSession(null);
        formulaEditSessionRef.current = null;
        setFormulaEditSurface(null);
        clearSharedFormulaEditSession();
        controller.setIsEditing(false);

        if (navigation && navigation !== 'none') {
            const nextCol = Math.min(
                Math.max(0, navigation === 'right' ? target.col + 1 : navigation === 'left' ? target.col - 1 : target.col),
                Math.max(0, colCount - 1),
            );
            const nextRow = Math.min(
                Math.max(0, navigation === 'down' ? target.row + 1 : navigation === 'up' ? target.row - 1 : target.row),
                Math.max(0, rowCount - 1),
            );
            const selected = controller.setSelection({
                col: nextCol,
                row: nextRow,
                endCol: nextCol,
                endRow: nextRow,
            });
            if (selected) {
                controller.notifySelectionChange();
                controller.scrollToCell(nextCol, nextRow);
            }
        }
        controller.requestRender();
    };

    const handleNameBoxNavigate = (selection: { col: number, row: number, endCol: number, endRow: number }) => {
        controller.setSelection(selection);
        controller.notifySelectionChange();
        controller.scrollToCell(
            Math.min(selection.col, selection.endCol),
            Math.min(selection.row, selection.endRow),
        );
        controller.requestRender();
    };

    const showProtectedSheetResizeNotice = useCallback((
        e: React.MouseEvent | null,
        message: string,
        operationLabel: string,
    ) => {
        setInvalidEntryNotice(buildProtectedSheetActionNotice({
            message,
            operationLabel,
            sheetName: activeDefinedNameSheetName,
            pointer: e ? { x: e.clientX, y: e.clientY } : null,
            viewport: controller.getViewport(),
        }));
    }, [activeDefinedNameSheetName, controller]);

    const startResizeCol = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (columnResizeBlockedByProtection) {
            showProtectedSheetResizeNotice(e, columnResizeProtection.reason, 'Resize column');
            return;
        }
        setResizing({
            type: 'col',
            index,
            startPos: e.clientX,
            startSize: controller.getColumnWidth(index)
        });
        document.body.style.cursor = 'col-resize';
    };

    const startResizeRow = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (rowResizeBlockedByProtection) {
            showProtectedSheetResizeNotice(e, rowResizeProtection.reason, 'Resize row');
            return;
        }
        setResizing({
            type: 'row',
            index,
            startPos: e.clientY,
            startSize: controller.getRowHeight(index)
        });
        document.body.style.cursor = 'row-resize';
    };

    const autoSizeColumnIndex = useCallback((index: number) => {
        if (columnResizeBlockedByProtection) {
            showProtectedSheetResizeNotice(null, columnResizeProtection.reason, 'AutoFit column width');
            return;
        }
        const handled = onAutoSizeColumn?.(index) === true;
        if (!handled) controller.autoSizeColumn(index);
        onColumnResizeCommit?.(index, Math.max(20, controller.getColumnLogicalWidth(index)));
        setTick(t => t + 1);
        onGeometryChange?.();
        updateVisibleBounds();
    }, [columnResizeBlockedByProtection, columnResizeProtection.reason, controller, onAutoSizeColumn, onColumnResizeCommit, onGeometryChange, showProtectedSheetResizeNotice, updateVisibleBounds]);

    const autoSizeRowIndex = useCallback((index: number) => {
        if (rowResizeBlockedByProtection) {
            showProtectedSheetResizeNotice(null, rowResizeProtection.reason, 'AutoFit row height');
            return;
        }
        const handled = onAutoSizeRow?.(index) === true;
        if (!handled) controller.autoSizeRow(index);
        onRowResizeCommit?.(index, Math.max(20, controller.getRowLogicalHeight(index)));
        setTick(t => t + 1);
        onGeometryChange?.();
        updateVisibleBounds();
    }, [controller, onAutoSizeRow, onGeometryChange, onRowResizeCommit, rowResizeBlockedByProtection, rowResizeProtection.reason, showProtectedSheetResizeNotice, updateVisibleBounds]);

    const applyColumnLogicalWidth = useCallback((startCol: number, endCol: number, width: number) => {
        if (columnResizeBlockedByProtection) {
            showProtectedSheetResizeNotice(null, columnResizeProtection.reason, 'Set column width');
            return;
        }
        const nextWidth = Math.max(20, Math.round(width));
        for (let col = Math.max(0, startCol); col <= Math.min(colCount - 1, endCol); col++) {
            controller.setColumnWidth(col, nextWidth);
            onColumnResizeCommit?.(col, Math.max(20, controller.getColumnLogicalWidth(col)));
        }
        setTick(t => t + 1);
        onGeometryChange?.();
        updateVisibleBounds();
    }, [colCount, columnResizeBlockedByProtection, columnResizeProtection.reason, controller, onColumnResizeCommit, onGeometryChange, showProtectedSheetResizeNotice, updateVisibleBounds]);

    const applyRowLogicalHeight = useCallback((startRow: number, endRow: number, height: number) => {
        if (rowResizeBlockedByProtection) {
            showProtectedSheetResizeNotice(null, rowResizeProtection.reason, 'Set row height');
            return;
        }
        const nextHeight = Math.max(20, Math.round(height));
        for (let row = Math.max(0, startRow); row <= Math.min(rowCount - 1, endRow); row++) {
            controller.setRowHeight(row, nextHeight);
            onRowResizeCommit?.(row, Math.max(20, controller.getRowLogicalHeight(row)));
        }
        setTick(t => t + 1);
        onGeometryChange?.();
        updateVisibleBounds();
    }, [controller, onGeometryChange, onRowResizeCommit, rowCount, rowResizeBlockedByProtection, rowResizeProtection.reason, showProtectedSheetResizeNotice, updateVisibleBounds]);

    const handleAutoResizeCol = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        autoSizeColumnIndex(index);
    };

    const handleAutoResizeRow = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        autoSizeRowIndex(index);
    };

    const getContextCommandStateForId = (id: SpreadsheetCommandId) => {
        const currentFormat = selectedCell ? controller.getAuthoringCellFormat(selectedCell.col, selectedCell.row) : undefined;
        const selectionKind = contextMenu?.kind === 'column'
            ? 'column'
            : contextMenu?.kind === 'row'
                ? 'row'
                : 'cell';
        return getCommandState(getSpreadsheetCommand(id), {
            hasSelection: Boolean(contextMenu || selectedRange || controller.selection),
            currentFormat,
            streamerAttached: Boolean(controller.streamer),
            localPreviewAttached: controller.hasLocalQueryPreview(),
            selectionKind,
            hasStructuralActions: contextMenu?.kind === 'column'
                ? typeof onColumnAction === 'function'
                : contextMenu?.kind === 'row'
                    ? typeof onRowAction === 'function'
                    : false,
            hasTableSelection: contextMenu?.kind === 'cell'
                ? Boolean(controller.getTableAtCell(contextMenu.col, contextMenu.row))
                : Boolean(selectedCell && controller.getTableAtCell(selectedCell.col, selectedCell.row)),
            sheetProtection: activeSheetProtected ? activeSheetProtection : null,
        });
    };

    const getDisabledPasteSpecialCommandIds = (): SpreadsheetCommandId[] => PASTE_SPECIAL_COMMANDS
        .filter(({ id }) => !getContextCommandStateForId(id).enabled)
        .map(({ id }) => id);

    const isContextMenuSeparatorDescriptor = (
        descriptor: SpreadsheetContextMenuDescriptor,
    ): descriptor is Extract<SpreadsheetContextMenuDescriptor, { type: 'separator' }> => descriptor.type === 'separator';

    const descriptorToContextMenuItem = (
        descriptor: SpreadsheetContextMenuDescriptor,
        options: {
            columnMenu?: Extract<SpreadsheetContextMenuState, { kind: 'column' }>
            rowMenu?: Extract<SpreadsheetContextMenuState, { kind: 'row' }>
        } = {},
    ): ContextMenuItem => {
        if (isContextMenuSeparatorDescriptor(descriptor)) {
            return { type: 'separator' };
        }
        const command = descriptor.commandId ? getSpreadsheetCommand(descriptor.commandId) : null;
        const state: Pick<SpreadsheetCommandState, 'enabled' | 'disabledReason'> = descriptor.commandId
            ? getContextCommandStateForId(descriptor.commandId)
            : { enabled: true, disabledReason: '' };
        const baseItem: Exclude<ContextMenuItem, { type: 'separator' }> = {
            label: descriptor.label ?? command?.label ?? '',
            title: descriptor.title ?? (command
                ? state.disabledReason
                    ? `${command.description} ${state.disabledReason}.`
                    : command.description
                : undefined),
            shortcut: descriptor.shortcut ?? command?.shortcut,
            checked: descriptor.checked,
            disabled: descriptor.disabled ?? !state.enabled,
            submenu: descriptor.submenu?.map((item) => descriptorToContextMenuItem(item, options)),
        };
        const action = descriptor.action;
        if (!action) return baseItem;
        const columnMenu = options.columnMenu;
        if (columnMenu && ['insert_left', 'insert_right', 'delete', 'clear'].includes(action)) {
            const selection = { startCol: columnMenu.startCol, endCol: columnMenu.endCol };
            return {
                ...baseItem,
                action: () => {
                    void Promise.resolve(onColumnAction?.(action as 'insert_left' | 'insert_right' | 'delete' | 'clear', selection));
                    setContextMenu(null);
                },
            };
        }
        if (columnMenu && action === 'column-width') {
            return {
                ...baseItem,
                action: () => {
                    const currentWidth = Math.round(controller.getColumnLogicalWidth(columnMenu.startCol));
                    const value = window.prompt('Column width', String(currentWidth));
                    if (value == null) return;
                    const width = Number(value);
                    if (!Number.isFinite(width) || width <= 0) return;
                    applyColumnLogicalWidth(columnMenu.startCol, columnMenu.endCol, width);
                    setContextMenu(null);
                },
            };
        }
        if (columnMenu && action === 'autofit-column-width') {
            return {
                ...baseItem,
                action: () => {
                    for (let col = columnMenu.startCol; col <= columnMenu.endCol; col++) autoSizeColumnIndex(col);
                    setContextMenu(null);
                },
            };
        }
        const rowMenu = options.rowMenu;
        if (rowMenu && ['insert_above', 'insert_below', 'delete', 'clear'].includes(action)) {
            const selection = { startRow: rowMenu.startRow, endRow: rowMenu.endRow };
            return {
                ...baseItem,
                action: () => {
                    void Promise.resolve(onRowAction?.(action as 'insert_above' | 'insert_below' | 'delete' | 'clear', selection));
                    setContextMenu(null);
                },
            };
        }
        if (rowMenu && action === 'row-height') {
            return {
                ...baseItem,
                action: () => {
                    const currentHeight = Math.round(controller.getRowLogicalHeight(rowMenu.startRow));
                    const value = window.prompt('Row height', String(currentHeight));
                    if (value == null) return;
                    const height = Number(value);
                    if (!Number.isFinite(height) || height <= 0) return;
                    applyRowLogicalHeight(rowMenu.startRow, rowMenu.endRow, height);
                    setContextMenu(null);
                },
            };
        }
        if (rowMenu && action === 'autofit-row-height') {
            return {
                ...baseItem,
                action: () => {
                    for (let row = rowMenu.startRow; row <= rowMenu.endRow; row++) autoSizeRowIndex(row);
                    setContextMenu(null);
                },
            };
        }
        return {
            ...baseItem,
            action: () => handleContextMenuAction(action),
        };
    };

    const buildClipboardContextMenuItems = (): ContextMenuItem[] => buildSpreadsheetClipboardContextMenuDescriptors({
        hasEngineClipboard: controller.hasEngineClipboard(),
        disabledPasteSpecialCommandIds: getDisabledPasteSpecialCommandIds(),
    }).map((descriptor) => descriptorToContextMenuItem(descriptor));

    const buildCellContextMenuItems = (): ContextMenuItem[] => {
        const contextMetadata = contextMenu?.kind === 'cell'
            ? controller.getCellMetadata(contextMenu.col, contextMenu.row)
            : getSelectedCellMetadata();
        const contextTable = contextMenu?.kind === 'cell'
            ? controller.getTableAtCell(contextMenu.col, contextMenu.row)
            : (selectedCell ? controller.getTableAtCell(selectedCell.col, selectedCell.row) : null);
        const contextTableMenu = contextTable ? buildTableContextMenuPresentation(contextTable) : null;
        const contextArtifact = contextMenu?.kind === 'cell'
            ? controller.getDerivedArtifactAtCell(contextMenu.col, contextMenu.row)
            : (selectedCell ? controller.getDerivedArtifactAtCell(selectedCell.col, selectedCell.row) : null);
        const contextArtifactMenu = contextArtifact ? buildDerivedArtifactContextMenuPresentation(contextArtifact) : null;
        const contextSparklineCellRange = contextMenu?.kind === 'cell'
            ? {
                sheet_id: activeEngineSheetId,
                start_row: contextMenu.row,
                start_col: contextMenu.col,
                end_row: contextMenu.row,
                end_col: contextMenu.col,
            }
            : null;
        const selectedSparklineGroup = findSparklineGroupOverlappingRange(sparklineGroups, selectedEngineRange);
        const contextSparklineGroup = selectedSparklineGroup
            ?? (contextSparklineCellRange ? findSparklineGroupOverlappingRange(sparklineGroups, contextSparklineCellRange) : null);
        const items = buildSpreadsheetCellContextMenuDescriptors({
            hasEngineClipboard: controller.hasEngineClipboard(),
            disabledPasteSpecialCommandIds: getDisabledPasteSpecialCommandIds(),
            metadata: contextMetadata,
            tableMenu: contextTableMenu,
            derivedArtifactMenu: contextArtifactMenu,
        }).map((descriptor) => descriptorToContextMenuItem(descriptor));
        if (!contextSparklineGroup) return items;
        const formatIndex = items.findIndex((item) => !('type' in item) && item.label === 'Format');
        items.splice(
            formatIndex >= 0 ? formatIndex + 1 : 0,
            0,
            {
                label: 'Edit Sparklines...',
                title: 'Edit sparkline type, data range, axes, colors, and markers.',
                action: () => {
                    setSparklineDialogAutoEdit(true);
                    setSparklineDialogType(contextSparklineGroup.type);
                    setContextMenu(null);
                },
            },
        );
        return items;
    };

    const buildColumnContextMenuItems = (menu: Extract<SpreadsheetContextMenuState, { kind: 'column' }>): ContextMenuItem[] => {
        const descriptors = buildSpreadsheetRowColumnContextMenuDescriptors({
            kind: 'column',
            count: Math.max(1, menu.endCol - menu.startCol + 1),
            totalCount: colCount,
            hasStructuralActions: typeof onColumnAction === 'function',
            insertState: getContextCommandStateForId('insert-cells'),
            deleteState: getContextCommandStateForId('delete-cells'),
            formatState: getContextCommandStateForId('format-cells'),
            resizeBlockedByProtection: columnResizeBlockedByProtection,
            resizeProtectionReason: columnResizeProtection.reason,
            hasEngineClipboard: controller.hasEngineClipboard(),
            disabledPasteSpecialCommandIds: getDisabledPasteSpecialCommandIds(),
        });
        return descriptors.map((descriptor) => descriptorToContextMenuItem(descriptor, { columnMenu: menu }));
    };

    const buildRowContextMenuItems = (menu: Extract<SpreadsheetContextMenuState, { kind: 'row' }>): ContextMenuItem[] => {
        const descriptors = buildSpreadsheetRowColumnContextMenuDescriptors({
            kind: 'row',
            count: Math.max(1, menu.endRow - menu.startRow + 1),
            totalCount: rowCount,
            hasStructuralActions: typeof onRowAction === 'function',
            insertState: getContextCommandStateForId('insert-cells'),
            deleteState: getContextCommandStateForId('delete-cells'),
            formatState: getContextCommandStateForId('format-cells'),
            resizeBlockedByProtection: rowResizeBlockedByProtection,
            resizeProtectionReason: rowResizeProtection.reason,
            hasEngineClipboard: controller.hasEngineClipboard(),
            disabledPasteSpecialCommandIds: getDisabledPasteSpecialCommandIds(),
        });
        return descriptors.map((descriptor) => descriptorToContextMenuItem(descriptor, { rowMenu: menu }));
    };

    const contextMenuItems: ContextMenuItem[] = contextMenu
        ? contextMenu.kind === 'cell'
            ? buildCellContextMenuItems()
            : contextMenu.kind === 'column'
                ? buildColumnContextMenuItems(contextMenu)
                : buildRowContextMenuItems(contextMenu)
        : [];

    const pasteOptionsMenuItems: ContextMenuItem[] = pasteOptions
        ? buildPasteOptionsMenuEntries({ canPasteSpecial: pasteOptions.canPasteSpecial }).map(({ id, action, disabled }) => {
            const command = getSpreadsheetCommand(id);
            return {
                label: command.label,
                action: () => handlePasteOptionAction(action),
                title: command.description,
                disabled,
            };
        })
        : [];

    const fillOptionsMenuItems: ContextMenuItem[] = fillOptions
        ? buildFillOptionsMenuEntries({ canFillSeries: fillOptions.canFillSeries }).map(({ id, action, disabled }) => {
            const command = getSpreadsheetCommand(id);
            return {
                label: command.label,
                action: () => handleFillOptionAction(action),
                title: command.description,
                disabled,
            };
        })
        : [];

    const autoFilterMenuPresentation = useMemo(() => (
        buildAutoFilterMenuPresentation(autoFilterMenu, activeAutoFilterForMenu)
    ), [activeAutoFilterForMenu, autoFilterMenu]);
    const autoFilterColumnLabel = autoFilterMenuPresentation?.columnLabel ?? '';
    const autoFilterFilteredValueOptions = autoFilterMenuPresentation?.filteredValueOptions ?? [];
    const autoFilterSelectedValueKeys = autoFilterMenuPresentation?.selectedValueKeys ?? new Set<string>();
    const autoFilterConditionRows = autoFilterMenuPresentation?.conditionRows ?? [];
    const autoFilterHasColumnFilter = autoFilterMenuPresentation?.hasColumnFilter ?? false;
    const autoFilterHasAnyFilter = autoFilterMenuPresentation?.hasAnyFilter ?? false;
    const autoFilterAllSelected = autoFilterMenuPresentation?.allSelected ?? false;
    const autoFilterCanApply = autoFilterMenuPresentation?.canApply ?? false;
    const autoFilterHasOperatorCriteria = autoFilterMenuPresentation?.hasOperatorCriteria ?? false;

    const selectedValidationDropdown = (() => {
        const selection = selectedRange ?? controller.selection;
        const col = selection ? Math.min(selection.col, selection.endCol) : 0;
        const row = selection ? Math.min(selection.row, selection.endRow) : 0;
        const rect = selection
            ? controller.getRangeScreenRect({
                start: { col, row },
                end: { col, row },
            })
            : null;
        return buildValidationDropdownProjection({
            disabled: bareMode,
            rule: selectedValidationRule,
            projectedOptions: validationDropdownOptions,
            activeSheetId: activeEngineSheetId,
            selection,
            cellRect: rect,
            viewport: controller.getViewport(),
            currentValue: selection ? controller.getCellValue(col, row) : '',
        });
    })();
    const selectedValidationDropdownButton = buildValidationDropdownButtonPresentation(selectedValidationDropdown);

    const selectedValidationPrompt = (() => {
        const selection = selectedRange ?? controller.selection;
        const col = selection ? Math.min(selection.col, selection.endCol) : 0;
        const row = selection ? Math.min(selection.row, selection.endRow) : 0;
        const rect = selection
            ? controller.getRangeScreenRect({
                start: { col, row },
                end: { col, row },
            })
            : null;
        return buildValidationPromptProjection({
            disabled: bareMode || Boolean(editingCell) || Boolean(invalidEntryNotice) || !selectedValidationRule,
            prompt: selectedValidationInputPrompt,
            selection,
            cellRect: rect,
            viewport: controller.getViewport(),
        });
    })();

    const visibleCommentEntries = useMemo<VisibleCommentEntry[]>(() => {
        return buildVisibleCommentEntries({
            disabled: bareMode,
            visibleBounds,
            selection: selectedRange ?? controller.selection,
            getCellMetadata: (col, row) => controller.getCellMetadata(col, row),
        });
    }, [
        bareMode,
        controller,
        selectedRange,
        selectedCellMetadata?.comment,
        visibleBounds.endCol,
        visibleBounds.endRow,
        visibleBounds.startCol,
        visibleBounds.startRow,
    ]);

    const selectedCommentEntry = useMemo(() => {
        return buildSelectedCommentEntry({
            selection: selectedRange ?? controller.selection,
            metadata: selectedCellMetadata,
        });
    }, [controller.selection, selectedCellMetadata?.comment, selectedRange]);

    const commentsPanePresentation = useMemo(() => buildCommentsPanePresentation({
        selectedRangeLabel,
        selectedCommentEntry,
        visibleCommentEntries,
    }), [selectedCommentEntry, selectedRangeLabel, visibleCommentEntries]);

    const selectCommentEntry = useCallback((entry: VisibleCommentEntry) => {
        const accepted = controller.setSelection({
            col: entry.col,
            row: entry.row,
            endCol: entry.col,
            endRow: entry.row,
        });
        if (!accepted) return;
        controller.scrollToCell(entry.col, entry.row);
        controller.notifySelectionChange();
        controller.requestRender();
        setTick(t => t + 1);
        updateVisibleBounds();
    }, [controller, updateVisibleBounds]);

    const handleCellMetadataAction = useCallback((actionId: CellMetadataActionId) => {
        const plan = buildCellMetadataActionPlan(actionId);
        if (plan) {
            executeCellMetadataActionPlan(plan);
        }
    }, [executeCellMetadataActionPlan]);

    const selectedMetadataCard = (() => {
        const selection = selectedRange ?? controller.selection;
        if (!selection) return null;
        const col = Math.min(selection.col, selection.endCol);
        const row = Math.min(selection.row, selection.endRow);
        const rect = controller.getRangeScreenRect({
            start: { col, row },
            end: { col, row },
        });
        return buildSelectedCellMetadataCardPresentation({
            disabled: Boolean(
                bareMode ||
                editingCell ||
                invalidEntryNotice ||
                selectedValidationPrompt ||
                cellMetadataMode ||
                showCommentsPane
            ),
            selection,
            metadata: selectedCellMetadata,
            protectionStatus: selectedCellProtectionStatus,
            cellRect: rect,
            viewport: controller.getViewport(),
        });
    })();

    useEffect(() => {
        const hasDropdown = Boolean(selectedValidationDropdown);
        controller.setValidationDropdownAvailable(hasDropdown);
        controller.setValidationDropdownCallback(hasDropdown && selectedValidationDropdown
            ? () => {
                const hostRect = scrollContainerRef.current?.getBoundingClientRect();
                setValidationDropdownMenu({
                    x: (hostRect?.left ?? 0) + selectedValidationDropdown.x,
                    y: (hostRect?.top ?? 0) + selectedValidationDropdown.y + 24,
                });
            }
            : null);
        return () => {
            controller.setValidationDropdownAvailable(false);
            controller.setValidationDropdownCallback(null);
        };
    }, [controller, selectedValidationDropdown]);

    const toggleFullscreen = useCallback(async () => {
        const active = isFullscreen || isImmersive;

        // Exit "fullscreen" (real or immersive).
        if (active) {
            setIsImmersive(false);

            if (isFullscreen) {
                const exit =
                    document.exitFullscreen ||
                    (document as any).webkitExitFullscreen ||
                    (document as any).webkitCancelFullScreen ||
                    (document as any).msExitFullscreen ||
                    (document as any).mozCancelFullScreen;
                if (exit) {
                    try {
                        await exit.call(document);
                    } catch (err) {
                        console.error('[AppHTMLHeaders] Fullscreen exit failed', err);
                    }
                }
            }
            return;
        }

        // Try browser fullscreen first (not supported on iOS Safari for normal pages).
        const docEl: any = document.documentElement;
        const request =
            docEl.requestFullscreen ||
            docEl.webkitRequestFullscreen ||
            docEl.webkitRequestFullScreen ||
            docEl.msRequestFullscreen ||
            docEl.mozRequestFullScreen ||
            docEl.mozRequestFullscreen;

        if (request) {
            try {
                await request.call(docEl);
                setIsImmersive(true);
                return;
            } catch (err) {
                console.warn('[AppHTMLHeaders] Browser fullscreen failed; falling back to immersive mode', err);
            }
        }

        // Fallback: "immersive mode" (hide app chrome) — works everywhere, including iOS Safari.
        setIsImmersive(true);
    }, [isFullscreen, isImmersive]);

    const renderColHeaders = () => {
        const headers = [];
        const startOffset = controller.getColumnOffset(visibleBounds.startCol);
        const headerScrollX = Math.max(0, Number(controller.getScroll().x) || 0);
        let currentX = 0;
        const selectedColumns = getSelectedColumnRange(selectedRange);

        for (let i = visibleBounds.startCol; i <= visibleBounds.endCol; i++) {
            const width = getColWidth(i) || HEADER_WIDTH;
            const isSelected = Boolean(selectedColumns && i >= selectedColumns.startCol && i <= selectedColumns.endCol);
            headers.push(
                <div key={i} style={{
                    position: 'absolute',
                    left: currentX,
                    width,
                    height: HEADER_HEIGHT,
                    borderRight: '1px solid #c0c0c0',
                    borderBottom: '1px solid #c0c0c0',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: `${HEADER_HEIGHT}px`,
                    backgroundColor: isSelected ? 'var(--fg-selection-bg-strong)' : '#f8f9fa',
                    fontSize: '12px',
                    userSelect: 'none',
                    color: '#3a3228',
                    borderTop: isSelected ? '2px solid var(--fg-selection-border-strong)' : '2px solid transparent',
                    fontWeight: isSelected ? 600 : 500,
                }}
                    data-testid={`fg-column-header-${i}`}
                    onMouseDown={(e) => handleColumnHeaderMouseDown(e, i)}
                    onContextMenu={(e) => handleColumnHeaderContextMenu(e, i)}
                >
                    {colToLetter(i)}
                    {!bareMode && (
                        <div
                            data-testid={`fg-column-resize-handle-${i}`}
                            title={columnResizeProtection.title}
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: 5,
                                cursor: columnResizeProtection.cursor,
                                zIndex: 10,
                                pointerEvents: 'auto'
                            }}
                            onMouseDown={(e) => startResizeCol(e, i)}
                            onDoubleClick={(e) => handleAutoResizeCol(e, i)}
                        />
                    )}
                </div>
            );
            currentX += width;
        }
        const visibleWidth = currentX;
        let totalWidth = 0;
        try {
            totalWidth = controller.getColumnOffset(controller.getColCount());
        } catch {
            totalWidth = (controller.getColCount() || colCount) * 100; // Default width estimate
        }

        // Return spacer directly
        return (
            <div
                ref={colHeaderContentRef}
                style={{
                    width: Math.max(totalWidth, startOffset + visibleWidth),
                    height: '100%',
                    position: 'relative',
                    transform: `translate3d(${-headerScrollX}px, 0, 0)`,
                    willChange: 'transform',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        left: startOffset,
                        top: 0,
                        width: visibleWidth,
                        height: HEADER_HEIGHT,
                    }}
                >
                    {headers}
                </div>
            </div>
        );
    };

    const renderRowHeaders = () => {
        const headers = [];
        // #region agent log
        // console.log('[AppHTMLHeaders] renderRowHeaders called', { startRow: visibleBounds.startRow, endRow: visibleBounds.endRow, rowCount: controller.getRowCount() });
        // #endregion
        let startOffset = 0;
        try {
            const worldStart = controller.getRowOffset(visibleBounds.startRow);
            startOffset = windowedScroll ? Math.max(0, worldStart - scrollBaseYRef.current) : worldStart;
        } catch (e) {
            // #region agent log
            console.error('[AppHTMLHeaders] getRowOffset failed', { startRow: visibleBounds.startRow, error: String(e) });
            // #endregion
        }
        let currentY = 0;
        const selectedRows = getSelectedRowRange(selectedRange);

        // Lightweight debug to understand header virtualization state on large jumps
        const now = performance.now();
        if (now - lastHeaderLogRef.current > 500 && controller.getRowCount() > 100000) {
            const sc = scrollContainerRef.current;
            console.log('[headers]', {
                startRow: visibleBounds.startRow,
                endRow: visibleBounds.endRow,
                rowCount: controller.getRowCount(),
                scrollTop: sc?.scrollTop ?? 0,
                scrollHeight: sc?.scrollHeight ?? 0,
                containerH: sc?.clientHeight ?? 0,
                baseY: scrollBaseYRef.current,
                hasRowCache: (controller as any).rowOffsetsCache ? true : false,
            });
            lastHeaderLogRef.current = now;
        }

        for (let i = visibleBounds.startRow; i <= visibleBounds.endRow; i++) {
            const height = getRowHeight(i);
            const isSelected = Boolean(selectedRows && i >= selectedRows.startRow && i <= selectedRows.endRow);
            // #region agent log
            if (i > 75 && height === undefined) {
                console.warn('[AppHTMLHeaders] getRowHeight undefined for row > 75', { row: i, height });
            }
            // #endregion
            headers.push(
                <div key={i} style={{
                    position: 'absolute',
                    top: currentY,
                    width: HEADER_WIDTH,
                    height: height,
                    borderBottom: '1px solid #c0c0c0',
                    borderRight: '1px solid #c0c0c0',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isSelected ? 'var(--fg-selection-bg-strong)' : '#f8f9fa',
                    fontSize: '12px',
                    userSelect: 'none',
                    color: '#3a3228',
                    borderLeft: isSelected ? '2px solid var(--fg-selection-border-strong)' : '2px solid transparent',
                    fontWeight: isSelected ? 600 : 500,
                }}
                    data-testid={`fg-row-header-${i}`}
                    onMouseDown={(e) => handleRowHeaderMouseDown(e, i)}
                    onContextMenu={(e) => handleRowHeaderContextMenu(e, i)}
                >
                    {i + 1}
                    {!bareMode && (
                        <div
                            data-testid={`fg-row-resize-handle-${i}`}
                            title={rowResizeProtection.title}
                            style={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: 5,
                                cursor: rowResizeProtection.cursor,
                                zIndex: 10,
                                pointerEvents: 'auto'
                            }}
                            onMouseDown={(e) => startResizeRow(e, i)}
                            onDoubleClick={(e) => handleAutoResizeRow(e, i)}
                        />
                    )}
                </div>
            );
            currentY += height;
        }
        const visibleHeight = currentY;
        const totalHeight = totalHeightDom;
        const scroll = controller.getScroll();
        const headerScrollY = windowedScroll
            ? Math.max(0, Number(scroll.y || 0) - Number(scrollBaseYRef.current || 0))
            : Math.max(0, Number(scroll.y || 0));

        // Return spacer directly so parent with REF can scroll
        return (
            <div
                ref={rowHeaderContentRef}
                style={{
                    height: Math.max(totalHeight, startOffset + visibleHeight),
                    width: '100%',
                    position: 'relative',
                    transform: `translate3d(0, ${-headerScrollY}px, 0)`,
                    willChange: 'transform',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: startOffset,
                        left: 0,
                        width: HEADER_WIDTH,
                        height: visibleHeight,
                    }}
                >
                    {headers}
                </div>
            </div>
        );
    };

    const encodeImportedRowsAsTsv = (rows: string[][]): string => rows
        .map(row => row
            .map(value => {
                const text = String(value ?? '');
                return /["\t\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
            })
            .join('\t'))
        .join('\n');

    const handleImport = (data: string[][]) => {
        const tsv = encodeImportedRowsAsTsv(data);
        if (controller.streamer && selectedCell) {
            // Import at selected cell
            const { col, row } = selectedCell;
            controller.streamer.pasteData(col, row, tsv);
            controller.requestRender();
        } else if (controller.streamer) {
            // Default to A1
            controller.streamer.pasteData(0, 0, tsv);
            controller.requestRender();
        }
    };

    const fullscreenActive = isFullscreen || isImmersive;
    const showWorkbookChrome = !bareMode && !isImmersive && (showToolbar || showFormulaBar);
    const showFloatingFullscreenButton = showFullscreenButton && (!showWorkbookChrome || fullscreenActive);
    const toolbarRowHeight = showWorkbookChrome && showToolbar ? LIVE_COMPACT_RIBBON_HEIGHT : 0;
    const formulaRowHeight = showWorkbookChrome && showFormulaBar
        ? (formulaBarExpanded ? LIVE_EXPANDED_FORMULA_HEIGHT : LIVE_COMPACT_FORMULA_HEIGHT)
        : 0;
    const statusRowHeight = showWorkbookChrome ? 28 : 0;
    const effectiveHeight = embedded ? (embeddedSize.height || viewportHeight) : viewportHeight;
    const showAddRowsFooter = !bareMode && visibleBounds.endRow >= (controller.getRowCount() - 50);
    const formulaReferenceOverlays = useMemo(() => {
        if (!formulaEditSession || formulaEditSession.mode === 'value') return [];
        return formulaEditSession.referenceSpans
            .filter((span) => isFormulaReferenceVisibleOnSheet(formulaEditSession, span, sheetName))
            .map((span) => {
                const rect = span.range ? controller.getRangeScreenRect(span.range) : null;
                if (!rect) return null;
                const viewport = controller.getViewport();
                const left = Math.max(0, rect.x);
                const top = Math.max(0, rect.y);
                const right = Math.min(viewport.width, rect.x + rect.w);
                const bottom = Math.min(viewport.height, rect.y + rect.h);
                if (right <= left || bottom <= top) return null;
                return {
                    id: span.id,
                    text: span.text,
                    range: span.range,
                    rect: { x: left, y: top, w: right - left, h: bottom - top },
                    color: getFormulaReferenceColor(span.colorIndex),
                    active: span.id === formulaEditSession.activeReferenceId,
                    hasRangeHandles: span.kind === 'cell' || span.kind === 'range' || span.kind === 'spill',
                };
            })
            .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
    }, [controller, formulaEditSession, sheetName, viewportProjectionVersion, visibleBounds]);

    const autoFilterHeaderButtons = useMemo(() => {
        if (bareMode) return [];
        const viewport = controller.getViewport();
        const sheetButtons = buildAutoFilterHeaderButtons({
            autoFilter: activeSheetAutoFilter,
            visibleBounds,
            viewport,
            getRangeScreenRect: (range) => controller.getRangeScreenRect(range),
        });
        const tableButtons = buildTableFilterHeaderButtons({
            tables: activeSheetTables,
            activeAutoFilter: activeSheetAutoFilter,
            visibleBounds,
            viewport,
            getRangeScreenRect: (range) => controller.getRangeScreenRect(range),
        });
        return [...sheetButtons, ...tableButtons];
    }, [activeSheetAutoFilter, activeSheetTables, bareMode, controller, viewportProjectionVersion, visibleBounds]);

    const autoFilterHeaderButtonPresentations = useMemo(() => (
        autoFilterHeaderButtons.map((button) => buildAutoFilterHeaderButtonPresentation({
            button,
            activeMenu: autoFilterMenu,
        }))
    ), [autoFilterHeaderButtons, autoFilterMenu]);

    const tableOverlays = useMemo(() => {
        if (bareMode) return [];
        const viewport = controller.getViewport();
        return buildTableOverlayProjections({
            tables: activeSheetTables,
            visibleBounds,
            viewport,
            getRangeScreenRect: (range) => controller.getRangeScreenRect(range),
            selectedRange: selectedRange ? {
                startCol: selectedEngineRange.start_col,
                startRow: selectedEngineRange.start_row,
                endCol: selectedEngineRange.end_col,
                endRow: selectedEngineRange.end_row,
            } : null,
        });
    }, [activeSheetTables, bareMode, controller, selectedEngineRange, selectedRange, viewportProjectionVersion, visibleBounds]);

    const derivedArtifactOverlays = useMemo(() => {
        if (bareMode) return [];
        const viewport = controller.getViewport();
        return buildDerivedArtifactOverlayProjections({
            artifacts: Object.values(activeSheetDerivedArtifacts),
            activeSheetId: activeEngineSheetId,
            visibleBounds,
            viewport,
            selectedRange: selectedEngineRange,
            previewStates: derivedArtifactPreviews,
            getRangeScreenRect: (range) => controller.getRangeScreenRect({
                start: { col: range.start_col, row: range.start_row },
                end: { col: range.end_col, row: range.end_row },
            }),
        });
    }, [activeEngineSheetId, activeSheetDerivedArtifacts, bareMode, controller, derivedArtifactPreviews, selectedEngineRange, viewportProjectionVersion, visibleBounds]);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        // Remove legacy empty sheet-bar placeholders that can remain after older JSX paths/HMR.
        for (const node of Array.from(root.children)) {
            if (!(node instanceof HTMLDivElement)) continue;
            const style = node.style;
            const isLegacySheetBarSlot =
                style.gridColumnStart === '1' &&
                style.gridColumnEnd === '3' &&
                style.gridRowStart === '5' &&
                style.gridRowEnd === '6' &&
                String(style.zIndex || '').trim() === '40';
            const isEmpty = !node.firstElementChild && !String(node.textContent ?? '').trim();
            if (isLegacySheetBarSlot && isEmpty) {
                node.remove();
            }
        }
    });

    return (
        <div
            ref={rootRef}
            className="fg-spreadsheet-chrome-root"
            data-excel-chrome-variant={showWorkbookChrome ? LIVE_EXCEL_RIBBON_VARIANT : undefined}
            // role="grid" - Disabled due to severe performance penalties on large grids.
            // Chrome attempts to recalculate the entire accessibility tree on selection changes at scrollTop=0.
            // TODO: Re-implement with proper virtualization attributes (aria-rowcount, virtual roles).
            aria-label="Spreadsheet"
            style={{
                display: 'grid',
                gridTemplateColumns: `${HEADER_WIDTH}px 1fr`,
                gridTemplateRows: `${toolbarRowHeight}px ${formulaRowHeight}px ${HEADER_HEIGHT}px 1fr ${statusRowHeight}px`,
                gridAutoRows: '0px',
                width: embedded ? '100%' : '100vw',
                height: embedded ? `${effectiveHeight}px` : `${viewportHeight}px`,
                boxSizing: 'border-box',
                paddingTop: embedded ? 0 : 'env(safe-area-inset-top)',
                paddingRight: embedded ? 0 : 'env(safe-area-inset-right)',
                paddingBottom: embedded ? 0 : 'env(safe-area-inset-bottom)',
                paddingLeft: embedded ? 0 : 'env(safe-area-inset-left)',
                overflow: 'hidden',
                userSelect: 'none',
                outline: 'none'
            }}
        >
            {/* Accessibility: Live Region for Selection Announcements - Disabled for Performance Testing */}
            {/* <div
                aria-live="polite"
                aria-atomic="true"
                style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0
                }}
            >
                {selectedCell ? `${colToLetter(selectedCell.col)}${selectedCell.row + 1}` : ''}
            </div> */}

            {showWorkbookChrome && (
                <>
                    {showToolbar && (
                        <div className="fg-sheet-toolbar-row" style={{
                            gridColumn: '1 / 3',
                            gridRow: '1 / 2',
                            zIndex: 40,
                            borderBottom: '1px solid rgba(209, 213, 219, 0.88)',
                            pointerEvents: 'auto',
                            padding: 0,
                            margin: 0,
                        }}>
                            <ExcelRibbon
                                controller={controller}
                                variant={LIVE_EXCEL_RIBBON_VARIANT}
                                workbookName={workbookName || (sheetName ? `FuseGrid Workbook - ${sheetName}` : 'FuseGrid Workbook')}
                                onWorkbookNameChange={onWorkbookNameChange}
                                saveState="Autosaved"
                                currentFormat={selectedCellAuthoringFormat}
                                rowCount={rowCount}
                                colCount={colCount}
                                onColumnAction={onColumnAction}
                                onRowAction={onRowAction}
                                onToggleQuery={onToggleQuery || (() => { })}
                                onImport={() => setShowImportDialog(true)}
                                onOpenFind={openFindDialog}
                                onOpenFormatCells={() => setShowFormatCells(true)}
                                onToggleFullscreen={toggleFullscreen}
                                onViewStateChange={handleWorkbookChromeViewStateChange}
                                commandHandlers={ribbonCommandHandlers}
                                visualizeActions={visualizeActions}
                            />
                        </div>
                    )}

                    {showFormulaBar && (
                        <div className="fg-sheet-formula-row" style={{
                            gridColumn: '1 / 3',
                            gridRow: '2 / 3',
                            zIndex: 40,
                            borderBottom: '1px solid rgba(209, 213, 219, 0.88)',
                            pointerEvents: 'auto',
                            padding: 0,
                            margin: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.82)',
                        }}>
                            <ExcelFormulaBarRow
                                controller={controller}
                                selectedRange={selectedRange}
                                rowCount={rowCount}
                                colCount={colCount}
                                liveValue={currEditValue}
                                formulaSession={formulaEditSession}
                                currentSheetName={sheetName}
                                expanded={formulaBarExpanded}
                                definedNames={definedNamedRanges}
                                tableNames={activeSheetTableNameEntries}
                                formulaDefinedNames={formulaReferenceSuggestions}
                                onToggleExpanded={() => setFormulaBarExpanded((expanded) => !expanded)}
                                onNavigateToSelection={handleNameBoxNavigate}
                                onNavigateToDefinedName={navigateToNamedRange}
                                onAcceptFormula={handleFormulaBarAccept}
                                onCancelFormula={handleCancelEdit}
                                onBeginFormulaEdit={beginFormulaBarEdit}
                                onDraftChange={updateFormulaEditDraft}
                                onFormulaSessionChange={acceptFormulaEditSession}
                                onFinishFormulaEdit={finishFormulaEditSession}
                                showFunctionAssistant={formulaEditSurface === 'formula-bar'}
                                onActivateFormulaSurface={() => setFormulaEditSurface('formula-bar')}
                                onOpenInsertFunction={openInsertFunctionDialog}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Import Dialog (Overlay) */}
            {!bareMode && showImportDialog && (
                <div style={{ pointerEvents: 'auto', position: 'absolute', zIndex: 1000 }}>
                    <DataConnectionDialog
                        onImport={handleImport}
                        onClose={() => setShowImportDialog(false)}
                    />
                </div>
            )}

            {!bareMode && showFormatCells && (
                <ExcelFormatCellsDialog
                    controller={controller}
                    currentFormat={selectedCellAuthoringFormat}
                    onClose={() => setShowFormatCells(false)}
                    onApplied={handleWorkbookChromeViewStateChange}
                />
            )}

            {!bareMode && showConditionalFormatting && (
                <ExcelConditionalFormattingDialog
                    controller={controller}
                    selectedRange={selectedEngineRange}
                    selectionLabel={selectedRangeLabel}
                    rules={conditionalFormatRules}
                    loading={conditionalFormatRulesLoading}
                    error={conditionalFormatRulesError}
                    onRefresh={refreshConditionalFormatRules}
                    onDeleteRule={handleDeleteConditionalFormatRule}
                    onReorderRules={handleReorderConditionalFormatRules}
                    onClose={() => setShowConditionalFormatting(false)}
                    onApplied={handleConditionalFormatRulesChanged}
                />
            )}

            {!bareMode && sparklineDialogType && (
                <ExcelSparklineDialog
                    controller={controller}
                    selectedRange={selectedEngineRange}
                    selectionLabel={selectedRangeLabel}
                    initialType={sparklineDialogType}
                    autoEditExisting={sparklineDialogAutoEdit}
                    onClose={() => {
                        setSparklineDialogType(null);
                        setSparklineDialogAutoEdit(false);
                    }}
                    onApplied={handleSparklineGroupsChanged}
                />
            )}

            {!bareMode && chartDialogType && (
                <ExcelChartInsertDialog
                    controller={controller}
                    selectedRange={selectedEngineRange}
                    selectionLabel={selectedRangeLabel}
                    initialType={chartDialogType}
                    onClose={() => setChartDialogType(null)}
                    onInserted={handleWorkbookChromeViewStateChange}
                />
            )}

            {!bareMode && showNameManager && (
                <ExcelDefinedNameManagerDialog
                    definedNames={definedNames}
                    tableNames={activeSheetTableNameEntries}
                    sheets={definedNameSheets}
                    activeSheetId={activeEngineSheetId}
                    selectedRange={selectedEngineRange}
                    rowCount={rowCount}
                    colCount={colCount}
                    loading={nameManagerLoading}
                    error={nameManagerError}
                    initialMode={nameManagerInitialMode}
                    onInitialModeConsumed={() => setNameManagerInitialMode(null)}
                    onClose={() => setShowNameManager(false)}
                    onRefresh={refreshDefinedNames}
                    onUpsert={handleUpsertDefinedName}
                    onDelete={handleDeleteName}
                    onSelect={navigateToNamedRange}
                    onSelectTable={handleNameBoxNavigate}
                />
            )}

            {!bareMode && showCreateNamesDialog && (
                <ExcelCreateNamesFromSelectionDialog
                    selectionLabel={selectedRangeLabel}
                    defaultOptions={{
                        topRow: Boolean(selectedRange && Math.min(selectedRange.row, selectedRange.endRow) < Math.max(selectedRange.row, selectedRange.endRow)),
                        leftColumn: false,
                    }}
                    onClose={() => setShowCreateNamesDialog(false)}
                    onCreate={handleCreateNamesFromSelection}
                />
            )}

            {!bareMode && showInsertFunctionDialog && (
                <ExcelInsertFunctionDialog
                    initialFunctionName={insertFunctionEditTarget?.name ?? null}
                    initialArgs={insertFunctionEditTarget?.args ?? []}
                    onClose={() => {
                        setShowInsertFunctionDialog(false);
                        setInsertFunctionEditTarget(null);
                    }}
                    onEvaluateDraft={(formula) => (
                        controller.streamer
                            ? controller.streamer.evaluateDraftFormula(formula, { engineSheetId: activeEngineSheetId }).then((payload) => payload.display_value)
                            : Promise.resolve('')
                    )}
                    onPickRange={() => {
                        const selection = normalizeSelectionRange(controller.selection ?? selectedRange ?? {
                            col: selectedEngineRange.start_col,
                            row: selectedEngineRange.start_row,
                            endCol: selectedEngineRange.end_col,
                            endRow: selectedEngineRange.end_row,
                        });
                        return formatFormulaRangeRef({
                            start: { col: selection.col, row: selection.row },
                            end: { col: selection.endCol, row: selection.endRow },
                        });
                    }}
                    onInsert={(name, args) => {
                        insertFunctionAtFormulaCaret(name, args, insertFunctionEditTarget);
                        setShowInsertFunctionDialog(false);
                        setInsertFunctionEditTarget(null);
                    }}
                />
            )}

            {!bareMode && showSheetProtectionDialog && (
                <ExcelSheetProtectionDialog
                    currentProtection={activeSheetProtection}
                    sheetName={activeDefinedNameSheetName}
                    onClose={() => setShowSheetProtectionDialog(false)}
                    onApply={handleApplySheetProtection}
                />
            )}

            {!bareMode && cellMetadataMode && (
                <ExcelCellMetadataDialog
                    mode={cellMetadataMode}
                    selectionLabel={selectedRangeLabel}
                    currentComment={selectedCellMetadata?.comment ?? null}
                    currentHyperlink={selectedCellMetadata?.hyperlink ?? null}
                    currentProtection={selectedCellMetadata?.protection ?? null}
                    currentValidationId={selectedCellMetadata?.validationId ?? null}
                    currentValidationRule={selectedValidationRule}
                    onClose={() => setCellMetadataMode(null)}
                    onApplyComment={handleApplyCellComment}
                    onApplyHyperlink={handleApplyCellHyperlink}
                    onApplyProtection={handleApplyCellProtection}
                    onApplyValidation={handleApplyDataValidation}
                    onApplyValidationRule={handleApplyDataValidationRule}
                />
            )}

            {!bareMode && showCommentsPane && (
                <aside
                    className="fg-spreadsheet-comments-pane"
                    data-testid="fg-spreadsheet-comments-pane"
                    role="complementary"
                    aria-label={commentsPanePresentation.ariaLabel}
                    title={commentsPanePresentation.reviewTitle}
                    style={{
                        position: 'absolute',
                        top: showWorkbookChrome ? toolbarRowHeight + formulaRowHeight + 10 : 12,
                        right: 12,
                        bottom: showWorkbookChrome ? statusRowHeight + 10 : 12,
                        zIndex: 55,
                        width: 320,
                        maxWidth: 'calc(100% - 24px)',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid rgba(148, 163, 184, 0.42)',
                        borderRadius: 6,
                        background: '#ffffff',
                        color: '#111827',
                        boxShadow: '0 18px 44px rgba(15, 23, 42, 0.20)',
                        pointerEvents: 'auto',
                        overflow: 'hidden',
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 12px',
                            borderBottom: '1px solid rgba(148, 163, 184, 0.28)',
                            background: '#f8fafc',
                        }}
                    >
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 820, color: '#111827' }}>{commentsPanePresentation.title}</div>
                            <div style={{ fontSize: 11, color: '#667085', fontWeight: 620 }}>
                                {commentsPanePresentation.visibleCountLabel}
                            </div>
                        </div>
                        <button
                            type="button"
                            aria-label={commentsPanePresentation.closeAriaLabel}
                            title={commentsPanePresentation.closeTitle}
                            onClick={() => setShowCommentsPane(false)}
                            style={{
                                width: 26,
                                height: 26,
                                border: '1px solid #d1d5db',
                                borderRadius: 4,
                                background: '#ffffff',
                                color: '#374151',
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 850,
                                lineHeight: '22px',
                            }}
                        >
                            x
                        </button>
                    </div>

                    <div style={{ padding: 12, borderBottom: '1px solid rgba(148, 163, 184, 0.22)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                            <strong style={{ color: 'var(--fg-green)', fontSize: 12 }}>{commentsPanePresentation.activeCellLabel}</strong>
                            <span style={{ color: '#667085', fontSize: 12, fontWeight: 700 }}>{commentsPanePresentation.activeAddress}</span>
                        </div>
                        {commentsPanePresentation.activeComment ? (
                            <div
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    color: '#1f2937',
                                    fontSize: 12,
                                    lineHeight: 1.4,
                                    marginBottom: 10,
                                }}
                            >
                                {commentsPanePresentation.activeComment}
                            </div>
                        ) : (
                            <div style={{ color: '#667085', fontSize: 12, lineHeight: 1.4, marginBottom: 10 }}>
                                {commentsPanePresentation.activeEmptyMessage}
                            </div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <button
                                type="button"
                                title={commentsPanePresentation.editAction.title}
                                disabled={!commentsPanePresentation.editAction.enabled}
                                onClick={() => handleCellMetadataAction(commentsPanePresentation.editAction.id)}
                                style={{
                                    border: '1px solid var(--fg-green)',
                                    borderRadius: 4,
                                    background: 'var(--fg-green)',
                                    color: '#ffffff',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 760,
                                    padding: '5px 10px',
                                }}
                            >
                                {commentsPanePresentation.editAction.label}
                            </button>
                            <button
                                type="button"
                                title={commentsPanePresentation.clearAction.title}
                                disabled={!commentsPanePresentation.clearAction.enabled}
                                onClick={() => handleCellMetadataAction(commentsPanePresentation.clearAction.id)}
                                style={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: 4,
                                    background: commentsPanePresentation.clearAction.enabled ? '#ffffff' : '#f3f4f6',
                                    color: commentsPanePresentation.clearAction.enabled ? '#1f2937' : '#9ca3af',
                                    cursor: commentsPanePresentation.clearAction.enabled ? 'pointer' : 'not-allowed',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    padding: '5px 10px',
                                }}
                            >
                                {commentsPanePresentation.clearAction.label}
                            </button>
                        </div>
                    </div>

                    <div
                        aria-label={commentsPanePresentation.visibleListAriaLabel}
                        style={{
                            overflow: 'auto',
                            padding: 8,
                            flex: 1,
                            minHeight: 0,
                        }}
                    >
                        {commentsPanePresentation.visibleItems.length > 0 ? (
                            commentsPanePresentation.visibleItems.map((entry) => (
                                <button
                                    key={entry.key}
                                    type="button"
                                    title={entry.title}
                                    onClick={() => selectCommentEntry(entry)}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        margin: '0 0 6px 0',
                                        padding: '8px 9px',
                                        border: entry.selected ? '1px solid rgba(16, 124, 65, 0.55)' : '1px solid rgba(148, 163, 184, 0.28)',
                                        borderRadius: 5,
                                        background: entry.selected ? '#f0fdf4' : '#ffffff',
                                        color: '#1f2937',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                                        <strong style={{ color: entry.selected ? 'var(--fg-green)' : '#334155', fontSize: 12 }}>
                                            {entry.address}
                                        </strong>
                                        {entry.selectedLabel ? (
                                            <span style={{ color: 'var(--fg-green)', fontSize: 11, fontWeight: 750 }}>{entry.selectedLabel}</span>
                                        ) : null}
                                    </div>
                                    <div
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            color: '#475467',
                                            fontSize: 12,
                                            lineHeight: 1.35,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {entry.comment}
                                    </div>
                                </button>
                            ))
                        ) : (
                            <div
                                style={{
                                    padding: '18px 12px',
                                    border: '1px dashed rgba(148, 163, 184, 0.46)',
                                    borderRadius: 5,
                                    color: '#667085',
                                    fontSize: 12,
                                    lineHeight: 1.4,
                                    textAlign: 'center',
                                }}
                            >
                                {commentsPanePresentation.visibleEmptyMessage}
                            </div>
                        )}
                    </div>
                </aside>
            )}

            {/* Corner (Row 3) */}
            <div className="fg-sheet-corner" style={{
                backgroundColor: '#f8f9fa',
                borderRight: '1px solid #ccc',
                borderBottom: '1px solid #ccc',
                zIndex: 30,
                gridRow: '3 / 4',
                gridColumn: '1 / 2',
                pointerEvents: 'auto'
            }}
                title="Select all"
                onMouseDown={(event) => {
                    if (event.button !== 0) return;
                    event.preventDefault();
                    event.stopPropagation();
                    controller.selectAllCells();
                    setContextMenu(null);
                }}
            />

            {/* Column Headers (Row 3) */}
            <div
                ref={colHeaderRef}
                className="fusegrid-col-headers fg-sheet-col-headers"
                style={{
                    overflow: 'hidden',
                    position: 'relative',
                    zIndex: 40,
                    gridRow: '3 / 4',
                    gridColumn: '2 / 3',
                    pointerEvents: 'auto',
                    height: HEADER_HEIGHT,
                    minHeight: HEADER_HEIGHT,
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #c0c0c0',
                    width: '100%',
                }}
            >
                {renderColHeaders()}
            </div>

            {/* Row Headers (Row 4) */}
            <div
                className="fg-sheet-row-headers"
                ref={rowHeaderRef}
                style={{
                    overflow: 'hidden',
                    position: 'relative',
                    zIndex: 20,
                    gridRow: '4 / 5',
                    gridColumn: '1 / 2',
                    pointerEvents: 'auto'
                }}
            >
                {renderRowHeaders()}
            </div>

            {/* Main Viewport (Row 4) */}
            <div
                className="fg-sheet-main-viewport"
                style={{
                    position: 'relative',
                    gridRow: '4 / 5',
                    gridColumn: '2 / 3',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                }}
            >
                {/* Frozen Canvas Layer (Fixed) */}
                <div
                    ref={canvasContainerRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 0
                    }}
                >
                    <SpreadsheetCanvas
                        controller={controller}
                        style={{ width: '100%', height: '100%' }}
                        inputTarget={scrollEl}
                    />
                </div>

                {renderFixedOverlay && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            overflow: 'hidden',
                            zIndex: 11,
                            pointerEvents: 'none',
                        }}
                    >
                        {renderFixedOverlay}
                    </div>
                )}

                {/* Scroll Overlay Layer (Scrolling) */}
                <div
                    ref={setScrollContainerEl}
                    onScroll={handleScroll}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        overflow: 'auto',
                        backgroundColor: 'transparent',
                        zIndex: 10,
                        willChange: 'scroll-position',
                        touchAction: 'pan-x pan-y',
                        // pointerEvents: 'auto' // handled by browser defaults for scroll container
                    }}
                >
                    {/* Phantom Spacer */}
                    <div style={{
                        width: Math.max(totalWidth, colCount * HEADER_WIDTH),
                        height: totalHeightDom + (showAddRowsFooter ? 50 : 0),
                        pointerEvents: 'none',
                        contain: 'size layout',
                        contentVisibility: 'auto'
                    }} />

                    {renderOverlay && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: totalWidth,
                                height: totalHeightWorld,
                                pointerEvents: 'auto',
                                zIndex: 12
                            }}
                        >
                            {renderOverlay({ totalWidth, totalHeight: totalHeightWorld })}
                        </div>
                    )}

                    {/* Footer Inside Scroll Container - Only render if near bottom to save DOM/Performance */}
                    {showAddRowsFooter && (
                        <div
                            ref={footerRef}
                            style={{
                                position: 'absolute',
                                top: windowedScroll ? (totalHeightWorld - scrollBaseYRef.current) : totalHeightWorld, // At the end of data
                                left: 0,
                                width: '100%',
                                height: 50,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                paddingLeft: '50px', // Align with grid data start roughly
                                backgroundColor: '#ffffff', // Solid background to hide gridlines
                                gap: '0',
                                pointerEvents: 'auto',
                                zIndex: 20
                            }}
                            // Prevent bubbling to scroll container? no, we want to be able to click it.
                            onMouseDown={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => onAddRows(addRowCount)}
                                style={{
                                    backgroundColor: 'transparent',
                                    color: 'var(--fg-green)',
                                    border: '1px solid transparent',
                                    borderRadius: '4px',
                                    height: '36px',
                                    padding: '0 16px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f3f4'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                Add
                            </button>

                            <input
                                type="number"
                                value={addRowCount}
                                onChange={(e) => setAddRowCount(Number(e.target.value))}
                                style={{
                                    width: '60px',
                                    height: '28px',
                                    margin: '0 8px',
                                    textAlign: 'center',
                                    border: '1px solid #dadce0',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    color: '#3c4043'
                                }}
                            />

                            <span style={{
                                fontSize: '14px',
                                color: '#3c4043'
                            }}>
                                more rows at the bottom
                            </span>

                            {isLoadingMore && <span style={{ color: 'var(--fg-status-working-text)', marginLeft: '16px', fontSize: '14px' }}>Loading...</span>}
                        </div>
                    )}
                </div>

                {editingCell && (
                    <div
                        className="fg-spreadsheet-editor-layer"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 45,
                            pointerEvents: 'none',
                            overflow: 'visible',
                        }}
                    >
                        <CellEditor
                            initialValue={editingCell.initialValue}
                            rect={editingCell.rect}
                            format={editingCell.format}
                            onCommit={handleCommitEdit}
                            onCancel={handleCancelEdit}
                            onChange={updateFormulaEditDraft}
                            formulaSession={formulaEditSession}
                            formulaDefinedNames={formulaReferenceSuggestions}
                            onFormulaSessionChange={acceptFormulaEditSession}
                            showFunctionAssistant={formulaEditSurface === 'cell'}
                            onActivateFormulaSurface={() => setFormulaEditSurface('cell')}
                        />
                    </div>
                )}

                {!bareMode && selectedValidationPrompt && (
                    <div
                        className="fg-spreadsheet-validation-input-prompt"
                        data-testid="fg-spreadsheet-validation-input-prompt"
                        role="note"
                        title={selectedValidationPrompt.message || selectedValidationPrompt.title}
                        style={{
                            position: 'absolute',
                            left: selectedValidationPrompt.x,
                            top: selectedValidationPrompt.y,
                            zIndex: 24,
                            width: 260,
                            maxWidth: 'calc(100% - 24px)',
                            pointerEvents: 'none',
                            padding: '8px 10px',
                            border: '1px solid rgba(16, 124, 65, 0.28)',
                            borderRadius: 6,
                            background: '#f8fff9',
                            color: '#10281a',
                            boxShadow: '0 10px 22px rgba(17, 24, 39, 0.12)',
                            fontSize: 12,
                            lineHeight: 1.35,
                        }}
                    >
                        <div style={{ fontWeight: 750, color: 'var(--fg-green)', marginBottom: selectedValidationPrompt.message ? 2 : 0 }}>
                            {selectedValidationPrompt.title}
                        </div>
                        {selectedValidationPrompt.message ? (
                            <div>{selectedValidationPrompt.message}</div>
                        ) : null}
                    </div>
                )}

                {!bareMode && selectedMetadataCard && (
                    <div
                        className="fg-spreadsheet-cell-metadata-card"
                        data-testid="fg-spreadsheet-cell-metadata-card"
                        role="note"
                        title={selectedMetadataCard.titleLines.join('\n')}
                        style={{
                            position: 'absolute',
                            left: selectedMetadataCard.x,
                            top: selectedMetadataCard.y,
                            zIndex: 25,
                            width: 280,
                            maxWidth: 'calc(100% - 24px)',
                            pointerEvents: 'auto',
                            padding: '8px 10px',
                            border: '1px solid rgba(16, 124, 65, 0.24)',
                            borderRadius: 6,
                            background: '#ffffff',
                            color: '#111827',
                            boxShadow: '0 12px 28px rgba(17, 24, 39, 0.16)',
                            fontSize: 12,
                            lineHeight: 1.35,
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: selectedMetadataCard.comment ? 6 : 4 }}>
                            <strong style={{ color: 'var(--fg-green)' }}>
                                {selectedMetadataCard.title}
                            </strong>
                            <span style={{ color: '#6b7280', fontWeight: 650 }}>{selectedMetadataCard.address}</span>
                        </div>
                        {selectedMetadataCard.comment ? (
                            <div style={{ marginBottom: selectedMetadataCard.hyperlink ? 8 : 6, whiteSpace: 'pre-wrap' }}>
                                {selectedMetadataCard.comment}
                            </div>
                        ) : null}
                        {selectedMetadataCard.hyperlink ? (
                            <div
                                data-testid="fg-spreadsheet-hyperlink-preview"
                                title={selectedMetadataCard.hyperlinkPreview?.detail ?? selectedMetadataCard.hyperlink}
                                style={{
                                    width: '100%',
                                    margin: selectedMetadataCard.comment ? '0 0 8px 0' : '0 0 6px 0',
                                    padding: '6px 7px',
                                    border: selectedMetadataCard.openableHyperlink
                                        ? '1px solid rgba(16, 124, 65, 0.28)'
                                        : '1px solid rgba(245, 158, 11, 0.42)',
                                    borderRadius: 4,
                                    background: selectedMetadataCard.openableHyperlink ? '#f8fff9' : '#fffbeb',
                                    color: '#374151',
                                    textAlign: 'left',
                                }}
                            >
                                <div style={{ color: selectedMetadataCard.openableHyperlink ? 'var(--fg-green)' : '#92400e', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {selectedMetadataCard.hyperlinkPreview?.warning ?? 'Destination'}: {selectedMetadataCard.hyperlinkPreview?.label ?? selectedMetadataCard.hyperlink}
                                </div>
                                <button
                                    type="button"
                                    title={selectedMetadataCard.openableHyperlink ? `Open ${selectedMetadataCard.hyperlink}` : 'Edit this link before opening it'}
                                    disabled={!selectedMetadataCard.openableHyperlink}
                                    onClick={() => {
                                        const plan = buildSelectedMetadataCardOpenHyperlinkPlan(selectedMetadataCard);
                                        if (plan) executeCellMetadataActionPlan(plan);
                                    }}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        margin: 0,
                                        padding: 0,
                                        border: 0,
                                        background: 'transparent',
                                        color: selectedMetadataCard.openableHyperlink ? '#475467' : '#6b7280',
                                        cursor: selectedMetadataCard.openableHyperlink ? 'pointer' : 'not-allowed',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'left',
                                        fontSize: 11,
                                        fontWeight: 650,
                                    }}
                                >
                                    {selectedMetadataCard.hyperlinkPreview?.detail ?? selectedMetadataCard.hyperlink}
                                </button>
                            </div>
                        ) : null}
                        {selectedMetadataCard.protectionLabel ? (
                            <div
                                style={{
                                    margin: (selectedMetadataCard.comment || selectedMetadataCard.hyperlink) ? '0 0 8px 0' : '0 0 6px 0',
                                    padding: '5px 7px',
                                    border: '1px solid rgba(55, 65, 81, 0.16)',
                                    borderRadius: 4,
                                    background: '#f9fafb',
                                    color: '#374151',
                                    fontWeight: 650,
                                }}
                            >
                                <div>Protection: {selectedMetadataCard.protectionLabel}</div>
                                {selectedMetadataCard.protectionDetail ? (
                                    <div style={{ color: '#6b7280', fontWeight: 550, marginTop: 2 }}>
                                        {selectedMetadataCard.protectionDetail}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 6 }}>
                            {selectedMetadataCard.actions.map((action) => (
                                <button
                                    key={action.id}
                                    type="button"
                                    title={action.title}
                                    disabled={!action.enabled}
                                    onClick={() => handleCellMetadataAction(action.id)}
                                    style={{
                                        border: '1px solid #d1d5db',
                                        borderRadius: 4,
                                        background: action.enabled ? '#ffffff' : '#f3f4f6',
                                        color: action.enabled ? '#1f2937' : '#9ca3af',
                                        cursor: action.enabled ? 'pointer' : 'not-allowed',
                                        fontSize: 12,
                                        fontWeight: 650,
                                        padding: '4px 8px',
                                    }}
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {validationIssueMarkers.length > 0 && (
                    <div
                        className="fg-spreadsheet-validation-issue-markers"
                        data-testid="fg-spreadsheet-validation-issue-markers"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 13,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {validationIssueMarkers.map(({ key, title, ariaLabel, x, y, width, height }) => (
                            <div
                                key={key}
                                title={title}
                                aria-label={ariaLabel}
                                className="fg-spreadsheet-validation-issue-marker"
                                style={{
                                    position: 'absolute',
                                    left: x,
                                    top: y,
                                    width,
                                    height,
                                    border: '2px solid #d13438',
                                    borderRadius: '999px',
                                    boxShadow: '0 0 0 1px rgba(209, 52, 56, 0.18), 0 0 0 4px rgba(209, 52, 56, 0.08)',
                                    boxSizing: 'border-box',
                                    backgroundColor: 'rgba(209, 52, 56, 0.035)',
                                }}
                            />
                        ))}
                    </div>
                )}

                {formulaReferenceOverlays.length > 0 && (
                    <div
                        className="fg-formula-reference-overlays"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 14,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {formulaReferenceOverlays.map(({ id, text, range, rect, color, active, hasRangeHandles }) => (
                            <div
                                key={id}
                                data-formula-reference={text}
                                style={{
                                    position: 'absolute',
                                    left: rect.x,
                                    top: rect.y,
                                    width: Math.max(1, rect.w),
                                    height: Math.max(1, rect.h),
                                    boxSizing: 'border-box',
                                    border: `${active ? 3 : 2}px solid ${color}`,
                                    backgroundColor: active ? `${color}1f` : `${color}14`,
                                    boxShadow: active ? `0 0 0 1px ${color}55` : 'none',
                                }}
                            >
                                {active && hasRangeHandles && (
                                    <>
                                        <span
                                            title="Move referenced range"
                                            style={{
                                                position: 'absolute',
                                                left: -5,
                                                top: -5,
                                                width: 10,
                                                height: 10,
                                                border: `2px solid ${color}`,
                                                backgroundColor: '#ffffff',
                                                boxSizing: 'border-box',
                                                cursor: 'move',
                                                pointerEvents: 'auto',
                                            }}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                formulaReferenceMoveRef.current = {
                                                    referenceId: id,
                                                };
                                            }}
                                        />
                                        <span
                                            title="Resize referenced range"
                                            style={{
                                                position: 'absolute',
                                                right: -5,
                                                bottom: -5,
                                                width: 9,
                                                height: 9,
                                                border: `2px solid ${color}`,
                                                backgroundColor: '#ffffff',
                                                boxSizing: 'border-box',
                                                cursor: 'nwse-resize',
                                                pointerEvents: 'auto',
                                            }}
                                            onMouseDown={(event) => {
                                                if (!range) return;
                                                event.preventDefault();
                                                event.stopPropagation();
                                                formulaReferenceResizeRef.current = {
                                                    referenceId: id,
                                                    anchor: range.start,
                                                    sheetName: range.sheetName,
                                                };
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!bareMode && tableOverlays.length > 0 && (
                    <div
                        className="fg-spreadsheet-table-overlays"
                        data-testid="fg-spreadsheet-table-overlays"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 15,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {tableOverlays.map((table) => {
                            const address = `${colToLetter(table.range.start_col)}${table.range.start_row + 1}:${colToLetter(table.range.end_col)}${table.range.end_row + 1}`;
                            const title = `${table.name} table ${address}. Right-click inside the table to resize or delete table metadata.`;
                            return (
                                <div
                                    key={table.id}
                                    data-testid={`fg-table-overlay-${table.id}`}
                                    title={title}
                                    aria-label={title}
                                    style={{
                                        position: 'absolute',
                                        left: table.rect.x,
                                        top: table.rect.y,
                                        width: table.rect.w,
                                        height: table.rect.h,
                                        border: '2px solid rgba(16, 124, 65, 0.72)',
                                        boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.9)',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {table.headerRect && table.headerRect.h > 4 && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: Math.max(0, table.headerRect.x - table.rect.x),
                                                top: Math.max(0, table.headerRect.y - table.rect.y),
                                                width: Math.min(table.rect.w, table.headerRect.w),
                                                height: Math.min(table.rect.h, table.headerRect.h),
                                                background: 'rgba(16, 124, 65, 0.08)',
                                                borderBottom: '1px solid rgba(16, 124, 65, 0.36)',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    )}
                                    {table.selected && (
                                        <button
                                            type="button"
                                            title={`Double-click to rename ${table.name}.`}
                                            aria-label={`Selected table ${table.name}. Double-click to rename.`}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            onDoubleClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                const nextName = window.prompt('Table name', table.name);
                                                if (nextName !== null) {
                                                    controller.renameTableAtCell(table.range.start_col, table.range.start_row, nextName);
                                                }
                                            }}
                                            style={{
                                                position: 'absolute',
                                                left: 4,
                                                top: -24,
                                                maxWidth: Math.max(48, table.rect.w - 12),
                                                padding: '2px 7px',
                                                borderRadius: 4,
                                                background: 'rgba(255, 255, 255, 0.94)',
                                                border: '1px solid rgba(16, 124, 65, 0.36)',
                                                color: 'var(--fg-green)',
                                                fontSize: 11,
                                                fontWeight: 800,
                                                lineHeight: '16px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                pointerEvents: 'auto',
                                                cursor: 'text',
                                            }}
                                        >
                                            {table.name}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!bareMode && derivedArtifactOverlays.length > 0 && (
                    <div
                        className="fg-spreadsheet-derived-artifact-overlays"
                        data-testid="fg-spreadsheet-derived-artifact-overlays"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 17,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {derivedArtifactOverlays.map((artifact) => {
                            const renderPresentation = buildDerivedArtifactOverlayRenderPresentation(artifact);
                            const chartPreview = renderPresentation.chartPreview;
                            const pivotPreview = renderPresentation.pivotPreview;
                            const previewFootnote = artifact.previewFootnote;
                            const overlayChrome = renderPresentation.chrome;
                            const overlayTheme = renderPresentation.theme;
                            const chartPresentation = renderPresentation.chartPresentation;
                            const chartSeriesSummaryItems = renderPresentation.chartSeriesSummaryItems;
                            const chartAggregatePlot = renderPresentation.chartAggregatePlot;
                            const chartSeriesPlotItems = renderPresentation.chartSeriesPlotItems;
                            const chartCategoryBarGroups = renderPresentation.chartCategoryBarGroups;
                            const chartAxisGuideLines = renderPresentation.chartAxisGuideLines;
                            const chartAxisTickMarks = renderPresentation.chartAxisTickMarks;
                            const chartAxisTickLabels = renderPresentation.chartAxisTickLabels;
                            const chartDataLabelItems = renderPresentation.chartDataLabelItems;
                            const chartPieGradient = renderPresentation.chartPieGradient;
                            const chartPieHoleSizePct = renderPresentation.chartPieHoleSizePct;
                            const chartPieSliceItems = renderPresentation.chartPieSliceItems;
                            const chartPieLegendItems = renderPresentation.chartPieLegendItems;
                            const chartSettings = renderPresentation.chartSettings;
                            const chartLegendPosition = chartSettings?.legendPosition ?? 'top';
                            const chartQuickToolbar = renderPresentation.chartQuickToolbar;
                            const showChartQuickToolbar = artifact.placementSelected && chartQuickToolbar;
                            const pivotMeasureSummaryItems = renderPresentation.pivotMeasureSummaryItems;
                            const chartRenderKind = renderPresentation.chartRenderKind;
                            const chartAreaStyle = chartPresentation?.chartAreaStyle;
                            const plotAreaStyle = chartPresentation?.plotAreaStyle;
                            const chartTitleStyle = chartPresentation?.chartTitleStyle;
                            const categoryAxisTitleStyle = chartPresentation?.categoryAxisTitleStyle;
                            const valueAxisTitleStyle = chartPresentation?.valueAxisTitleStyle;
                            const secondaryValueAxisTitleStyle = chartPresentation?.secondaryValueAxisTitleStyle;
                            const previewStatus = renderPresentation.previewStatus;
                            const placementSelectionUpdate = artifact.placementSelectionUpdate;
                            const sourceSelectionUpdate = artifact.sourceSelectionUpdate;
                            return (
                                <React.Fragment key={artifact.id}>
                                {artifact.sourceRect && (
                                    <div
                                        data-testid={`fg-derived-artifact-source-outline-${artifact.id}`}
                                        title={overlayChrome.sourceOutlineTitle}
                                        aria-label={overlayChrome.sourceOutlineAriaLabel}
                                        style={{
                                            position: 'absolute',
                                            left: artifact.sourceRect.x,
                                            top: artifact.sourceRect.y,
                                            width: artifact.sourceRect.w,
                                            height: artifact.sourceRect.h,
                                            border: `${artifact.sourceSelected ? 3 : 2}px ${artifact.sourceSelected ? 'solid' : 'dashed'} ${overlayTheme.sourceOutlineBorderColor}`,
                                            background: artifact.sourceSelected ? overlayTheme.sourceOutlineSelectedBackground : overlayTheme.sourceOutlineBackground,
                                            boxShadow: artifact.sourceSelected ? `0 0 0 2px ${overlayTheme.sourceOutlineShadowColor}` : undefined,
                                            boxSizing: 'border-box',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            data-testid={`fg-derived-artifact-source-move-handle-${artifact.id}`}
                                            title={overlayChrome.sourceMoveTitle}
                                            aria-label={overlayChrome.sourceMoveAriaLabel}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                activeDerivedArtifactDragRef.current = artifact.dragStates.sourceMove;
                                                applyDerivedArtifactSelectionUpdate(sourceSelectionUpdate);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                left: 3,
                                                top: 3,
                                                maxWidth: Math.max(40, artifact.sourceRect.w - 20),
                                                height: 16,
                                                padding: '0 5px',
                                                borderRadius: 4,
                                                border: `1px solid ${overlayTheme.sourceHandleBorderColor}`,
                                                background: 'rgba(255, 255, 255, 0.94)',
                                                color: overlayTheme.sourceHandleTextColor,
                                                fontSize: 10,
                                                fontWeight: 800,
                                                lineHeight: '14px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                cursor: 'move',
                                                boxSizing: 'border-box',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            {overlayChrome.sourceChipLabel}
                                        </div>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            data-testid={`fg-derived-artifact-source-resize-handle-${artifact.id}`}
                                            title={overlayChrome.sourceResizeTitle}
                                            aria-label={overlayChrome.sourceResizeAriaLabel}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                activeDerivedArtifactDragRef.current = artifact.dragStates.sourceResize;
                                                applyDerivedArtifactSelectionUpdate(sourceSelectionUpdate);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                right: 3,
                                                bottom: 3,
                                                width: 10,
                                                height: 10,
                                                borderRadius: 3,
                                                border: `2px solid ${overlayTheme.sourceResizeBorderColor}`,
                                                background: '#fff',
                                                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
                                                cursor: 'nwse-resize',
                                                boxSizing: 'border-box',
                                                pointerEvents: 'auto',
                                            }}
                                        />
                                    </div>
                                )}
                                <div
                                    role="button"
                                    tabIndex={0}
                                    data-testid={`fg-derived-artifact-overlay-${artifact.id}`}
                                    title={overlayChrome.overlayTitle}
                                    aria-label={overlayChrome.overlayAriaLabel}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        controller.selectDerivedArtifactAtCell(placementSelectionUpdate.placementCell.col, placementSelectionUpdate.placementCell.row);
                                        applyDerivedArtifactSelectionUpdate(placementSelectionUpdate);
                                    }}
                                    onContextMenu={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        controller.selectDerivedArtifactAtCell(placementSelectionUpdate.placementCell.col, placementSelectionUpdate.placementCell.row);
                                        applyDerivedArtifactSelectionUpdate(placementSelectionUpdate);
                                        setContextMenu({
                                            kind: 'cell',
                                            x: event.clientX,
                                            y: event.clientY,
                                            col: placementSelectionUpdate.placementCell.col,
                                            row: placementSelectionUpdate.placementCell.row,
                                        });
                                    }}
                                    style={{
                                        position: 'absolute',
                                        left: artifact.rect.x,
                                        top: artifact.rect.y,
                                        width: artifact.rect.w,
                                        height: artifact.rect.h,
                                        border: `${artifact.placementSelected ? 3 : 2}px solid ${overlayTheme.overlayBorderColor}`,
                                        borderRadius: 6,
                                        background: overlayTheme.overlayBackground,
                                        boxShadow: artifact.placementSelected
                                            ? `0 0 0 2px ${overlayTheme.overlaySelectedShadowColor}, 0 8px 24px rgba(15, 23, 42, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.9)`
                                            : '0 8px 24px rgba(15, 23, 42, 0.10), inset 0 0 0 1px rgba(255, 255, 255, 0.9)',
                                        boxSizing: 'border-box',
                                        pointerEvents: 'auto',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            height: 24,
                                            padding: '0 8px',
                                            background: overlayTheme.headerBackground,
                                            borderBottom: `1px solid ${overlayTheme.headerBorderColor}`,
                                            color: overlayTheme.headerTextColor,
                                            fontSize: 11,
                                            fontWeight: 800,
                                            lineHeight: '24px',
                                            whiteSpace: 'nowrap',
                                            cursor: 'move',
                                        }}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            activeDerivedArtifactDragRef.current = artifact.dragStates.placementMove;
                                            controller.selectDerivedArtifactAtCell(placementSelectionUpdate.placementCell.col, placementSelectionUpdate.placementCell.row);
                                            applyDerivedArtifactSelectionUpdate(placementSelectionUpdate);
                                        }}
                                    >
                                        <span aria-hidden="true">{overlayChrome.iconLabel}</span>
                                        <span
                                            style={{
                                                ...chartTextOverflowStyle(chartTitleStyle),
                                                color: chartTitleStyle?.color,
                                                fontSize: chartTitleStyle?.fontSize,
                                                fontWeight: chartTitleStyle?.fontWeight,
                                                fontStyle: chartTitleStyle?.fontStyle,
                                                transform: chartTitleStyle?.rotationDeg ? `rotate(${chartTitleStyle.rotationDeg}deg)` : undefined,
                                                transformOrigin: 'center',
                                                textAlign: chartTitleStyle?.textAlign,
                                                alignSelf: chartTitleStyle?.textVerticalAlign === 'top' ? 'flex-start' : chartTitleStyle?.textVerticalAlign === 'bottom' ? 'flex-end' : undefined,
                                                whiteSpace: chartTitleStyle?.textWrap ? 'normal' : undefined,
                                                padding: chartTitleStyle ? `${chartTitleStyle.textInsetTop}px ${chartTitleStyle.textInsetRight}px ${chartTitleStyle.textInsetBottom}px ${chartTitleStyle.textInsetLeft}px` : undefined,
                                            }}
                                        >
                                            {overlayChrome.title}
                                        </span>
                                        <span
                                            style={{
                                                marginLeft: 'auto',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                color: overlayTheme.headerAddressTextColor,
                                                fontSize: 10,
                                                fontWeight: 800,
                                            }}
                                        >
                                            {overlayChrome.placementAddress}
                                        </span>
                                    </div>
                                    {showChartQuickToolbar && chartQuickToolbar && (
                                        <div
                                            data-testid={`fg-derived-artifact-chart-toolbar-${artifact.id}`}
                                            title={chartQuickToolbar.title}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            style={{
                                                position: 'absolute',
                                                left: 8,
                                                right: 8,
                                                top: 29,
                                                height: 38,
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(86px, 0.75fr) minmax(132px, 1.15fr) minmax(108px, 1fr) minmax(116px, 1fr)',
                                                alignItems: 'stretch',
                                                gap: 5,
                                                padding: 4,
                                                borderRadius: 5,
                                                border: '1px solid rgba(16, 124, 65, 0.22)',
                                                background: 'rgba(255, 255, 255, 0.88)',
                                                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.10)',
                                                boxSizing: 'border-box',
                                                overflow: 'hidden',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            {[chartQuickToolbar.editGroup, chartQuickToolbar.chartTypeGroup, chartQuickToolbar.sourceGroup, chartQuickToolbar.displayGroup].map((group) => (
                                                <div
                                                    key={group.label}
                                                    title={group.title}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'auto minmax(0, 1fr)',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            color: '#166534',
                                                            fontSize: 9,
                                                            fontWeight: 900,
                                                            lineHeight: '22px',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {group.label}
                                                    </span>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 3,
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {group.items.map((item) => (
                                                            <button
                                                                key={item.action}
                                                                type="button"
                                                                data-testid={`fg-derived-artifact-chart-toolbar-action-${item.action}`}
                                                                aria-pressed={item.active}
                                                                title={item.title}
                                                                onMouseDown={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                }}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    handleDerivedArtifactOverlayAction(artifact, item.action, item);
                                                                }}
                                                                style={{
                                                                    height: 22,
                                                                    minWidth: 0,
                                                                    flex: '1 1 0',
                                                                    padding: '0 4px',
                                                                    borderRadius: 4,
                                                                    border: item.active ? '1px solid rgba(16, 124, 65, 0.72)' : '1px solid rgba(148, 163, 184, 0.34)',
                                                                    background: item.active ? 'rgba(16, 124, 65, 0.12)' : 'rgba(248, 250, 252, 0.92)',
                                                                    color: item.active ? '#166534' : '#334155',
                                                                    fontSize: 9,
                                                                    fontWeight: 900,
                                                                    lineHeight: '20px',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                {item.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: showChartQuickToolbar ? '74px 8px 8px' : '28px 8px 8px',
                                            border: `1px dashed ${overlayTheme.previewBorderColor}`,
                                            borderRadius: 4,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#475569',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            background: 'rgba(255, 255, 255, 0.42)',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {previewStatus.statusKind === 'loading' ? (
                                            <span title={previewStatus.title}>{previewStatus.label}</span>
                                        ) : previewStatus.statusKind === 'error' ? (
                                            <span title={previewStatus.title}>{previewStatus.label}</span>
                                        ) : chartPreview ? (
                                            previewStatus.statusKind === 'chart' ? (
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        padding: '6px 8px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        gap: 4,
                                                        background: chartAreaStyle?.background ?? 'transparent',
                                                        border: chartAreaStyle && chartAreaStyle.borderWidth > 0 ? `${chartAreaStyle.borderWidth}px solid ${chartAreaStyle.borderColor}` : undefined,
                                                        borderRadius: chartAreaStyle?.borderRadius ?? 4,
                                                        boxSizing: 'border-box',
                                                    }}
                                                >
                                                    {chartSettings?.showLegend !== false && chartLegendPosition === 'top' && chartSeriesSummaryItems.length > 1 && (
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: `repeat(${chartSeriesSummaryItems.length}, minmax(0, 1fr))`,
                                                                gap: 4,
                                                            }}
                                                        >
                                                            {chartSeriesSummaryItems.map((series) => (
                                                                <div
                                                                    key={series.name}
                                                                    title={series.title}
                                                                    style={{
                                                                        minWidth: 0,
                                                                        border: '1px solid rgba(16, 124, 65, 0.18)',
                                                                        borderRadius: 4,
                                                                        background: 'rgba(255, 255, 255, 0.64)',
                                                                        padding: '2px 4px',
                                                                    }}
                                                                >
                                                                    <div style={{ color: '#166534', fontSize: 9, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {series.name}
                                                                    </div>
                                                                    <div style={{ color: '#334155', fontSize: 10, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {series.displayTotal}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {chartRenderKind === 'plot' ? (
                                                        <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 56, borderRadius: plotAreaStyle?.borderRadius ?? 4, background: plotAreaStyle?.background ?? 'rgba(255, 255, 255, 0.58)', border: plotAreaStyle && plotAreaStyle.borderWidth > 0 ? `${plotAreaStyle.borderWidth}px solid ${plotAreaStyle.borderColor}` : undefined, boxSizing: 'border-box', overflow: 'hidden' }}>
                                                            <svg
                                                                aria-hidden="true"
                                                                viewBox="0 0 100 100"
                                                                preserveAspectRatio="none"
                                                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                                                            >
                                                                {chartAxisGuideLines.map((line, index) => (
                                                                    <line
                                                                        key={`${line.orientation}:${line.weight}:${line.pct}:${index}`}
                                                                        x1={line.orientation === 'vertical' ? line.pct : 0}
                                                                        y1={line.orientation === 'horizontal' ? line.pct : 0}
                                                                        x2={line.orientation === 'vertical' ? line.pct : 100}
                                                                        y2={line.orientation === 'horizontal' ? line.pct : 100}
                                                                        stroke={line.weight === 'major' ? 'rgba(100, 116, 139, 0.24)' : 'rgba(148, 163, 184, 0.16)'}
                                                                        strokeWidth={line.weight === 'major' ? '0.7' : '0.45'}
                                                                        strokeDasharray={line.weight === 'minor' ? '2 3' : undefined}
                                                                        vectorEffect="non-scaling-stroke"
                                                                    />
                                                                ))}
                                                                {chartAxisTickMarks.map((tick, index) => {
                                                                    const tickLength = tick.mark === 'cross' ? 7 : tick.mark === 'inside' ? 5 : 3;
                                                                    const strokeWidth = tick.weight === 'major' ? '0.9' : '0.65';
                                                                    const stroke = tick.weight === 'major' ? 'rgba(51, 65, 85, 0.58)' : 'rgba(100, 116, 139, 0.42)';
                                                                    if (tick.axis === 'category') return (
                                                                        <line
                                                                            key={`${tick.axis}:${tick.weight}:${tick.mark}:${tick.pct}:${index}`}
                                                                            x1={tick.pct}
                                                                            y1={100 - tickLength}
                                                                            x2={tick.pct}
                                                                            y2={100}
                                                                            stroke={stroke}
                                                                            strokeWidth={strokeWidth}
                                                                            strokeLinecap="round"
                                                                            vectorEffect="non-scaling-stroke"
                                                                        />
                                                                    );
                                                                    const x1 = tick.axis === 'secondaryValue' ? 100 - tickLength : 0;
                                                                    const x2 = tick.axis === 'secondaryValue' ? 100 : tickLength;
                                                                    return (
                                                                        <line
                                                                            key={`${tick.axis}:${tick.weight}:${tick.mark}:${tick.pct}:${index}`}
                                                                            x1={x1}
                                                                            y1={tick.pct}
                                                                            x2={x2}
                                                                            y2={tick.pct}
                                                                            stroke={stroke}
                                                                            strokeWidth={strokeWidth}
                                                                            strokeLinecap="round"
                                                                            vectorEffect="non-scaling-stroke"
                                                                        />
                                                                    );
                                                                })}
                                                                {chartSeriesPlotItems.length > 0 ? (
                                                                    <>
                                                                        <defs>
                                                                            {chartSeriesPlotItems.map((series, seriesIndex) => {
                                                                                if (!series.fillPattern) return null;
                                                                                const patternId = `fg-chart-fill-${artifact.id}-${seriesIndex}`;
                                                                                const strokeWidth = series.fillPattern === 'percent' ? 1.2 : 0.9;
                                                                                const patternForeground = series.fillForegroundColor ?? series.color;
                                                                                const commonStroke = { stroke: patternForeground, strokeWidth, vectorEffect: 'non-scaling-stroke' as const };
                                                                                return (
                                                                                    <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
                                                                                        <rect width="8" height="8" fill={series.areaFillColor} />
                                                                                        {(series.fillPattern === 'horizontal' || series.fillPattern === 'cross') && <path d="M0 2 H8 M0 6 H8" {...commonStroke} />}
                                                                                        {(series.fillPattern === 'vertical' || series.fillPattern === 'cross') && <path d="M2 0 V8 M6 0 V8" {...commonStroke} />}
                                                                                        {(series.fillPattern === 'diagonalDown' || series.fillPattern === 'diagonalCross') && <path d="M-2 0 L8 10 M2 -2 L10 6" {...commonStroke} />}
                                                                                        {(series.fillPattern === 'diagonalUp' || series.fillPattern === 'diagonalCross') && <path d="M-2 8 L8 -2 M2 10 L10 2" {...commonStroke} />}
                                                                                        {series.fillPattern === 'percent' && <circle cx="4" cy="4" r="1.1" fill={patternForeground} opacity="0.75" />}
                                                                                    </pattern>
                                                                                );
                                                                            })}
                                                                            {chartSeriesPlotItems.flatMap((series, seriesIndex) => (
                                                                                series.plotPoints.map((point, pointIndex) => {
                                                                                    if (!point.markerFillPattern) return null;
                                                                                    const patternId = `fg-chart-point-fill-${artifact.id}-${seriesIndex}-${pointIndex}`;
                                                                                    const strokeWidth = point.markerFillPattern === 'percent' ? 1.2 : 0.9;
                                                                                    const patternForeground = point.markerFillForegroundColor ?? point.markerFillColor ?? series.color;
                                                                                    const patternBackground = point.markerFillBackgroundColor ?? point.markerFillColor ?? series.color;
                                                                                    const commonStroke = { stroke: patternForeground, strokeWidth, vectorEffect: 'non-scaling-stroke' as const };
                                                                                    return (
                                                                                        <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
                                                                                            <rect width="8" height="8" fill={patternBackground} />
                                                                                            {(point.markerFillPattern === 'horizontal' || point.markerFillPattern === 'cross') && <path d="M0 2 H8 M0 6 H8" {...commonStroke} />}
                                                                                            {(point.markerFillPattern === 'vertical' || point.markerFillPattern === 'cross') && <path d="M2 0 V8 M6 0 V8" {...commonStroke} />}
                                                                                            {(point.markerFillPattern === 'diagonalDown' || point.markerFillPattern === 'diagonalCross') && <path d="M-2 0 L8 10 M2 -2 L10 6" {...commonStroke} />}
                                                                                            {(point.markerFillPattern === 'diagonalUp' || point.markerFillPattern === 'diagonalCross') && <path d="M-2 8 L8 -2 M2 10 L10 2" {...commonStroke} />}
                                                                                            {point.markerFillPattern === 'percent' && <circle cx="4" cy="4" r="1.1" fill={patternForeground} opacity="0.75" />}
                                                                                        </pattern>
                                                                                    );
                                                                                })
                                                                            ))}
                                                                        </defs>
                                                                        {chartSeriesPlotItems.map((series, seriesIndex) => (
                                                                            <React.Fragment key={series.name}>
                                                                                {series.renderAreaFill && series.areaPolygon && (
                                                                                    <polygon
                                                                                        points={series.areaPolygon}
                                                                                        fill={series.fillPattern ? `url(#fg-chart-fill-${artifact.id}-${seriesIndex})` : series.areaFillColor}
                                                                                        stroke="none"
                                                                                    />
                                                                                )}
                                                                                {series.renderLineStroke && series.polyline && (
                                                                                    <path
                                                                                        d={series.smoothPath || `M ${series.polyline.split(' ').map((point) => point.replace(',', ' ')).join(' L ')}`}
                                                                                        fill="none"
                                                                                        stroke={series.lineColor ?? series.color}
                                                                                        strokeWidth={series.lineWidth ?? 2.4}
                                                                                        strokeDasharray={
                                                                                            series.lineDash === 'dash' ? '7 4'
                                                                                                : series.lineDash === 'dot' ? '2 4'
                                                                                                    : series.lineDash === 'dashDot' ? '7 4 2 4'
                                                                                                        : undefined
                                                                                        }
                                                                                        strokeLinejoin="round"
                                                                                        strokeLinecap="round"
                                                                                        vectorEffect="non-scaling-stroke"
                                                                                    />
                                                                                )}
                                                                                {series.plotPoints.map((point, pointIndex) => {
                                                                                    const radius = series.pointRadius ?? chartPresentation?.seriesPointRadius ?? 2.2;
                                                                                    const markerKey = `${series.name}:${point.label}:${pointIndex}`;
                                                                                    const markerFillColor = point.markerFillColor ?? series.markerFillColor ?? series.color;
                                                                                    const markerFill = point.markerFillPattern ? `url(#fg-chart-point-fill-${artifact.id}-${seriesIndex}-${pointIndex})` : markerFillColor;
                                                                                    const markerLineColor = point.markerLineColor ?? series.markerLineColor ?? '#fff';
                                                                                    const markerFillOpacity = point.markerFillOpacity ?? 1;
                                                                                    if (series.markerShape === 'square') {
                                                                                        return (
                                                                                            <rect
                                                                                                key={markerKey}
                                                                                                x={point.xPct - radius}
                                                                                                y={point.yPct - radius}
                                                                                                width={radius * 2}
                                                                                                height={radius * 2}
                                                                                                fill={markerFill}
                                                                                                fillOpacity={markerFillOpacity}
                                                                                                stroke={markerLineColor}
                                                                                                strokeWidth="1.2"
                                                                                                vectorEffect="non-scaling-stroke"
                                                                                            />
                                                                                        );
                                                                                    }
                                                                                    if (series.markerShape === 'diamond') {
                                                                                        return (
                                                                                            <polygon
                                                                                                key={markerKey}
                                                                                                points={`${point.xPct},${point.yPct - radius} ${point.xPct + radius},${point.yPct} ${point.xPct},${point.yPct + radius} ${point.xPct - radius},${point.yPct}`}
                                                                                                fill={markerFill}
                                                                                                fillOpacity={markerFillOpacity}
                                                                                                stroke={markerLineColor}
                                                                                                strokeWidth="1.2"
                                                                                                vectorEffect="non-scaling-stroke"
                                                                                            />
                                                                                        );
                                                                                    }
                                                                                    if (series.markerShape === 'triangle') {
                                                                                        return (
                                                                                            <polygon
                                                                                                key={markerKey}
                                                                                                points={`${point.xPct},${point.yPct - radius} ${point.xPct + radius},${point.yPct + radius} ${point.xPct - radius},${point.yPct + radius}`}
                                                                                                fill={markerFill}
                                                                                                fillOpacity={markerFillOpacity}
                                                                                                stroke={markerLineColor}
                                                                                                strokeWidth="1.2"
                                                                                                vectorEffect="non-scaling-stroke"
                                                                                            />
                                                                                        );
                                                                                    }
                                                                                    if (series.markerShape === 'x') {
                                                                                        return (
                                                                                            <g key={markerKey}>
                                                                                                <line x1={point.xPct - radius} y1={point.yPct - radius} x2={point.xPct + radius} y2={point.yPct + radius} stroke="#fff" strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                                                                                                <line x1={point.xPct + radius} y1={point.yPct - radius} x2={point.xPct - radius} y2={point.yPct + radius} stroke="#fff" strokeWidth="2.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                                                                                                <line x1={point.xPct - radius} y1={point.yPct - radius} x2={point.xPct + radius} y2={point.yPct + radius} stroke={markerLineColor} strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                                                                                                <line x1={point.xPct + radius} y1={point.yPct - radius} x2={point.xPct - radius} y2={point.yPct + radius} stroke={markerLineColor} strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                                                                                            </g>
                                                                                        );
                                                                                    }
                                                                                    return (
                                                                                        <circle
                                                                                            key={markerKey}
                                                                                            cx={point.xPct}
                                                                                            cy={point.yPct}
                                                                                            r={radius}
                                                                                            fill={markerFill}
                                                                                            fillOpacity={markerFillOpacity}
                                                                                            stroke={markerLineColor}
                                                                                            strokeWidth="1.2"
                                                                                            vectorEffect="non-scaling-stroke"
                                                                                        />
                                                                                    );
                                                                                })}
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </>
                                                                ) : chartPresentation?.renderAggregateLine && chartAggregatePlot?.polyline && (
                                                                    <>
                                                                        <defs>
                                                                            {chartAggregatePlot.points.map((point, pointIndex) => {
                                                                                if (!point.markerFillPattern) return null;
                                                                                const patternId = `fg-chart-aggregate-point-fill-${artifact.id}-${pointIndex}`;
                                                                                const strokeWidth = point.markerFillPattern === 'percent' ? 1.2 : 0.9;
                                                                                const patternForeground = point.markerFillForegroundColor ?? point.markerFillColor ?? point.color;
                                                                                const patternBackground = point.markerFillBackgroundColor ?? point.markerFillColor ?? point.color;
                                                                                const commonStroke = { stroke: patternForeground, strokeWidth, vectorEffect: 'non-scaling-stroke' as const };
                                                                                return (
                                                                                    <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
                                                                                        <rect width="8" height="8" fill={patternBackground} />
                                                                                        {(point.markerFillPattern === 'horizontal' || point.markerFillPattern === 'cross') && <path d="M0 2 H8 M0 6 H8" {...commonStroke} />}
                                                                                        {(point.markerFillPattern === 'vertical' || point.markerFillPattern === 'cross') && <path d="M2 0 V8 M6 0 V8" {...commonStroke} />}
                                                                                        {(point.markerFillPattern === 'diagonalDown' || point.markerFillPattern === 'diagonalCross') && <path d="M-2 0 L8 10 M2 -2 L10 6" {...commonStroke} />}
                                                                                        {(point.markerFillPattern === 'diagonalUp' || point.markerFillPattern === 'diagonalCross') && <path d="M-2 8 L8 -2 M2 10 L10 2" {...commonStroke} />}
                                                                                        {point.markerFillPattern === 'percent' && <circle cx="4" cy="4" r="1.1" fill={patternForeground} opacity="0.75" />}
                                                                                    </pattern>
                                                                                );
                                                                            })}
                                                                        </defs>
                                                                        {chartPresentation.renderAreaFill && chartAggregatePlot.areaPolygon && (
                                                                            <polygon
                                                                                points={chartAggregatePlot.areaPolygon}
                                                                                fill={chartAggregatePlot.areaFillColor}
                                                                                stroke="none"
                                                                            />
                                                                        )}
                                                                        <path
                                                                            d={chartAggregatePlot.smoothPath || `M ${chartAggregatePlot.polyline.split(' ').map((point) => point.replace(',', ' ')).join(' L ')}`}
                                                                            fill="none"
                                                                            stroke={chartAggregatePlot.lineColor}
                                                                            strokeWidth="3"
                                                                            strokeLinejoin="round"
                                                                            strokeLinecap="round"
                                                                            vectorEffect="non-scaling-stroke"
                                                                        />
                                                                    </>
                                                                )}
                                                                {chartSeriesPlotItems.length === 0 && chartAggregatePlot?.points.map((point, index) => (
                                                                    <circle
                                                                        key={`${point.label}:${index}`}
                                                                        cx={point.xPct}
                                                                        cy={point.yPct}
                                                                        r={chartPresentation?.aggregatePointRadius ?? 2.4}
                                                                        fill={point.markerFillPattern ? `url(#fg-chart-aggregate-point-fill-${artifact.id}-${index})` : (point.markerFillColor ?? point.color)}
                                                                        fillOpacity={point.markerFillOpacity ?? 1}
                                                                        stroke={point.markerLineColor ?? '#fff'}
                                                                        strokeWidth="1.4"
                                                                        vectorEffect="non-scaling-stroke"
                                                                    />
                                                                ))}
                                                                {chartDataLabelItems.map((label, index) => (
                                                                    label.leaderLine ? (
                                                                        <line
                                                                            key={`${label.title}:leader-line:${index}`}
                                                                            x1={label.leaderLine.x1Pct}
                                                                            y1={label.leaderLine.y1Pct}
                                                                            x2={label.leaderLine.x2Pct}
                                                                            y2={label.leaderLine.y2Pct}
                                                                            stroke="rgba(71, 85, 105, 0.55)"
                                                                            strokeWidth="0.9"
                                                                            strokeLinecap="round"
                                                                            vectorEffect="non-scaling-stroke"
                                                                        />
                                                                    ) : null
                                                                ))}
                                                            </svg>
                                                            {chartSettings?.showCategoryAxis !== false && (
                                                                <div style={{ position: 'absolute', left: 6, bottom: 4, right: 6, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'end', gap: 8, color: '#64748b', fontSize: 9, fontWeight: 800 }}>
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chartAggregatePlot?.startLabel}</span>
                                                                    <span
                                                                        style={{
                                                                            ...chartTextOverflowStyle(categoryAxisTitleStyle),
                                                                            color: categoryAxisTitleStyle?.color ?? '#475569',
                                                                            fontSize: categoryAxisTitleStyle?.fontSize,
                                                                            fontWeight: categoryAxisTitleStyle?.fontWeight,
                                                                            fontStyle: categoryAxisTitleStyle?.fontStyle,
                                                                            transform: categoryAxisTitleStyle?.rotationDeg ? `rotate(${categoryAxisTitleStyle.rotationDeg}deg)` : undefined,
                                                                            transformOrigin: 'center',
                                                                            textAlign: categoryAxisTitleStyle?.textAlign,
                                                                            alignSelf: categoryAxisTitleStyle?.textVerticalAlign === 'top' ? 'flex-start' : categoryAxisTitleStyle?.textVerticalAlign === 'bottom' ? 'flex-end' : 'center',
                                                                            whiteSpace: categoryAxisTitleStyle?.textWrap ? 'normal' : 'nowrap',
                                                                            padding: `${categoryAxisTitleStyle?.textInsetTop ?? 0}px ${categoryAxisTitleStyle?.textInsetRight ?? 0}px ${categoryAxisTitleStyle?.textInsetBottom ?? 0}px ${categoryAxisTitleStyle?.textInsetLeft ?? 0}px`,
                                                                        }}
                                                                    >
                                                                        {chartSettings?.categoryAxisTitle}
                                                                    </span>
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{chartAggregatePlot?.endLabel}</span>
                                                                </div>
                                                            )}
                                                            {chartSettings?.showValueAxis !== false && chartSettings?.valueAxisTitle && (
                                                                <div style={{ position: 'absolute', left: 3, top: 6, bottom: 18, writingMode: 'vertical-rl', transform: `rotate(${valueAxisTitleStyle?.rotationDeg ?? 180}deg)`, color: valueAxisTitleStyle?.color ?? '#475569', fontSize: valueAxisTitleStyle?.fontSize ?? 9, fontWeight: valueAxisTitleStyle?.fontWeight ?? 900, fontStyle: valueAxisTitleStyle?.fontStyle, textAlign: valueAxisTitleStyle?.textAlign, display: 'flex', alignItems: valueAxisTitleStyle?.textVerticalAlign === 'top' ? 'flex-start' : valueAxisTitleStyle?.textVerticalAlign === 'bottom' ? 'flex-end' : 'center', padding: `${valueAxisTitleStyle?.textInsetTop ?? 0}px ${valueAxisTitleStyle?.textInsetRight ?? 0}px ${valueAxisTitleStyle?.textInsetBottom ?? 0}px ${valueAxisTitleStyle?.textInsetLeft ?? 0}px`, ...chartTextOverflowStyle(valueAxisTitleStyle), whiteSpace: valueAxisTitleStyle?.textWrap ? 'normal' : 'nowrap' }}>
                                                                    {chartSettings.valueAxisTitle}
                                                                </div>
                                                            )}
                                                            {chartSettings?.hasSecondaryValueAxis === true && chartSettings?.secondaryValueAxisDeleted !== true && chartSettings.secondaryValueAxisTitle && (
                                                                <div style={{ position: 'absolute', right: 3, top: 6, bottom: 18, writingMode: 'vertical-rl', transform: secondaryValueAxisTitleStyle?.rotationDeg ? `rotate(${secondaryValueAxisTitleStyle.rotationDeg}deg)` : undefined, color: secondaryValueAxisTitleStyle?.color ?? '#475569', fontSize: secondaryValueAxisTitleStyle?.fontSize ?? 9, fontWeight: secondaryValueAxisTitleStyle?.fontWeight ?? 900, fontStyle: secondaryValueAxisTitleStyle?.fontStyle, textAlign: secondaryValueAxisTitleStyle?.textAlign, display: 'flex', alignItems: secondaryValueAxisTitleStyle?.textVerticalAlign === 'top' ? 'flex-start' : secondaryValueAxisTitleStyle?.textVerticalAlign === 'bottom' ? 'flex-end' : 'center', padding: `${secondaryValueAxisTitleStyle?.textInsetTop ?? 0}px ${secondaryValueAxisTitleStyle?.textInsetRight ?? 0}px ${secondaryValueAxisTitleStyle?.textInsetBottom ?? 0}px ${secondaryValueAxisTitleStyle?.textInsetLeft ?? 0}px`, ...chartTextOverflowStyle(secondaryValueAxisTitleStyle), whiteSpace: secondaryValueAxisTitleStyle?.textWrap ? 'normal' : 'nowrap' }}>
                                                                    {chartSettings.secondaryValueAxisTitle}
                                                                </div>
                                                            )}
                                                            {chartAxisTickLabels.map((label, index) => {
                                                                if (label.axis === 'category') {
                                                                    const top = label.placement === 'high' ? 4 : undefined;
                                                                    const bottom = label.placement === 'high' ? undefined : 17;
                                                                    return (
                                                                        <div
                                                                            key={`${label.axis}:${label.pct}:${label.label}:${index}`}
                                                                            title={label.label}
                                                                            style={{
                                                                                position: 'absolute',
                                                                                left: `${label.pct}%`,
                                                                                top,
                                                                                bottom,
                                                                                maxWidth: 62,
                                                                                transform: 'translateX(-50%)',
                                                                                color: '#64748b',
                                                                                fontSize: 8,
                                                                                fontWeight: 800,
                                                                                lineHeight: 1,
                                                                                pointerEvents: 'none',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                            }}
                                                                        >
                                                                            {label.label}
                                                                        </div>
                                                                    );
                                                                }
                                                                const left = label.placement === 'high' ? undefined : 4;
                                                                const right = label.placement === 'high' ? 4 : undefined;
                                                                return (
                                                                    <div
                                                                        key={`${label.axis}:${label.pct}:${label.label}:${index}`}
                                                                        title={label.label}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left,
                                                                            right,
                                                                            top: `${label.pct}%`,
                                                                            maxWidth: 54,
                                                                            transform: 'translateY(-50%)',
                                                                            color: '#64748b',
                                                                            fontSize: 8,
                                                                            fontWeight: 800,
                                                                            lineHeight: 1,
                                                                            textAlign: label.placement === 'high' ? 'right' : 'left',
                                                                            pointerEvents: 'none',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {label.label}
                                                                    </div>
                                                                );
                                                            })}
                                                            {chartDataLabelItems.map((label, index) => {
                                                                const transform = label.anchor === 'left'
                                                                    ? 'translate(-100%, -50%)'
                                                                    : label.anchor === 'right'
                                                                        ? 'translate(0, -50%)'
                                                                        : label.anchor === 'below'
                                                                            ? 'translate(-50%, 0)'
                                                                            : label.anchor === 'above'
                                                                                ? 'translate(-50%, -100%)'
                                                                                : 'translate(-50%, -50%)';
                                                                const rotation = label.rotationDeg ? ` rotate(${label.rotationDeg}deg)` : '';
                                                                return (
                                                                    <div
                                                                        key={`${label.title}:data-label:${index}`}
                                                                        title={label.title}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: `${label.xPct}%`,
                                                                            top: `${label.yPct}%`,
                                                                            maxWidth: 84,
                                                                            transform: `${transform}${rotation}`,
                                                                            color: label.color,
                                                                            fontSize: label.fontSize,
                                                                            fontWeight: label.fontWeight,
                                                                            fontStyle: label.fontStyle,
                                                                            textAlign: label.textAlign,
                                                                            display: 'flex',
                                                                            alignItems: label.textVerticalAlign === 'top' ? 'flex-start' : label.textVerticalAlign === 'bottom' ? 'flex-end' : 'center',
                                                                            padding: `${label.textInsetTop}px ${label.textInsetRight}px ${label.textInsetBottom}px ${label.textInsetLeft}px`,
                                                                            lineHeight: 1.1,
                                                                            pointerEvents: 'none',
                                                                            ...chartTextOverflowStyle(label),
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {label.label}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : chartRenderKind === 'pie' ? (
                                                        <div style={{ display: 'grid', gridTemplateColumns: chartSettings?.showLegend === false ? '1fr' : 'minmax(52px, 0.42fr) 1fr', alignItems: 'center', gap: 8, minHeight: 68, background: plotAreaStyle?.background ?? 'transparent', border: plotAreaStyle && plotAreaStyle.borderWidth > 0 ? `${plotAreaStyle.borderWidth}px solid ${plotAreaStyle.borderColor}` : undefined, borderRadius: plotAreaStyle?.borderRadius ?? 4, boxSizing: 'border-box' }}>
                                                            <div
                                                                aria-hidden="true"
                                                                style={{
                                                                    position: 'relative',
                                                                    width: 54,
                                                                    height: 54,
                                                                    borderRadius: '50%',
                                                                    background: chartPieSliceItems.length > 0 ? 'transparent' : `conic-gradient(${chartPieGradient})`,
                                                                    boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.12), 0 2px 7px rgba(15, 23, 42, 0.10)',
                                                                }}
                                                            >
                                                                {chartPieSliceItems.length > 0 && (
                                                                    <svg viewBox="0 0 100 100" width="54" height="54" style={{ display: 'block', overflow: 'visible' }}>
                                                                        <defs>
                                                                            {chartPieSliceItems.map((slice, index) => {
                                                                                if (!slice.fillPattern) return null;
                                                                                const patternId = `fg-chart-pie-slice-fill-${artifact.id}-${index}`;
                                                                                const strokeWidth = slice.fillPattern === 'percent' ? 1.2 : 0.9;
                                                                                const patternForeground = slice.fillForegroundColor ?? slice.color;
                                                                                const patternBackground = slice.fillBackgroundColor ?? slice.color;
                                                                                const commonStroke = { stroke: patternForeground, strokeWidth, vectorEffect: 'non-scaling-stroke' as const };
                                                                                return (
                                                                                    <pattern key={patternId} id={patternId} patternUnits="userSpaceOnUse" width="8" height="8">
                                                                                        <rect width="8" height="8" fill={patternBackground} />
                                                                                        {(slice.fillPattern === 'horizontal' || slice.fillPattern === 'cross') && <path d="M0 2 H8 M0 6 H8" {...commonStroke} />}
                                                                                        {(slice.fillPattern === 'vertical' || slice.fillPattern === 'cross') && <path d="M2 0 V8 M6 0 V8" {...commonStroke} />}
                                                                                        {(slice.fillPattern === 'diagonalDown' || slice.fillPattern === 'diagonalCross') && <path d="M-2 0 L8 10 M2 -2 L10 6" {...commonStroke} />}
                                                                                        {(slice.fillPattern === 'diagonalUp' || slice.fillPattern === 'diagonalCross') && <path d="M-2 8 L8 -2 M2 10 L10 2" {...commonStroke} />}
                                                                                        {slice.fillPattern === 'percent' && <circle cx="4" cy="4" r="1.1" fill={patternForeground} opacity="0.75" />}
                                                                                    </pattern>
                                                                                );
                                                                            })}
                                                                        </defs>
                                                                        {chartPieSliceItems.map((slice, index) => (
                                                                            <path
                                                                                key={`${slice.label}:${index}`}
                                                                                d={slice.path}
                                                                                fill={slice.fillPattern ? `url(#fg-chart-pie-slice-fill-${artifact.id}-${index})` : slice.color}
                                                                                transform={slice.exploded ? `translate(${slice.translateX} ${slice.translateY})` : undefined}
                                                                                stroke="rgba(255,255,255,0.82)"
                                                                                strokeWidth="1.2"
                                                                                vectorEffect="non-scaling-stroke"
                                                                            />
                                                                        ))}
                                                                    </svg>
                                                                )}
                                                                {chartPieSliceItems.length === 0 && chartPieHoleSizePct > 0 && (
                                                                    <span
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: '50%',
                                                                            top: '50%',
                                                                            width: `${chartPieHoleSizePct}%`,
                                                                            height: `${chartPieHoleSizePct}%`,
                                                                            transform: 'translate(-50%, -50%)',
                                                                            borderRadius: '50%',
                                                                            background: overlayTheme.overlayBackground,
                                                                            boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.10)',
                                                                        }}
                                                                    />
                                                                )}
                                                                {chartDataLabelItems.length > 0 && (
                                                                    <svg
                                                                        viewBox="0 0 100 100"
                                                                        width="54"
                                                                        height="54"
                                                                        style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}
                                                                    >
                                                                        {chartDataLabelItems.map((label, index) => (
                                                                            label.leaderLine ? (
                                                                                <line
                                                                                    key={`${label.title}:pie-leader-line:${index}`}
                                                                                    x1={label.leaderLine.x1Pct}
                                                                                    y1={label.leaderLine.y1Pct}
                                                                                    x2={label.leaderLine.x2Pct}
                                                                                    y2={label.leaderLine.y2Pct}
                                                                                    stroke="rgba(71, 85, 105, 0.55)"
                                                                                    strokeWidth="1"
                                                                                    strokeLinecap="round"
                                                                                    vectorEffect="non-scaling-stroke"
                                                                                />
                                                                            ) : null
                                                                        ))}
                                                                    </svg>
                                                                )}
                                                                {chartDataLabelItems.map((label, index) => {
                                                                    const transform = label.anchor === 'left'
                                                                        ? 'translate(-100%, -50%)'
                                                                        : label.anchor === 'right'
                                                                            ? 'translate(0, -50%)'
                                                                            : label.anchor === 'below'
                                                                                ? 'translate(-50%, 0)'
                                                                                : label.anchor === 'above'
                                                                                    ? 'translate(-50%, -100%)'
                                                                                    : 'translate(-50%, -50%)';
                                                                    return (
                                                                        <div
                                                                            key={`${label.title}:pie-data-label:${index}`}
                                                                            title={label.title}
                                                                            style={{
                                                                                position: 'absolute',
                                                                                left: `${label.xPct}%`,
                                                                                top: `${label.yPct}%`,
                                                                                maxWidth: 74,
                                                                                transform,
                                                                                color: '#334155',
                                                                                fontSize: 8.5,
                                                                                fontWeight: 900,
                                                                                lineHeight: 1.1,
                                                                                pointerEvents: 'none',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                            }}
                                                                        >
                                                                            {label.label}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            {chartSettings?.showLegend !== false && (
                                                            <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
                                                                {chartPieLegendItems.map((item, index) => (
                                                                    <div
                                                                        key={`${item.label}:${index}`}
                                                                        title={item.sampledRemainder && item.hiddenPointCount
                                                                            ? `Sampled-out source values: ${item.hiddenPointCount} more.`
                                                                            : item.label}
                                                                        style={{ display: 'grid', gridTemplateColumns: '8px minmax(0, 1fr) auto', alignItems: 'center', gap: 5, minWidth: 0 }}
                                                                    >
                                                                        <span style={{ width: 7, height: 7, borderRadius: 999, background: item.color }} />
                                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155', fontSize: 10 }}>{item.label}</span>
                                                                        <span style={{ color: '#64748b', fontSize: 10 }}>{item.sharePct}%</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            )}
                                                        </div>
                                                    ) : chartCategoryBarGroups.length > 0 ? (
                                                        chartCategoryBarGroups.map((group, groupIndex) => (
                                                            <div key={`${group.label}:${groupIndex}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(42px, 0.45fr) 1fr auto', alignItems: 'center', gap: 6, minHeight: group.minHeightPx }}>
                                                                {chartSettings?.showCategoryAxis === false ? (
                                                                    <span aria-hidden="true" />
                                                                ) : (
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155', fontSize: 10 }}>{group.label}</span>
                                                                )}
                                                                <span style={{ display: 'grid', gap: group.barGapPx, minWidth: 0 }}>
                                                                    {group.grouping === 'stacked' || group.grouping === 'percentStacked' ? (
                                                                        <span
                                                                            title={group.bars.map((bar) => `${bar.seriesName}: ${bar.displayValue}`).join(', ')}
                                                                            style={{ position: 'relative', height: group.barHeightPx, borderRadius: 999, background: 'rgba(148, 163, 184, 0.22)', overflow: 'hidden' }}
                                                                        >
                                                                            {group.bars[0] && (
                                                                                <span
                                                                                    aria-hidden="true"
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        left: `${group.bars[0].axisPct}%`,
                                                                                        top: 0,
                                                                                        bottom: 0,
                                                                                        width: 1,
                                                                                        background: 'rgba(15, 23, 42, 0.28)',
                                                                                        transform: 'translateX(-0.5px)',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            {group.bars.map((bar) => (
                                                                                <span
                                                                                    key={bar.key}
                                                                                    title={`${bar.seriesName}: ${bar.displayValue}`}
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        left: `${bar.barStartPct}%`,
                                                                                        top: 0,
                                                                                        width: `${bar.barWidthPct}%`,
                                                                                        height: '100%',
                                                                                        borderRadius: 999,
                                                                                        background: bar.fillBackgroundColor ?? bar.color,
                                                                                        boxShadow: `inset 0 0 0 1px ${bar.fillForegroundColor ?? 'rgba(255,255,255,0.38)'}`,
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </span>
                                                                    ) : group.bars.map((bar) => (
                                                                            <span
                                                                                key={bar.key}
                                                                                title={`${bar.seriesName}: ${bar.displayValue}`}
                                                                                style={{ position: 'relative', height: group.barHeightPx, borderRadius: 999, background: 'rgba(148, 163, 184, 0.22)', overflow: 'hidden' }}
                                                                            >
                                                                                <span
                                                                                    aria-hidden="true"
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        left: `${bar.axisPct}%`,
                                                                                        top: 0,
                                                                                        bottom: 0,
                                                                                        width: 1,
                                                                                        background: 'rgba(15, 23, 42, 0.28)',
                                                                                        transform: 'translateX(-0.5px)',
                                                                                    }}
                                                                                />
                                                                                <span
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        left: `${bar.barStartPct}%`,
                                                                                        top: 0,
                                                                                        width: `${bar.barWidthPct}%`,
                                                                                        height: '100%',
                                                                                        borderRadius: 999,
                                                                                        background: bar.fillBackgroundColor ?? bar.color,
                                                                                        boxShadow: `inset 0 0 0 1px ${bar.fillForegroundColor ?? 'rgba(255,255,255,0.38)'}`,
                                                                                    }}
                                                                                />
                                                                            </span>
                                                                        ))
                                                                    }
                                                                </span>
                                                                {chartSettings?.showValueAxis === false && !chartSettings?.showDataLabels ? (
                                                                    <span aria-hidden="true" />
                                                                ) : (
                                                                    <span style={{ display: 'grid', gap: 1, minWidth: 0 }}>
                                                                        {group.bars.map((bar) => (
                                                                            <span key={`${bar.key}:value`} style={{ color: '#64748b', fontSize: 9.5, lineHeight: 1, whiteSpace: 'nowrap' }}>{bar.displayValue}</span>
                                                                        ))}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        chartAggregatePlot?.points.map((point, index) => (
                                                            <div key={`${point.label}:${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(42px, 0.45fr) 1fr auto', alignItems: 'center', gap: 6, minHeight: 14 }}>
                                                                {chartSettings?.showCategoryAxis === false ? (
                                                                    <span aria-hidden="true" />
                                                                ) : (
                                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155', fontSize: 10 }}>{point.displayLabel}</span>
                                                                )}
                                                                <span style={{ position: 'relative', height: 8, borderRadius: 999, background: 'rgba(148, 163, 184, 0.22)', overflow: 'hidden' }}>
                                                                    <span
                                                                        aria-hidden="true"
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: `${point.axisPct}%`,
                                                                            top: 0,
                                                                            bottom: 0,
                                                                            width: 1,
                                                                            background: 'rgba(15, 23, 42, 0.28)',
                                                                            transform: 'translateX(-0.5px)',
                                                                        }}
                                                                    />
                                                                    <span
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: `${point.barStartPct}%`,
                                                                            top: 0,
                                                                            width: `${point.barWidthPct}%`,
                                                                            height: '100%',
                                                                            borderRadius: 999,
                                                                            background: point.color,
                                                                        }}
                                                                    />
                                                                </span>
                                                                {chartSettings?.showValueAxis === false && !chartSettings?.showDataLabels ? (
                                                                    <span aria-hidden="true" />
                                                                ) : (
                                                                    <span style={{ color: '#64748b', fontSize: 10 }}>{point.displayValue}</span>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                    {chartSettings?.showCategoryAxis !== false && chartSettings?.categoryAxisTitle && chartRenderKind === 'bar' && (
                                                        <div style={{ color: categoryAxisTitleStyle?.color ?? '#475569', fontSize: categoryAxisTitleStyle?.fontSize ?? 9, fontWeight: categoryAxisTitleStyle?.fontWeight ?? 900, fontStyle: categoryAxisTitleStyle?.fontStyle, transform: categoryAxisTitleStyle?.rotationDeg ? `rotate(${categoryAxisTitleStyle.rotationDeg}deg)` : undefined, transformOrigin: 'center', textAlign: categoryAxisTitleStyle?.textAlign ?? 'center', alignSelf: categoryAxisTitleStyle?.textVerticalAlign === 'top' ? 'flex-start' : categoryAxisTitleStyle?.textVerticalAlign === 'bottom' ? 'flex-end' : 'center', padding: `${categoryAxisTitleStyle?.textInsetTop ?? 0}px ${categoryAxisTitleStyle?.textInsetRight ?? 0}px ${categoryAxisTitleStyle?.textInsetBottom ?? 0}px ${categoryAxisTitleStyle?.textInsetLeft ?? 0}px`, whiteSpace: categoryAxisTitleStyle?.textWrap ? 'normal' : 'nowrap', ...chartTextOverflowStyle(categoryAxisTitleStyle) }}>
                                                            {chartSettings.categoryAxisTitle}
                                                        </div>
                                                    )}
                                                    {chartSettings?.showLegend !== false && chartLegendPosition === 'bottom' && chartSeriesSummaryItems.length > 1 && (
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: `repeat(${chartSeriesSummaryItems.length}, minmax(0, 1fr))`,
                                                                gap: 4,
                                                            }}
                                                        >
                                                            {chartSeriesSummaryItems.map((series) => (
                                                                <div
                                                                    key={series.name}
                                                                    title={series.title}
                                                                    style={{
                                                                        minWidth: 0,
                                                                        border: '1px solid rgba(16, 124, 65, 0.18)',
                                                                        borderRadius: 4,
                                                                        background: 'rgba(255, 255, 255, 0.64)',
                                                                        padding: '2px 4px',
                                                                    }}
                                                                >
                                                                    <div style={{ color: '#166534', fontSize: 9, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {series.name}
                                                                    </div>
                                                                    <div style={{ color: '#334155', fontSize: 10, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {series.displayTotal}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {chartSettings?.showValueAxis !== false && chartSettings?.valueAxisTitle && chartRenderKind === 'bar' && (
                                                        <div style={{ color: valueAxisTitleStyle?.color ?? '#475569', fontSize: valueAxisTitleStyle?.fontSize ?? 9, fontWeight: valueAxisTitleStyle?.fontWeight ?? 900, fontStyle: valueAxisTitleStyle?.fontStyle, transform: valueAxisTitleStyle?.rotationDeg ? `rotate(${valueAxisTitleStyle.rotationDeg}deg)` : undefined, transformOrigin: 'center', textAlign: valueAxisTitleStyle?.textAlign ?? 'right', alignSelf: valueAxisTitleStyle?.textVerticalAlign === 'top' ? 'flex-start' : valueAxisTitleStyle?.textVerticalAlign === 'bottom' ? 'flex-end' : 'center', padding: `${valueAxisTitleStyle?.textInsetTop ?? 0}px ${valueAxisTitleStyle?.textInsetRight ?? 0}px ${valueAxisTitleStyle?.textInsetBottom ?? 0}px ${valueAxisTitleStyle?.textInsetLeft ?? 0}px`, whiteSpace: valueAxisTitleStyle?.textWrap ? 'normal' : 'nowrap', ...chartTextOverflowStyle(valueAxisTitleStyle) }}>
                                                            {chartSettings.valueAxisTitle}
                                                        </div>
                                                    )}
                                                    {previewFootnote && (
                                                        <div style={{ color: '#64748b', fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={previewFootnote}>
                                                            {previewFootnote}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span title={previewStatus.title}>{previewStatus.label}</span>
                                            )
                                        ) : pivotPreview ? (
                                            previewStatus.statusKind === 'pivot' ? (
                                                <div style={{ width: '100%', height: '100%', overflow: 'hidden', padding: 6, boxSizing: 'border-box' }}>
                                                    {pivotMeasureSummaryItems.length > 0 && (
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: `repeat(${pivotMeasureSummaryItems.length}, minmax(0, 1fr))`,
                                                                gap: 4,
                                                                marginBottom: 5,
                                                            }}
                                                        >
                                                            {pivotMeasureSummaryItems.map((measure) => (
                                                                <div
                                                                    key={measure.header}
                                                                    title={measure.title}
                                                                    style={{
                                                                        minWidth: 0,
                                                                        border: '1px solid rgba(37, 99, 235, 0.16)',
                                                                        borderRadius: 4,
                                                                        background: 'rgba(255, 255, 255, 0.72)',
                                                                        padding: '3px 4px',
                                                                    }}
                                                                >
                                                                    <div style={{ color: '#1d4ed8', fontSize: 9, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {measure.header}
                                                                    </div>
                                                                    <div style={{ color: '#334155', fontSize: 11, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {measure.displayTotal}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 10, color: '#334155' }}>
                                                        <thead>
                                                            <tr>
                                                                {pivotPreview.headers.map((header, index) => (
                                                                    <th key={`${header}:${index}`} style={{ padding: '3px 4px', textAlign: 'left', borderBottom: '1px solid rgba(37, 99, 235, 0.20)', color: '#1d4ed8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{header}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {pivotPreview.rows.map((row, rowIndex) => (
                                                                <tr key={rowIndex}>
                                                                    {row.map((cell, colIndex) => (
                                                                        <td key={`${rowIndex}:${colIndex}`} style={{ padding: '3px 4px', borderBottom: '1px solid rgba(148, 163, 184, 0.18)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cell}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {previewFootnote && (
                                                        <div style={{ marginTop: 3, color: '#64748b', fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={previewFootnote}>
                                                            {previewFootnote}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span title={previewStatus.title}>{previewStatus.label}</span>
                                            )
                                        ) : (
                                            <span title={previewStatus.title}>{previewStatus.label}</span>
                                        )}
                                    </div>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        data-testid={`fg-derived-artifact-resize-handle-${artifact.id}`}
                                        title={overlayChrome.placementResizeTitle}
                                        aria-label={overlayChrome.placementResizeAriaLabel}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            activeDerivedArtifactDragRef.current = artifact.dragStates.placementResize;
                                            controller.selectDerivedArtifactAtCell(placementSelectionUpdate.placementCell.col, placementSelectionUpdate.placementCell.row);
                                            applyDerivedArtifactSelectionUpdate(placementSelectionUpdate);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: 3,
                                            bottom: 3,
                                            width: 12,
                                            height: 12,
                                            borderRadius: 3,
                                            border: `2px solid ${overlayTheme.sourceResizeBorderColor}`,
                                            background: '#fff',
                                            boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
                                            cursor: 'nwse-resize',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                {!bareMode && autoFilterHeaderButtonPresentations.length > 0 && (
                    <div
                        className="fg-spreadsheet-autofilter-buttons"
                        data-testid="fg-spreadsheet-autofilter-buttons"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 16,
                            pointerEvents: 'none',
                            overflow: 'hidden',
                        }}
                    >
                        {autoFilterHeaderButtonPresentations.map((button) => {
                            return (
                                <button
                                    key={button.key}
                                    type="button"
                                    data-testid={`fg-autofilter-button-${button.col}`}
                                    aria-haspopup="menu"
                                    aria-expanded={button.expanded ? true : undefined}
                                    aria-label={button.ariaLabel}
                                    title={button.title}
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        const rect = event.currentTarget.getBoundingClientRect();
                                        setAutoFilterMenu((current) => (
                                            buildAutoFilterMenuTogglePlan({
                                                button,
                                                currentMenu: current,
                                                anchorRect: { left: rect.left, bottom: rect.bottom },
                                            })
                                        ));
                                    }}
                                    style={{
                                        position: 'absolute',
                                        left: button.x,
                                        top: button.y,
                                        width: 20,
                                        height: 18,
                                        padding: 0,
                                        border: button.filtered ? '1px solid rgba(16, 124, 65, 0.72)' : '1px solid rgba(107, 114, 128, 0.34)',
                                        borderRadius: 3,
                                        background: button.filtered ? '#e9f6ef' : '#ffffff',
                                        color: button.filtered ? 'var(--fg-green)' : '#4b5563',
                                        boxShadow: button.filtered ? '0 2px 8px rgba(16, 124, 65, 0.20)' : '0 2px 6px rgba(17, 24, 39, 0.10)',
                                        fontSize: 10,
                                        fontWeight: 900,
                                        lineHeight: '16px',
                                        cursor: 'pointer',
                                        pointerEvents: 'auto',
                                    }}
                                >
                                    v
                                </button>
                            );
                        })}
                    </div>
                )}

                {!bareMode && selectedValidationDropdown && (
                    <div
                        className="fg-spreadsheet-validation-dropdown"
                        data-testid="fg-spreadsheet-validation-dropdown"
                        style={{
                            position: 'absolute',
                            left: selectedValidationDropdown.x,
                            top: selectedValidationDropdown.y,
                            zIndex: 27,
                            pointerEvents: 'auto',
                        }}
                    >
                        <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={validationDropdownMenu ? true : undefined}
                            aria-label={selectedValidationDropdownButton?.ariaLabel}
                            title={selectedValidationDropdownButton?.title}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                const rect = event.currentTarget.getBoundingClientRect();
                                setValidationDropdownMenu((current) => current ? null : { x: rect.left, y: rect.bottom + 4 });
                            }}
                            style={{
                                width: 20,
                                height: 22,
                                padding: 0,
                                border: '1px solid rgba(16, 124, 65, 0.45)',
                                borderRadius: 3,
                                background: '#ffffff',
                                color: 'var(--fg-green)',
                                boxShadow: '0 3px 10px rgba(17, 24, 39, 0.14)',
                                fontSize: 12,
                                fontWeight: 800,
                                lineHeight: '18px',
                                cursor: 'pointer',
                            }}
                        >
                            {selectedValidationDropdownButton?.glyph}
                        </button>
                    </div>
                )}

                {!bareMode && invalidEntryNotice && (() => {
                    const invalidEntryPresentation = buildInvalidEntryNoticePresentation({
                        notice: invalidEntryNotice,
                        sheetSize: { colCount, rowCount },
                    });
                    if (!invalidEntryPresentation) return null;
                    const noticeStyle = invalidEntryPresentation.noticeStyle;
                    return (
                        <div
                            className="fg-spreadsheet-invalid-entry-notice"
                            data-testid="fg-spreadsheet-invalid-entry-notice"
                            role="alertdialog"
                            aria-modal="true"
                            aria-label={invalidEntryPresentation.title}
                            title={invalidEntryPresentation.message}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 1000,
                                pointerEvents: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 16,
                                background: 'rgba(15, 23, 42, 0.18)',
                            }}
                        >
                            <div
                                style={{
                                    width: 'min(420px, calc(100vw - 32px))',
                                    border: `1px solid ${noticeStyle.border}`,
                                    borderRadius: 6,
                                    background: '#ffffff',
                                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.24)',
                                    color: '#1f2937',
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '9px 12px',
                                        borderBottom: `1px solid ${noticeStyle.border}`,
                                        background: noticeStyle.background,
                                        color: noticeStyle.color,
                                    }}
                                >
                                    <div aria-hidden="true" style={{ fontWeight: 850, fontSize: 14, lineHeight: '18px', width: 18, textAlign: 'center' }}>{noticeStyle.icon}</div>
                                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 800 }}>
                                        {invalidEntryPresentation.title}
                                    </div>
                                    <button
                                        type="button"
                                        aria-label="Dismiss invalid entry message"
                                        title="Dismiss invalid entry message"
                                        onClick={() => setInvalidEntryNotice(null)}
                                        style={{
                                            border: 0,
                                            background: 'transparent',
                                            color: noticeStyle.color,
                                            cursor: 'pointer',
                                            fontWeight: 850,
                                            padding: 0,
                                            width: 22,
                                            height: 22,
                                            lineHeight: '22px',
                                        }}
                                    >
                                        x
                                    </button>
                                </div>
                                <div style={{ padding: '14px 16px 12px', fontSize: 13, lineHeight: 1.45 }}>
                                    <div style={{ marginBottom: 8 }}>{invalidEntryPresentation.message}</div>
                                    {invalidEntryPresentation.isProtected && invalidEntryPresentation.protectionReason ? (
                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                                            {invalidEntryPresentation.protectionReason}
                                        </div>
                                    ) : null}
                                    {invalidEntryPresentation.isProtected ? (
                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                                            No worksheet data changed.
                                        </div>
                                    ) : null}
                                    {invalidEntryPresentation.operationLabel ? (
                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: invalidEntryPresentation.target ? 3 : 0 }}>
                                            Action: <strong>{invalidEntryPresentation.operationLabel}</strong>
                                        </div>
                                    ) : null}
                                    {invalidEntryPresentation.target ? (
                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: invalidEntryPresentation.showsSeparateOffendingCell || invalidEntryPresentation.hasAttemptedValue ? 3 : 0 }}>
                                            {invalidEntryPresentation.targetLabel}: <strong>{invalidEntryPresentation.target}</strong>
                                        </div>
                                    ) : null}
                                    {invalidEntryPresentation.showsSeparateOffendingCell ? (
                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: invalidEntryPresentation.hasAttemptedValue ? 3 : 0 }}>
                                            Offending cell: <strong>{invalidEntryPresentation.offendingCell}</strong>
                                        </div>
                                    ) : null}
                                    {invalidEntryPresentation.attemptedValueLabel != null ? (
                                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                                            Entered value: <strong>{invalidEntryPresentation.attemptedValueLabel}</strong>
                                        </div>
                                    ) : null}
                                </div>
                                {invalidEntryPresentation.actions.length > 0 ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            flexWrap: 'wrap',
                                            gap: 8,
                                            padding: '10px 16px 14px',
                                            background: '#f8fafc',
                                            borderTop: '1px solid rgba(148, 163, 184, 0.2)',
                                        }}
                                    >
                                        {invalidEntryPresentation.actions.map((action) => (
                                            <button
                                                key={action.id}
                                                type="button"
                                                title={action.title}
                                                onClick={() => handleInvalidEntryAction(action.id)}
                                                style={{
                                                    minWidth: 72,
                                                    border: action.id === 'accept-anyway' || action.id === 'edit-protection' ? '1px solid var(--fg-green)' : '1px solid #d1d5db',
                                                    borderRadius: 4,
                                                    background: action.id === 'accept-anyway' || action.id === 'edit-protection' ? 'var(--fg-green)' : '#ffffff',
                                                    color: action.id === 'accept-anyway' || action.id === 'edit-protection' ? '#ffffff' : '#1f2937',
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    fontWeight: action.id === 'accept-anyway' || action.id === 'edit-protection' ? 760 : 680,
                                                    padding: '5px 12px',
                                                }}
                                            >
                                                {action.label}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    );
                })()}

                {!bareMode && pasteOptions && (
                    <div
                        className="fg-spreadsheet-paste-options"
                        data-testid="fg-spreadsheet-paste-options"
                        style={{
                            position: 'absolute',
                            left: pasteOptions.x,
                            top: pasteOptions.y,
                            zIndex: 28,
                            pointerEvents: 'auto',
                        }}
                    >
                        <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={pasteOptionsMenu ? true : undefined}
                            title={
                                pasteOptions.canPasteSpecial
                                    ? 'Paste options: change how the last engine-copied range is pasted.'
                                    : 'Paste options are available after copying a range in this workbook.'
                            }
                            disabled={!pasteOptions.canPasteSpecial}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                const rect = event.currentTarget.getBoundingClientRect();
                                setPasteOptionsMenu((current) => current ? null : { x: rect.left, y: rect.bottom + 4 });
                            }}
                            style={{
                                height: 28,
                                minWidth: 118,
                                padding: '0 9px',
                                border: '1px solid rgba(16, 124, 65, 0.45)',
                                borderRadius: 4,
                                background: pasteOptions.canPasteSpecial ? '#ffffff' : '#f3f4f6',
                                color: pasteOptions.canPasteSpecial ? 'var(--fg-green)' : '#9ca3af',
                                boxShadow: '0 6px 18px rgba(17, 24, 39, 0.14)',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: pasteOptions.canPasteSpecial ? 'pointer' : 'not-allowed',
                            }}
                        >
                            Paste Options v
                        </button>
                    </div>
                )}

                {!bareMode && fillOptions && (
                    <div
                        className="fg-spreadsheet-fill-options"
                        data-testid="fg-spreadsheet-fill-options"
                        style={{
                            position: 'absolute',
                            left: fillOptions.x,
                            top: fillOptions.y,
                            zIndex: 28,
                            pointerEvents: 'auto',
                        }}
                    >
                        <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={fillOptionsMenu ? true : undefined}
                            title="Auto Fill Options: choose whether the filled cells copy the source or continue a detected series."
                            onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                const rect = event.currentTarget.getBoundingClientRect();
                                setFillOptionsMenu((current) => current ? null : { x: rect.left, y: rect.bottom + 4 });
                            }}
                            style={{
                                height: 28,
                                minWidth: 132,
                                padding: '0 9px',
                                border: '1px solid rgba(16, 124, 65, 0.45)',
                                borderRadius: 4,
                                background: '#ffffff',
                                color: 'var(--fg-green)',
                                boxShadow: '0 6px 18px rgba(17, 24, 39, 0.14)',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            Auto Fill Options v
                        </button>
                    </div>
                )}

            </div>    {/* Footer Moved Inside Scroll Container */}

            {showWorkbookChrome && (
                <div
                    style={{
                        gridColumn: '1 / 3',
                        gridRow: '5 / 6',
                        zIndex: 35,
                        pointerEvents: 'auto',
                    }}
                >
                    <ExcelStatusBar
                        controller={controller}
                        selectedRange={selectedRange}
                        formulaSession={formulaEditSession}
                        protectionStatus={selectedCellProtectionStatus}
                        onViewStateChange={handleWorkbookChromeViewStateChange}
                    />
                </div>
            )}

            {
                !bareMode && (
                    <>
                        {
                            showFind && (
                                <FindDialog
                                    mode={findMode}
                                    query={findQuery}
                                    replacement={replaceValue}
                                    scope={findScope}
                                    canReplace={Boolean(controller.streamer)}
                                    onModeChange={setFindMode}
                                    onQueryChange={handleFindQueryChange}
                                    onReplacementChange={setReplaceValue}
                                    onScopeChange={setFindScope}
                                    onNext={handleNextMatch}
                                    onPrev={handlePrevMatch}
                                    onReplaceCurrent={handleReplaceCurrent}
                                    onReplaceAll={handleReplaceAll}
                                    onClose={() => setShowFind(false)}
                                    matchCount={matches.length}
                                    currentMatchIndex={currentMatchIndex}
                                />
                            )
                        }

                        {
                            contextMenu && (
                                <ContextMenu
                                    x={contextMenu.x}
                                    y={contextMenu.y}
                                    items={contextMenuItems}
                                    onClose={() => setContextMenu(null)}
                                />
                            )
                        }
                        {
                            pasteOptionsMenu && pasteOptionsMenuItems.length > 0 && (
                                <ContextMenu
                                    x={pasteOptionsMenu.x}
                                    y={pasteOptionsMenu.y}
                                    items={pasteOptionsMenuItems}
                                    onClose={() => setPasteOptionsMenu(null)}
                                />
                            )
                        }
                        {
                            fillOptionsMenu && fillOptionsMenuItems.length > 0 && (
                                <ContextMenu
                                    x={fillOptionsMenu.x}
                                    y={fillOptionsMenu.y}
                                    items={fillOptionsMenuItems}
                                    onClose={() => setFillOptionsMenu(null)}
                                />
                            )
                        }
                        {
                            autoFilterMenu && activeAutoFilterForMenu && autoFilterMenuPresentation && (
                                <div
                                    ref={autoFilterPopoverRef}
                                    role="menu"
                                    aria-label={`AutoFilter options for ${autoFilterColumnLabel}`}
                                    data-testid="fg-autofilter-value-popover"
                                    onMouseDown={(event) => {
                                        event.stopPropagation();
                                    }}
                                    style={{
                                        position: 'fixed',
                                        left: autoFilterMenu.x,
                                        top: autoFilterMenu.y,
                                        zIndex: 1100,
                                        width: 292,
                                        maxWidth: 'calc(100vw - 24px)',
                                        maxHeight: 'min(420px, calc(100vh - 24px))',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        background: '#ffffff',
                                        border: '1px solid #d7dde8',
                                        borderRadius: 8,
                                        boxShadow: '0 18px 44px rgba(15, 23, 42, 0.18)',
                                        color: '#1f2937',
                                        fontSize: 13,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 8,
                                            padding: '10px 10px 8px',
                                            borderBottom: '1px solid #e6eaf1',
                                        }}
                                    >
                                        <button
                                            type="button"
                                            title={autoFilterMenuPresentation.sortAscTitle}
                                            onClick={() => handleAutoFilterMenuAction('sort-asc')}
                                            style={{
                                                height: 30,
                                                border: '1px solid #cfd7e4',
                                                borderRadius: 6,
                                                background: '#fff',
                                                color: '#1f2937',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Sort A to Z
                                        </button>
                                        <button
                                            type="button"
                                            title={autoFilterMenuPresentation.sortDescTitle}
                                            onClick={() => handleAutoFilterMenuAction('sort-desc')}
                                            style={{
                                                height: 30,
                                                border: '1px solid #cfd7e4',
                                                borderRadius: 6,
                                                background: '#fff',
                                                color: '#1f2937',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Sort Z to A
                                        </button>
                                    </div>
                                    <div style={{ padding: '10px 10px 8px' }}>
                                        <input
                                            type="search"
                                            aria-label={autoFilterMenuPresentation.searchAriaLabel}
                                            title={autoFilterMenuPresentation.searchTitle}
                                            placeholder="Search values"
                                            value={autoFilterMenu.valueQuery ?? ''}
                                            onChange={(event) => handleAutoFilterValueQueryChange(event.currentTarget.value)}
                                            style={{
                                                width: '100%',
                                                height: 32,
                                                border: '1px solid #cfd7e4',
                                                borderRadius: 6,
                                                padding: '0 10px',
                                                color: '#111827',
                                                outline: 'none',
                                            }}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            padding: '0 10px 10px',
                                            borderBottom: '1px solid #eef1f6',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 8,
                                                marginBottom: 6,
                                                color: '#344054',
                                                fontWeight: 800,
                                            }}
                                        >
                                            <span>Custom Criteria</span>
                                            <button
                                                type="button"
                                                disabled={!autoFilterHasOperatorCriteria}
                                                title={autoFilterMenuPresentation.clearConditionsTitle}
                                                onClick={handleAutoFilterClearConditions}
                                                style={{
                                                    border: 0,
                                                    background: 'transparent',
                                                    color: autoFilterHasOperatorCriteria ? '#2f855a' : '#98a2b3',
                                                    fontWeight: 800,
                                                    padding: 0,
                                                    cursor: autoFilterHasOperatorCriteria ? 'pointer' : 'default',
                                                }}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '82px',
                                                gap: 8,
                                                alignItems: 'center',
                                                marginBottom: 6,
                                            }}
                                        >
                                            <select
                                                aria-label={autoFilterMenuPresentation.conditionJoinAriaLabel}
                                                title={autoFilterMenuPresentation.conditionJoinTitle}
                                                value={autoFilterMenu.draft?.conditionJoin ?? 'and'}
                                                onChange={(event) => handleAutoFilterConditionJoinChange(event.currentTarget.value === 'or' ? 'or' : 'and')}
                                                style={{
                                                    height: 30,
                                                    border: '1px solid #cfd7e4',
                                                    borderRadius: 6,
                                                    padding: '0 6px',
                                                    color: '#1f2937',
                                                    background: '#fff',
                                                    fontWeight: 700,
                                                }}
                                            >
                                                <option value="and">And</option>
                                                <option value="or">Or</option>
                                            </select>
                                        </div>
                                        {autoFilterConditionRows.map((condition, index) => (
                                            <div
                                                key={`condition-${index}`}
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '116px minmax(0, 1fr)',
                                                    gap: 8,
                                                    alignItems: 'center',
                                                    marginTop: index === 0 ? 0 : 6,
                                                }}
                                            >
                                                <select
                                                    aria-label={`Criterion ${index + 1} operator for ${autoFilterColumnLabel}`}
                                                    title={`Choose criterion ${index + 1} operator for ${autoFilterColumnLabel}.`}
                                                    value={condition.operator}
                                                    onChange={(event) => handleAutoFilterConditionOperatorChange(
                                                        index,
                                                        event.currentTarget.value as SpreadsheetAutoFilterOperator,
                                                    )}
                                                    style={{
                                                        height: 30,
                                                        border: '1px solid #cfd7e4',
                                                        borderRadius: 6,
                                                        padding: '0 6px',
                                                        color: '#1f2937',
                                                        background: '#fff',
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {AUTO_FILTER_OPERATOR_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    aria-label={`Criterion ${index + 1} value for ${autoFilterColumnLabel}`}
                                                    title={`Enter criterion ${index + 1} value for ${autoFilterColumnLabel}.`}
                                                    value={condition.value}
                                                    onChange={(event) => handleAutoFilterConditionValueChange(index, event.currentTarget.value)}
                                                    placeholder={index === 0 ? 'Value' : 'Optional value'}
                                                    style={{
                                                        width: '100%',
                                                        height: 30,
                                                        border: '1px solid #cfd7e4',
                                                        borderRadius: 6,
                                                        padding: '0 8px',
                                                        color: '#111827',
                                                        outline: 'none',
                                                        minWidth: 0,
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <label
                                        title={autoFilterMenuPresentation.selectAllTitle}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '2px 12px 8px',
                                            fontWeight: 700,
                                            cursor: autoFilterMenuPresentation.selectAllDisabled ? 'default' : 'pointer',
                                            opacity: autoFilterMenuPresentation.selectAllDisabled ? 0.5 : 1,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={autoFilterAllSelected}
                                            disabled={autoFilterMenuPresentation.selectAllDisabled}
                                            onChange={(event) => handleAutoFilterSelectAll(event.currentTarget.checked)}
                                        />
                                        Select All
                                    </label>
                                    <div
                                        style={{
                                            minHeight: 88,
                                            maxHeight: 190,
                                            overflowY: 'auto',
                                            borderTop: '1px solid #eef1f6',
                                            borderBottom: '1px solid #eef1f6',
                                            padding: '4px 0',
                                        }}
                                    >
                                        {autoFilterMenuPresentation.valueListState !== 'ready' ? (
                                            <div
                                                title={autoFilterMenuPresentation.valueListTitle}
                                                style={{
                                                    padding: '12px',
                                                    color: autoFilterMenuPresentation.valueListState === 'error' ? '#b42318' : '#667085',
                                                }}
                                            >
                                                {autoFilterMenuPresentation.valueListMessage}
                                            </div>
                                        ) : (
                                            autoFilterFilteredValueOptions.map((option) => {
                                                const checked = option.blank
                                                    ? autoFilterMenu.draft?.includeBlank === true
                                                    : autoFilterSelectedValueKeys.has(option.key);
                                                return (
                                                    <label
                                                        key={option.blank ? '__blank__' : option.key}
                                                        title={option.blank
                                                            ? `Include blank cells in ${autoFilterColumnLabel}.`
                                                            : `Include ${option.label} in ${autoFilterColumnLabel}.`}
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '18px minmax(0, 1fr) auto',
                                                            gap: 8,
                                                            alignItems: 'center',
                                                            padding: '6px 12px',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => handleAutoFilterToggleValue(option)}
                                                        />
                                                        <span
                                                            style={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {option.label}
                                                        </span>
                                                        <span style={{ color: '#667085', fontVariantNumeric: 'tabular-nums' }}>
                                                            {option.count}
                                                        </span>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 8,
                                            padding: 10,
                                            borderBottom: '1px solid #e6eaf1',
                                        }}
                                    >
                                        <button
                                            type="button"
                                            disabled={!autoFilterHasColumnFilter}
                                            title={autoFilterMenuPresentation.clearColumnTitle}
                                            onClick={() => handleAutoFilterMenuAction('clear-column')}
                                            style={{
                                                height: 30,
                                                border: '1px solid #cfd7e4',
                                                borderRadius: 6,
                                                background: '#fff',
                                                color: autoFilterHasColumnFilter ? '#1f2937' : '#9aa3b2',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Clear Column
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!autoFilterHasAnyFilter}
                                            title={autoFilterMenuPresentation.clearAllTitle}
                                            onClick={() => handleAutoFilterMenuAction('clear-all')}
                                            style={{
                                                height: 30,
                                                border: '1px solid #cfd7e4',
                                                borderRadius: 6,
                                                background: '#fff',
                                                color: autoFilterHasAnyFilter ? '#1f2937' : '#9aa3b2',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto auto',
                                            gap: 8,
                                            alignItems: 'center',
                                            padding: 10,
                                        }}
                                    >
                                        <button
                                            type="button"
                                            title={autoFilterMenuPresentation.removeTitle}
                                            onClick={() => handleAutoFilterMenuAction('remove')}
                                            style={{
                                                height: 30,
                                                border: '1px solid #ffd0cc',
                                                borderRadius: 6,
                                                background: '#fff',
                                                color: '#b42318',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Remove AutoFilter
                                        </button>
                                        <button
                                            type="button"
                                            title={autoFilterMenuPresentation.cancelTitle}
                                            onClick={() => setAutoFilterMenu(null)}
                                            style={{
                                                height: 30,
                                                border: '1px solid #cfd7e4',
                                                borderRadius: 6,
                                                background: '#fff',
                                                color: '#1f2937',
                                                fontWeight: 700,
                                                padding: '0 10px',
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            disabled={!autoFilterCanApply}
                                            title={autoFilterMenuPresentation.applyTitle}
                                            onClick={handleAutoFilterApplyDraft}
                                            style={{
                                                height: 30,
                                                border: '1px solid #2f855a',
                                                borderRadius: 6,
                                                background: autoFilterCanApply ? '#2f855a' : '#d8e7de',
                                                color: autoFilterCanApply ? '#fff' : '#728178',
                                                fontWeight: 800,
                                                padding: '0 12px',
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )
                        }
                        {
                            validationDropdownMenu && selectedValidationDropdown && (
                                <ExcelValidationListPicker
                                    x={validationDropdownMenu.x}
                                    y={validationDropdownMenu.y}
                                    selectionLabel={selectedRangeLabel}
                                    values={selectedValidationDropdown.values}
                                    currentValue={selectedValidationDropdown.currentValue}
                                    sourceFormula={selectedValidationDropdown.sourceFormula}
                                    sourceRanges={selectedValidationDropdown.sourceRanges}
                                    error={selectedValidationDropdown.error}
                                    onSelect={(value) => {
                                        controller.setCellValue(selectedValidationDropdown.col, selectedValidationDropdown.row, value);
                                        handleWorkbookChromeViewStateChange();
                                    }}
                                    onEditRule={() => openCellMetadataDialog('validation')}
                                    onClose={() => setValidationDropdownMenu(null)}
                                />
                            )
                        }
                    </>
                )
            }
            {showFloatingFullscreenButton && (
                <button
                    type="button"
                    aria-label={fullscreenActive ? 'Exit full screen' : 'Enter full screen'}
                    title={
                        canFullscreen
                            ? (fullscreenActive ? 'Exit browser full screen' : 'Enter browser full screen')
                            : (fullscreenActive ? 'Exit immersive mode' : 'Immersive mode (iOS Safari does not support browser fullscreen)')
                    }
                    onClick={toggleFullscreen}
                    style={{
                        position: 'fixed',
                        right: 'calc(12px + env(safe-area-inset-right))',
                        bottom: 'calc(12px + env(safe-area-inset-bottom))',
                        zIndex: 2000,
                        padding: '10px 14px',
                        borderRadius: '999px',
                        border: '1px solid #dadce0',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--fg-green)',
                        pointerEvents: 'auto'
                    }}
                >
                    {fullscreenActive ? 'Exit Full Screen' : 'Full Screen'}
                </button>
            )}
        </div >
    );
};
